/**
 * useDexLiveScan — Push 8 / v1.4.2 b4.
 *
 * Push 8 adds full lifecycle cleanup on top of Push 7's wiring:
 *   - Auto-starts PiP inside the Live-Scan tap so the floating window is
 *     live before the user swaps to Pokémon GO (failure is non-fatal and
 *     surfaced honestly via `pipError`).
 *   - On stop / save / error / menu-close, auto-stops PiP, ends the iOS
 *     system broadcast (the extension now finishes itself — see
 *     `broadcastStopNote`), tears down listeners + poller, and clears
 *     stale frames so the next session can't ingest a backlog.
 *   - Tracks a `broadcastPhase` for diagnostics.
 *
 * Dex-only live OCR hook. Replaces the Push 6 "Live Scan next build"
 * stub in `DexLiveScanMenu` with a real start/stop session that:
 *
 *   1. Triggers the iOS BroadcastUploadExtension via the existing
 *      `BroadcastAutoStart` plugin (one-tap entry).
 *   2. Consumes broadcast frames from BOTH paths LiveScanModal used:
 *      the JS-bridge `frame` event and the disk-backed App Group
 *      poller. We deliberately reuse those producers — they're the
 *      hardened broadcast → JS pipeline. We do NOT reuse any of the
 *      LiveScanModal UI: this hook builds a Pokédex-only frame loop
 *      on top of the same producers.
 *   3. Runs `extractPokedexFrame` against every frame, feeds the
 *      result through `adaptDexLiveFrame`, and ingests into a
 *      `DexLiveScanSession`.
 *   4. Publishes per-frame `DexScanDiagnostics` so the menu's
 *      diagnostic panel always reflects the latest read.
 *   5. Sends Dex-only PiP guidance (`setDexPipPhase`) so the
 *      floating window shows "Open Pokédex" / "Hold steady" /
 *      "Reading 4 × 4 grid" / "Dex updated" — never the legacy
 *      box / detail / CP / row vocabulary.
 *
 * Strict non-reuse rules (per Push 6 direction):
 *   - The legacy `LiveScanModal` component is NOT mounted from here.
 *   - No CP / IV / instance / box / row / slot fields are introduced.
 *   - When variant or region is unreadable, the diagnostic note says
 *     so and no state is applied.
 */
import { hasPokedexChrome } from "@/lib/dexFrameGate";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import type { PluginListenerHandle } from "@capacitor/core";

import {
  canAutoStartBroadcast,
  onFinished,
  onFrame,
  requestNextFrame,
  startBroadcast,
  stopBroadcast,
  type BroadcastFrame,
  onNativeLog,
} from "@/lib/broadcastAutoStart";
import {
  broadcastBridge,
  isBroadcastLive,
} from "@/lib/broadcast-bridge";
import { getPlatform } from "@/lib/platform";
import { isOnTradeList } from "@/lib/tradeListStore";
import { openPokemonGo } from "@/lib/openPokemonGo";
import { describeScanRun } from "@/lib/scanRunReport";
import { drainNativeOcrStats } from "@/lib/ocrEngine";
import { installAppActiveTracker, isAppActive } from "@/lib/appActive";
import { loadFrameImage } from "@/lib/frameImageCache";
import { reportScanPerf } from "@/lib/ocrEngine";
import { createAppGroupFramePoller } from "@/lib/appGroupFramePoller";
import {
  adaptDexLiveFrame,
  resolveTrustedVariant,
  variantSourceLabel,
  type DexLiveFrameResult,
} from "@/lib/dexLiveFrameAdapter";
import {
  createDexLiveScanSession,
  type DexLiveScanSession,
  type DexLiveSessionTotals,
} from "@/lib/dexLiveScanSession";
import {
  publishDexScanDiagnostics,
  resolveDiagnosticNote,
  type DexScanDiagnostics,
  type DexImagePipelineDiag,
} from "@/lib/dexScanDiagnostics";
import {
  createDexPipelineRunner,
  type DexPipelineRunner,
} from "@/lib/dexPipelineRunner";
import {
  type PokedexVariant,
  type PokedexRegionLite,
  type VariantCellObservation,
  isDexPageColourFamily,
  soleVariantForColourFamily,
} from "@/lib/pogoPokedexVariantClassifier";
// Vision-only trial: dexColourFamilyTransition removed (colour-family
// transition tracker retired with the pre-OCR gate).
import { DexRegionStabilizer } from "@/lib/dexRegionStabilizer";
import {
  createScanContextTracker,
  resetMeansDifferentDex,
  type ScanContextTracker,
} from "@/lib/dexScanContext";
import {
  createPreContextBuffer,
  type PreContextBuffer,
} from "@/lib/dexPreContextBuffer";
import { rowKeyForYPct } from "@/lib/dexScanTelemetry";
import { createScanTiming } from "@/lib/dexScanTiming";
import { DEX_SCAN_VERBOSE } from "@/lib/dexScanLog";
import { recordDexFrame, clearDexFrameDump } from "@/lib/dexFrameDump";
import type { TabVariant } from "@/lib/variantSignals";
import {
  evaluateFrameQuality,
  sharpnessFromDataUrl,
  DEFAULT_FRAME_QUALITY_THRESHOLDS,
} from "@/lib/frameQualityGate";
import { getBlurGateConfig } from "@/lib/frameQualityFlag";
import {
  lumaBandProfileFromDataUrl,
  decideFrameStability,
} from "@/lib/frameStabilityGate";
import { getScrollStabilityConfig } from "@/lib/frameStabilityFlag";
// Vision-only trial: dexPreOcrGate removed (pixel-only pre-OCR palette gate +
// its SEARCHING/SCANNING state machine retired — Vision OCRs every frame).
import {
  resetScanPipelineTelemetry,
  recordScanFrameReceived,
  recordScanFrameSkippedDuplicate,
  recordScanOcrAttempt,
  readScanPipelineTelemetry,
} from "@/lib/scanPipelineTelemetry";
import { logScanTruthSummary } from "@/lib/scanTruthSummary";
import { getLastLiveScanFrameStats } from "@/lib/liveScanFrameStats";
import { createFrameDedup, type FrameDedup } from "@/lib/dexFrameDedup";
import {
  createHoldSteadyDetector,
  isLowCaptureActive,
  type HoldSteadyDetector,
  type HoldSteadyReason,
} from "@/lib/dexHoldSteadyPrompt";
// Vision-only trial: dexPageIdentityCache import removed (the classify cache it
// memoised the pre-OCR palette verdict for no longer exists).
import { isDexScanDebugOverlayEnabled } from "@/lib/dexScanDebugOverlayFlag";
import {
  buildDexScanReviewSummary,
  type DexScanReviewSummary,
} from "@/lib/dexScanReviewSummary";
import { getDexScanSettings } from "@/lib/dexScanSettings";
import {
  reconcileNotCaughtReadings,
  type DexReconciliationConflict,
} from "@/lib/dexReconciliation";
import {
  classifyRecallGapFinalState,
  computeMissingIds,
  computeRecallGapBands,
  countMissingRows,
  detectScrollGaps,
  dexIdToRow,
  expectedIdsForRegion,
  rowToDexIds,
  type RowGap,
} from "@/lib/dexScrollCoverage";
import {
  getPipDiagnostics,
  subscribePipState,
  type PipDiagnostics,
  preparePip,
  startPip,
  stopPip,
  setDexPipPhase,
  clearDexPipClinical,
  noteDexFrameCells,
  dexRecentNumbersForPip,
  dexRecentKindsForPip,
  type DexHudCell,
  getDexRecentAdds,
  resetDexRecentAdds,
  type DexLivePipPhase,
} from "@/lib/nexPip";
import { apiRequestRaw } from "@/lib/queryClient";
import { gridColumnsForVariant } from "@/lib/dexOcrRoi";
import { reportSelfHeal } from "@/lib/shadowRosterSelfHeal";
import { buildGmaxQaScanEvent } from "@/lib/gmaxQaScanEvent";
import {
  computeDexScanGuidance,
  decideCoaching,
  DEX_SCAN_RESTING_INSTRUCTION,
  type ScrollDir,
  type CoachingReason,
  type GuidanceArrow,
} from "@/lib/dexScanGuidance";
import { scanTargetsForVariant, type ScanTargets } from "@/lib/dexScanTargets";
import { createSelfCorrectionTracker, type SelfCorrectionTracker } from "@/lib/dexScanSelfCorrection";
import { getDexEntryState, getDexStoreVersion, retractScanPresent, setScanWriteCoalescing } from "@/lib/dexStateStore";
import {
  beginScanSession,
  endScanSession,
  recordMyPokemonScanRow,
} from "@/lib/scanSessionStore";
import {
  getRejectedUnreleasedWrites,
  resetRejectedUnreleasedWrites,
} from "@/lib/scanIntegrity";
import {
  createDetailScanGate,
  type DetailScanGate,
} from "@/lib/dexDetailScanGate";
import { detailPipSlots, hasAnySlot } from "@/lib/dexDetailPipSlots";
import {
  findMatchingRecord,
  findRecordById,
  listMyPokemon,
  mergeIntoRecordById,
  myPokemonCount,
  upsertScannedPokemon,
  type MyPokemonRecord,
  getMyPokemon,
} from "@/lib/myPokemonStore";
import { crossWriteMyPokemonToDex } from "@/lib/myPokemonDexCrossWrite";
import { showcaseHudFields, showcaseHudPick, showcaseStandingFor, type ShowcaseHudPick } from "@/lib/showcaseStanding";
import { encounterIvRangeFor } from "@/lib/encounterIvRange";
import type { EncounterBannerRead } from "@/lib/myPokemonOcr";
import {
  myPokemonHudFacts,
  myPokemonPipSlots,
  type MyPokemonPipSlots,
} from "@/lib/myPokemonPipSlots";
import { baseStatsCandidatesForSpecies, deriveMyPokemon } from "@/lib/myPokemonDerived";
import {
  DETAIL_HUD_MESSAGE,
  DETAIL_HUD_UP_TO_DATE_MESSAGE,
  DETAIL_HUD_UPDATED_MESSAGE,
  DETAIL_HUD_UPSELL_MESSAGE,
  DETAIL_HUD_UPSELL_CTA,
  MYPOKE_HUD_MESSAGE,
  MYPOKE_HUD_SAVED_MESSAGE,
  MYPOKE_HUD_UPDATED_MESSAGE,
  MYPOKE_HUD_UP_TO_DATE_MESSAGE,
  MYPOKE_HUD_NEED_APPRAISAL_MESSAGE,
  MYPOKE_HUD_SAVED_NEED_APPRAISAL_MESSAGE,
  MYPOKE_HUD_NEED_SPECIES_MESSAGE,
  STORAGE_BOX_HUD_MESSAGE,
} from "@/lib/hudStatusLines";
import { cpFromHp, cpOnAnyFormLadder } from "@/lib/cpFromHp";
import { cpLooksMisread, reconcileIvRead } from "@/lib/ivReconcile";
import { myPokemonSightingKey } from "@/lib/myPokemonSightingKey";
import { recordTrainerResources } from "@/lib/trainerResources";
import { applyMegaFrame } from "@/lib/megaScanApply";
import {
  copyNameToClipboard,
  getNamingSessionState,
  renderPreferredNickname,
} from "@/lib/namingSession";

/** App/build/head identifier injected by Vite at build, or null when unset. */
const APP_BUILD_ID: string | null =
  (import.meta.env.VITE_BUILD_ID as string | undefined) ?? null;

/** Stable, collision-resistant id for one scan session. */
function newScanId(): string {
  try {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
      return crypto.randomUUID();
    }
  } catch {
    /* fall through to timestamp id */
  }
  return `scan_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Fire-and-forget POST of the G-Max QA scan event to the app backend. Never
 * throws, never blocks finish(). The backend records it and the scheduled
 * monitor (server-side) reads the admin feed + updates Notion. The app never
 * touches Notion/Perplexity directly. Mirrors the reportScanPerf pattern.
 */
function reportGmaxQaScanEvent(args: {
  scanId: string;
  totals: DexLiveSessionTotals;
  appliedIds: number[];
}): void {
  try {
    const event = buildGmaxQaScanEvent({
      scanId: args.scanId,
      totals: args.totals,
      appliedIds: args.appliedIds,
      build: APP_BUILD_ID,
    });
    // 2026-08-30 audit: this QA collector exists ONLY on the Express dev
    // server; the production Worker has no /api/dex/gmax-qa-event route (or
    // gmax_qa_events table), so every production scan finished with a silent
    // 404. Dev-gated rather than ported: it is a QA instrument, not product
    // telemetry.
    if (import.meta.env.DEV) {
      void apiRequestRaw("POST", "/api/dex/gmax-qa-event", event);
    }
  } catch {
    /* never blocks finish() */
  }
}

export type DexLiveScanPhase =
  | "idle"
  | "starting"
  | "scanning"
  | "finishing"
  | "saved"
  | "error";

/**
 * Lifecycle of the iOS system (ReplayKit) broadcast as the hook
 * understands it. We can request a stop and stop consuming frames, but
 * the actual teardown is owned by the broadcast extension — see
 * `broadcastStopNote` for the honest limitation.
 */
export type DexBroadcastPhase =
  | "inactive"
  | "starting"
  | "active"
  | "stopping"
  | "stopped";

/**
 * Honest description of what "stop" does to the iOS system broadcast.
 *
 * v1.4.2 b5 (Push 9): PokeGenie-style stop. `stopBroadcast()` on the
 * native side re-taps the RPSystemBroadcastPickerView. Because a
 * broadcast is already active, iOS shows the sheet with a "Stop
 * Broadcast" button; the user taps it and iOS terminates the
 * broadcast cleanly via `broadcastFinished()` on the extension — no
 * banner, no orphaned red status bar. Prior builds tried to end the
 * broadcast from inside the extension via `finishBroadcastWithError(_:)`
 * (which always fires the "Live Broadcast stopped" banner) or
 * deliberately left the broadcast running (which left the red status
 * bar visible until the user stopped from Control Center). This flow
 * hands the stop to iOS's native UI, matching how PokeGenie and
 * CalcyIV handle it.
 */
export const broadcastStopNote =
  "Stop & save opens the iOS broadcast picker so you can tap Stop " +
  "Broadcast, same one-tap flow as start. iOS then ends the broadcast " +
  "cleanly and returns you to the app for the post-scan review.";


export interface DexLiveScanState {
  phase: DexLiveScanPhase;
  /** Most recent error message, if any. */
  error?: string;
  /** Whether the session is currently consuming broadcast frames. */
  active: boolean;
  /** Whether PiP is reporting itself as active. */
  pipActive: boolean;
  /**
   * Honest PiP status for the current session. `null` until we attempt a
   * start. When auto-start fails the scan still runs, but we surface the
   * reason so diagnostics don't pretend PiP is up.
   */
  pipError?: string | null;
  /** System broadcast lifecycle as best the hook can observe it. */
  broadcastPhase: DexBroadcastPhase;
  /** Snapshot of session totals (re-rendered on each frame). */
  totals: DexLiveSessionTotals | null;
  /** Most recent published diagnostic — same shape the panel renders. */
  lastDiag: DexScanDiagnostics | null;
  /**
   * Build 3 / v1.4.6 b3 — end-of-session fallback proposal. Populated by
   * `finish()` ONLY when the scan read ≥1 dex number on a real grid but never
   * resolved a confident variant, so nothing was applied. The menu surfaces a
   * confirmation ("Detected N entries — apply to <Filter>?") and calls
   * `applyFallback(variant)` if the user accepts. `null` when no fallback is
   * pending (either something applied, or no entries were buffered).
   */
  pendingFallback: { count: number; ids: number[] } | null;
  /**
   * Build 13a — Review & Confirm summary, populated by `finish()` when a scan
   * committed ≥1 entry. The menu shows detected (region, variant, count) with
   * an edit affordance before the run is treated as final. `null` until a scan
   * finishes with something applied.
   */
  reviewSummary: DexScanReviewSummary | null;
  /**
   * b17a — bidirectional reconciliation conflicts. High-confidence "not caught"
   * reads that disagree with a manual "caught" mark, computed by `finish()` from
   * the session's not-caught readings (gated on the "Suggest gaps" setting and
   * the chosen confidence profile). Empty when gap surfacing is off or nothing
   * disagreed. The menu routes these to a toast (1-2) or summary card (3+).
   */
  conflicts: DexReconciliationConflict[];
  /**
   * b18g — live slow-scroll nudge visibility. True when consecutive frames
   * jumped row indices > 1 and the nudge chip should be shown in the overlay.
   */
  showScrollNudge: boolean;
  /**
   * PR-E60 — targeted guided-scroll steer. Non-null when the live nudge should
   * name a specific row to return to. targetRow is the 0-based grid row that
   * was skipped; leadDexId is its first national-dex ID (the number the chip
   * tells the user to scroll back to). Persists until the user scrolls back
   * (the row is read) or taps Skip (dismissSteer). Null when no steer is active.
   */
  scrollSteer: { targetRow: number; leadDexId: number } | null;
}

export interface UseDexLiveScanOptions {
  /** Active Dex variant from the calling page (fallback when OCR is ambiguous). */
  initialVariant: PokedexVariant;
  /**
   * Fired per frame with the dex numbers that just transitioned to present in
   * the store (genuine new-to-dex adds, already-present and manual entries
   * excluded). The variant is the one the frame committed under. Lets the
   * scanner UI keep a rolling list of recently-added entries.
   */
  onNewAdds?: (adds: Array<{ dexNumber: number; variant: PokedexVariant }>) => void;
  /**
   * Whether the signed-in account has Pro. Detail-page live scanning is a Pro
   * feature (matching the screenshot-upload path in ScanPokedexModal), so a
   * Basic account never probes and never writes from a detail page. Passed in
   * rather than read here so this hook stays free of session context and stays
   * testable — LiveScanProvider owns the session read.
   *
   * Defaults to false: an unknown entitlement must never unlock a paid feature.
   */
  isPro?: boolean;
}

const INITIAL_STATE: DexLiveScanState = {
  phase: "idle",
  active: false,
  pipActive: false,
  pipError: null,
  broadcastPhase: "inactive",
  totals: null,
  lastDiag: null,
  pendingFallback: null,
  reviewSummary: null,
  conflicts: [],
  showScrollNudge: false,
  scrollSteer: null,
};

/**
 * Choose a PiP phase from the current session totals so we never have
 * to encode this logic at the call site. Keeps the messages tightly
 * coupled to what the OCR is actually reporting.
 */

/**
 * b13c.7 — human-readable variant label for the live PiP commit message
 * ("+4 to Hoenn / Perfect (…)"). "pokemon" reads as "Pokémon"; the GO-specific
 * size tabs keep their uppercase form (XXL / XXS); everything else is simply
 * title-cased.
 */
function variantPipLabel(variant: PokedexVariant): string {
  switch (variant) {
    case "pokemon": return "Pokémon";
    case "xxl": return "XXL";
    case "xxs": return "XXS";
    case "gmax": return "GMax";
    default: return variant.charAt(0).toUpperCase() + variant.slice(1);
  }
}

/**
 * The live PiP scanning-HUD body line. It NEVER names the variant: the
 * "Scanning [X] Dex" text sat inside the SAME horizontal band the variant OCR
 * reads, so the overlay's own label was being OCR'd back as variant text → a
 * detection feedback loop. Only the genuine reading_grid phase shows the plain
 * constant "Scanning…"; every other phase returns undefined so the caller falls
 * back to that phase's canonical directive copy (open dex / start scrolling /
 * hold steady) unchanged. Keeping the override scoped to reading_grid guarantees
 * the one phase that renders the scanning HUD can never leak a variant label.
 */
export function dexScanningHudMessage(
  phase: DexLivePipPhase,
  _liveVariant: PokedexVariant | null,
): string | undefined {
  if (phase !== "reading_grid") return undefined;
  return "Scanning…";
}

/**
 * Map the live session state onto a PiP guidance phase. This is the single
 * source of truth for "what should the pill tell the user to do right now",
 * derived purely from the session totals + scan phase so the messages stay
 * coupled to what the OCR pipeline is actually reporting. Exported for tests.
 *
 * The guidance hierarchy, most-blocking first:
 *  - pre-scan / no frames yet → open the Pokémon GO Pokédex
 *  - frames arriving but no recognisable dex grid (still in Nexus / non-PoGo
 *    screen → frames drop as nexus_dex_ui) → open a Pokémon GO dex page
 *  - on a recognised dex page but nothing read yet → start scrolling slowly
 *  - numbers seen but not confidently committing → hold steady / hold steadier
 *  - reading a confident grid → reading_grid (the genuine "Scanning…" state)
 *
 * `onRecognisedDexPage` lets the caller assert that a Pokémon GO dex page-colour
 * family is committed for this frame (the pre-OCR gate saw a real dex backdrop)
 * even when the frame produced no committed number — e.g. a frame held on
 * variant/region stability, where `lastMode` stays "unknown". In that case the
 * pill must NOT fall back to "open a dex page" (the page is already open); it
 * coaches scrolling so motion surfaces a readable grid. Pure guidance copy — it
 * never changes any gate, veto, stability, or write decision.
 */
export function pipPhaseFromTotals(
  totals: DexLiveSessionTotals | null,
  phase: DexLiveScanPhase,
  onRecognisedDexPage = false,
): DexLivePipPhase {
  if (phase === "saved") return "saved";
  if (phase === "idle" || phase === "starting") return "open_pokedex";
  if (!totals) return "open_pokedex";
  if (totals.framesObserved === 0) return "open_pokedex";
  if (totals.lastMode === "unknown") {
    // Telemetry 2026-06-24 (Shadow/Purified live scan): the pre-OCR gate ALLOWED
    // dex_shadow_purple frames and the loop processed them, yet every frame was
    // held (variant_hold / region-not-stable) so `lastMode` never left "unknown"
    // and `numbersRead` stayed 0. The pill therefore sat on the generic
    // "Open a Pokémon GO dex page" — telling the user to open a page they were
    // already looking at, which read as "nothing is being scanned". When a dex
    // page-colour family IS committed (`onRecognisedDexPage`) we KNOW we are on a
    // recognised Pokémon GO dex page; coach the user to scroll so motion refreshes
    // the active pill/region and surfaces a readable grid, instead of repeating
    // the misleading "open a dex page" directive. This changes ONLY the guidance
    // copy — no gate, veto, stability, or write behaviour is affected.
    if (onRecognisedDexPage) {
      return totals.numbersRead === 0 ? "start_scrolling" : "hold_steady";
    }
    // Frames ARE arriving but the classifier isn't seeing a Pokémon GO dex grid
    // (still in Nexus, on the map, or a non-dex screen → frames drop as
    // nexus_dex_ui). Direct the user onto a dex page instead of a misleading
    // "Scanning…".
    //
    // b13c.3: when the scan is live the pill must visibly leave the pre-scan
    // "Open Pokémon GO Pokédex" — otherwise a session where every frame is
    // dropped looks identical to "no broadcast at all". The "scanning" phase's
    // copy ("Open a Pokémon GO dex page") differs from the idle copy, so the
    // pill still changes when the loop goes live AND stays directive.
    //
    // B37 (Hold-steady-stuck-off-dex, device log 2026-06-30): we are NOT on a
    // recognised dex page (`onRecognisedDexPage` is false — the gate sees
    // nexus_dex_ui / nondex_mixed, e.g. the user navigated to the App Store or
    // the PoGo map). "Hold steady" is GRID-COACHING copy and must NEVER show
    // off-dex: the previous `numbersRead > 0 ? "hold_steady"` fallback left the
    // pill frozen on "Hold steady" over the App Store because numbersRead still
    // carried the prior on-grid count. Off-dex the only correct directive is to
    // return to a dex page — regardless of how many numbers a PRIOR dex page
    // read. A genuine transient unknown flicker WHILE still on the grid keeps the
    // gate in SCANNING on a dex-coloured class, so it arrives here with
    // onRecognisedDexPage=true and takes the recognised-page branch above instead.
    if (phase === "scanning") {
      return "scanning";
    }
    return "open_pokedex";
  }
  // On a recognised dex page with a READABLE GRID MODE (lastMode is not
  // "unknown"), but nothing read yet.
  //
  // 2026-07-21 — this used to return `start_scrolling` ("Start scrolling slowly
  // to scan"), on the stated premise that "the scanner needs scroll motion to
  // register entries". That premise is FALSE, and it was costing the whole
  // startup:
  //   - a still page passes the scroll-stability gate immediately (first frame
  //     has no baseline -> stable; every later still frame diffs 0 <= 8), so a
  //     static grid is read straight away;
  //   - MOTION is what blocks reads. A transitional frame is dropped before OCR
  //     AND calls noteScrollMotion(), which opens the settle window so the next
  //     admitted frame cannot commit either.
  // So the copy asked the user to do the one thing that stops the first read. On
  // the 2026-07-21 device trace six of the nine frames before the first commit
  // were dropped as transitional, and the first commit took 11.8s.
  //
  // With a readable grid on screen the correct instruction is to HOLD — the
  // scanner reads what is already there. `hold_steady` reuses existing copy, so
  // there is no native renderer change. The `start_scrolling` phase is retained
  // and still used above for `lastMode === "unknown"`, where the classifier has
  // NO grid and motion genuinely does help surface the pill/region.
  if (totals.numbersRead === 0) return "hold_steady";
  if (totals.lastConfidence < 50) return "low_confidence";
  return "reading_grid";
}

/**
 * Round-12 Fix 3 — consecutive processed frames that read ≥1 dex number but
 * committed nothing before the hold-steady detector's `lowCapture` input trips.
 * The detector then debounces on top of this, so the prompt only shows after a
 * sustained run of read-but-never-confirm frames (scrolling past rows too fast).
 */
export const HOLD_STEADY_LOW_CAPTURE_FRAMES = 3;

/**
 * ↑/↓ prefix for the PiP detail line, conveying scroll direction without a
 * native arrow glyph (the pastel scanning HUD has no arrow renderer). "hold" /
 * "none" add nothing — the copy itself already says to hold.
 */
/**
 * Minimum time a PiP guidance line is held before it may change, so the copy is
 * readable instead of churning at scan frame-rate. A changed top-line
 * instruction bypasses this (see the push site).
 */
const GUIDANCE_TEXT_MIN_MS = 1400;
/**
 * Off-grid hysteresis (2026-09-08, second device pass): the first cut counted
 * post-gate observations and flipped after two frames, so every scroll (a few
 * smeared frames that resolve nothing) sent the HUD to "Show a Pokédex or
 * Pokémon screen" mid-scan. Now the READER's own view counts (raw numbers,
 * a region tab or filter chip in the signature), and the copy only changes
 * after this many consecutive frames AND this long since the last frame that
 * saw a grid, which no scroll on a Pokédex page reaches.
 */
const OFF_GRID_FRAMES = 3;
const OFF_GRID_MS = 3000;

/**
 * Consecutive "not a detail page" probe outcomes required before the HUD releases
 * a latched species page back to grid scanning.
 *
 * 2026-07-29: on Android the live-captured detail page ANIMATES (3D model bob,
 * shiny sparkle) and re-encodes every frame, so an occasional probe frame caught
 * mid-animation fails `isDetailPage` even though the user has not left the page.
 * Tearing the latch down on that single miss dropped the HUD to grid coaching for
 * a frame and then re-latched — the "species → hold steady → scanning → species"
 * churn on the detail screen. Requiring a short run of misses lets an animation
 * blip pass while a real navigation away (which lands on sustained grid frames)
 * still releases promptly.
 */
const DETAIL_RELEASE_MISS_FRAMES = 3;
/**
 * How many consecutive "not a caught-Pokémon page" probes a LATCHED Pokémon
 * survives (2026-09-03 owner ask). Scrolling down to the moves takes the
 * name, CP and appraisal off-screen and the probe stops recognising the
 * page, yet it is the same Pokémon: the HUD (and its CP) should hold until
 * a different Pokémon's name is read (a sideways swipe), a Pokédex grid is
 * recognised, or the page has really been gone a while. Probes run on
 * stable frames, so this is several seconds, not a dozen frames of video.
 */
const MYPOKE_RELEASE_MISS_FRAMES = 12;
/**
 * The Pokémon HUD also releases by TIME (2026-09-25 Galaxy pass): at the
 * Android pipeline's ~1.7 s per frame, 12 miss frames kept the destiny badge
 * floating over the Pokédex region list for 20 s after the page was left.
 */
// 12 s (2026-09-30, was 5 s): at ~2 s a frame on a Galaxy, three held frames
// and one forced probe already passed the old window, and one missed probe
// then wiped a card the trainer was still looking at.
const MYPOKE_RELEASE_STALE_MS = 12000;
/**
 * Consecutive frames the scroll-stability gate may swallow while a caught
 * Pokémon's card is latched before one is probed anyway (2026-09-18).
 *
 * The gate compares a luma profile of frame rows 0.20-0.30 and, while a My
 * Pokémon card is latched, an "unstable" frame is answered by re-asserting the
 * card as last drawn, with no probe and no log. Nothing bounds that: as long as
 * something keeps those rows changing (our own HUD sits inside them when it is
 * parked top-right on a 5.8-inch screen; GO's scene animates there too), no
 * frame is ever read again and the card keeps saying "Appraise it to add IVs"
 * over an appraisal that is on screen. A reporter's card recovered the moment
 * the HUD was dragged bottom-right, out of those rows (Discord, 2026-09-18).
 *
 * A caught-Pokémon page is not a grid: reading it mid-motion risks at most one
 * bad read, which the two-frame agreement discards. So after this many held
 * frames the next one goes to the probe. It never reaches the grid extractor:
 * a forced frame that is not a caught-Pokémon page ends there.
 */
const MYPOKE_HOLD_FORCE_PROBE_AFTER = 3;

const ARROW_PREFIX: Record<"scroll-down" | "back" | "hold" | "none", string> = {
  "scroll-down": "↓ ",
  back: "↑ ",
  hold: "",
  none: "",
};

/**
 * Round-12 Fix 3 — fold a shown hold-steady decision onto the base PiP phase.
 * Guidance copy ONLY: it reuses the existing `scroll_slower` / `hold_steady`
 * phases (and their canonical copy) so no new strings are introduced, and it
 * never overrides the terminal `saved` phase. When the detector is not showing
 * the prompt the base phase passes through byte-for-byte. Pure + exported for
 * tests.
 */
export function holdSteadyPipPhase(
  basePhase: DexLivePipPhase,
  shown: boolean,
  reason: HoldSteadyReason | null,
): DexLivePipPhase {
  if (!shown || basePhase === "saved") return basePhase;
  // Low capture = numbers ARE being read but scroll past too fast to confirm →
  // coach a slower scroll. Motion / both = blur or mid-scroll drops → hold steady.
  return reason === "low_capture" ? "scroll_slower" : "hold_steady";
}

/**
 * b13c — build the optional diagnostic sub-line for the PiP pill. Returns the
 * caller's `baseDetail` unchanged unless the debug-overlay flag is on, in which
 * case it appends "· f:<clock> <ocr>" so Sam can see, on device, the last-frame
 * timestamp and last OCR result without an Xcode debugger. Text-only — reuses
 * the existing `detail` line, so there is no UI/UX styling change.
 */
function debugOverlayDetail(args: {
  frameTs: number;
  ocr: string;
  baseDetail?: string;
}): string | undefined {
  if (!isDexScanDebugOverlayEnabled()) return args.baseDetail;
  const clock = new Date(args.frameTs).toLocaleTimeString([], {
    hour12: false,
    minute: "2-digit",
    second: "2-digit",
  });
  const dbg = `f:${clock} ${args.ocr}`;
  return args.baseDetail ? `${args.baseDetail} · ${dbg}` : dbg;
}

export function useDexLiveScan(opts: UseDexLiveScanOptions) {
  const [state, setState] = useState<DexLiveScanState>(INITIAL_STATE);
  const sessionRef = useRef<DexLiveScanSession | null>(null);
  // Distinct dex cells SEEN this session (caught or not) — the scan-coverage
  // progress the PiP reports. Reset per session.
  const scannedIdsRef = useRef<Set<number>>(new Set());
  // Self-correction: accumulates high-confidence contrary reads so a false apply
  // that slipped through gets retracted rather than standing forever.
  const selfCorrectionRef = useRef<SelfCorrectionTracker>(createSelfCorrectionTracker());
  // Detail-page live scan: stillness gate + per-session applied set. See
  // dexDetailScanGate for why stillness is both the correctness gate and the
  // probe scheduler.
  const detailGateRef = useRef<DetailScanGate>(createDetailScanGate());
  // Lazily-imported detail OCR module, mirroring ocrModuleRef: the detail path
  // is Pro-only and often never taken, so it must not sit in the initial bundle.
  const detailModuleRef = useRef<typeof import("@/lib/pokemonDetailDexOcr") | null>(null);
  // Last variant-slot payload pushed to the PiP, so leaving a detail page can
  // clear the HUD exactly once instead of on every subsequent grid frame.
  const lastDetailSlotsRef = useRef<string[]>([]);
  // True while the HUD is showing detail-page output of EITHER kind (Pro variant
  // grid or Basic upsell), so leaving the page clears it exactly once. The
  // Basic path pushes no slots, so it cannot be inferred from lastDetailSlotsRef.
  const detailHudActiveRef = useRef<boolean>(false);
  // The species headline currently on the HUD ("CHARIZARD #6"), or null. The
  // grid push re-asserts this on every in-between frame so grid-scan copy can
  // never land on top of a detail page — see the DETAIL MODE OWNS THE TEXT note
  // at that push. Cleared wherever detailHudActiveRef is cleared.
  const lastDetailSpeciesRef = useRef<string | null>(null);
  // The status line under that headline. Carried in a ref for the same reason as
  // the species: the in-between grid frames re-assert it, and it is not always
  // DETAIL_HUD_MESSAGE — a page with nothing left to write says so instead.
  const lastDetailMessageRef = useRef<string>(DETAIL_HUD_MESSAGE);
  // Consecutive "not a detail page" probe outcomes while a species page is
  // latched. Released only once it reaches DETAIL_RELEASE_MISS_FRAMES, so a
  // single animation-blip miss on Android's live detail page does not tear the
  // HUD down and churn back. Reset on any successful detail read and per session.
  const detailReleaseMissesRef = useRef<number>(0);
  // ── My-Pokémon (individual caught-Pokémon page) scan state ──────────────
  // Mirrors the species-detail machinery above: a lazily-imported OCR module,
  // a HUD latch (headline + status line + fact slots re-asserted by every
  // in-between frame), and a release grace counter so an animation-blip miss
  // cannot tear the latch down. The page shares the species-detail stillness
  // gate (detailGateRef) — one dwell, one probe, whichever page type claims it.
  const myPokemonModuleRef = useRef<typeof import("@/lib/myPokemonOcr") | null>(null);
  const myPokemonHudActiveRef = useRef<boolean>(false);
  const lastMyPokemonHeadlineRef = useRef<string | null>(null);
  const lastMyPokemonMessageRef = useRef<string>(MYPOKE_HUD_MESSAGE);
  const lastMyPokemonSlotsRef = useRef<string[]>([]);
  // Top-1% league ball for the PiP's corner badge (same rule as the list
  // page's dot-rail ball); null = no badge, re-asserted with the slots.
  const lastMyPokemonLeagueBadgeRef = useRef<
    "great" | "ultra" | "master" | null
  >(null);
  // Hero band (2026-08-26): the big league-percent line + the evolution
  // callout, re-asserted with the slots like the badge.
  const lastMyPokemonPvpLineRef = useRef<string | null>(null);
  const lastMyPokemonEvoLineRef = useRef<string | null>(null);
  const lastMyPokemonTargetRef = useRef<
    MyPokemonPipSlots["target"]
  >(null);
  // Split-layout left column (2026-08-31): the lean fact lines that ride
  // with the target visual; re-asserted like the slots.
  const lastMyPokemonHeroLinesRef = useRef<string[]>([]);
  // The move pills under the fact lines (2026-09-03); re-asserted like them.
  const lastMyPokemonMovePillsRef = useRef<string[]>([]);
  const lastMyPokemonMovePillColorsRef = useRef<string[]>([]);
  // 2026-09-22 Android HUD extras (border colour, badge face, recommended
  // moves, showcase strip); re-asserted like the pills.
  const lastMyPokemonIvPercentRef = useRef<number | null>(null);
  const lastMyPokemonFocusRef = useRef<"ivs" | "moves">("ivs");
  const lastMyPokemonMoveRecsRef = useRef<string[]>([]);
  const lastMyPokemonShowcaseRef = useRef<ShowcaseHudPick | null>(null);
  // showcaseStandingFor ranks every scanned record, so it runs once per
  // record per box size, not once per frame.
  const showcaseCacheRef = useRef<{ key: string; pick: ShowcaseHudPick | null } | null>(null);
  // HUD-ONLY latch for CP and max HP within one sighting (2026-09-03 device
  // report: the CP line blinked on and off). The CP consistency gate drops a
  // CP the formula cannot place, and an occluded frame reads none at all, so
  // frame to frame the HUD facts alternated between "CP 627" and nothing
  // while the record path stayed correct. Once a sighting has shown a CP,
  // keep showing it until the identity changes. Never feeds a write.
  const myPokemonHudLatchRef = useRef<{
    key: string;
    cp: number | null;
    hpMax: number | null;
    /** The last NAMED sighting's species and name on this dwell (name carry). */
    speciesId: number | null;
    displayName: string | null;
  }>({
    key: "?",
    cp: null,
    hpMax: null,
    speciesId: null,
    displayName: null,
  });
  const myPokemonReleaseMissesRef = useRef<number>(0);
  /**
   * The last My Pokémon probe said the screen is the STORAGE BOX. The probe
   * runs once per dwell, but the grid extractor runs on every admitted frame
   * of that dwell; without this latch the box's CP labels were read as dex
   * numbers on the frames between probes and written to the active dex
   * (2026-09-18 owner report: a search-filtered box wrote #034 / #221 / #190
   * to the Purified dex). Cleared by the next probe that sees something else.
   */
  const storageBoxLatchRef = useRef<boolean>(false);
  /** One hand-off to Pokémon GO per Live Scan start (2026-09-18 owner ask). */
  const handedOffToGoRef = useRef<boolean>(false);
  const frameSizeLoggedRef = useRef<boolean>(false);
  const appActiveSkipLoggedRef = useRef<boolean>(false);
  /**
   * The record the HUD card last SHOWED (2026-09-30), latched or not. A
   * nameless frame with payload (moves, provenance, max moves) merges into
   * it, so a card that stands on a record found by CP/IV match still gains
   * the moves the trainer scrolled to. Cleared with the card.
   */
  const lastMyPokemonHudRecordIdRef = useRef<string | null>(null);
  /**
   * The HUD's current shape (2026-09-18 owner ask: "stop switching back and
   * forth"). Portrait is set when a Pokémon page latches and now STAYS until
   * a Pokédex grid is recognised: leaving a Pokémon's page to rename it, or a
   * frame that misses, used to flip the card to landscape and back again.
   * `markHudOrientation` is used inside the bridge payloads so the ref can
   * never drift from what the card was told.
   */
  const hudOrientationRef = useRef<"landscape" | "portrait">("landscape");
  const markHudOrientation = (o: "landscape" | "portrait"): "landscape" | "portrait" => {
    if (hudOrientationRef.current !== o) {
      // eslint-disable-next-line no-console
      console.log("[live-scan] hud orientation", { from: hudOrientationRef.current, to: o });
    }
    hudOrientationRef.current = o;
    return o;
  };
  /** When a caught-Pokémon page last put the card in portrait (ms epoch). */
  const lastMyPokemonLatchAtRef = useRef<number>(0);
  /**
   * A SPECIES page lies landscape like the grid (owner direction 2026-09-23)
   * EXCEPT while the card is still in a Pokémon dwell: the species probe can
   * fire on the frames between two Pokémon pages, and flipping the card
   * landscape there and back on the next Pokémon is the "constant switching"
   * the 2026-09-18 rule ended. So: portrait stays if the last Pokémon page is
   * more recent than the last recognised grid; a species page reached from
   * the grid turns the card landscape.
   */
  const speciesPageOrientation = (): "landscape" | "portrait" =>
    hudOrientationRef.current === "portrait" &&
    lastMyPokemonLatchAtRef.current > lastRecognisedGridAtRef.current
      ? "portrait"
      : "landscape";
  /** Frames the stability gate swallowed in a row under a My Pokémon hold. */
  const myPokemonHeldUnstableRef = useRef<number>(0);
  // When a frame last read as a Pokédex GRID page (a dex_* colour family):
  // the one screen change that should drop a latched Pokémon at once rather
  // than after the miss grace.
  const lastRecognisedGridAtRef = useRef<number>(0);
  // Two-frame commit gate: a record is written only when TWO consecutive
  // probe frames agree on (name, CP, max HP). A single frame's OCR wobble
  // (Psyduck's CP 585 read once as 505, 2026-08-20 device log) would otherwise
  // mint a duplicate record keyed on the wobble.
  const lastMyPokemonSightingRef = useRef<{
    key: string;
    cp: number | null;
    hpMax: number | null;
    /** "atk/def/hp" when this frame read the IV bars, else null. */
    iv: string | null;
    /**
     * The max HP read earlier in this dwell (same name, same CP), carried
     * while the appraisal overlay hides it (2026-10-07). Sent to the store
     * as hpVeto so a same-CP record with a DIFFERENT HP is never the match.
     */
    hpSeen: number | null;
    /**
     * This frame's shiny read (2026-09-28): the flag reaches the store only
     * when two consecutive frames of the same sighting both read shiny. A
     * plain Chansey was recorded shiny off one frame whose cream egg shading
     * and warm backdrop leaked into the palette vote; the store never turns
     * the flag off again, and it cross-writes the Shiny dex.
     */
    shiny: boolean;
    /** Consecutive reads that agreed on `iv` (three make it real). */
    ivAgree?: number;
  } | null>(null);
  // Keys (name/species) that committed at least once this session, so the HUD
  // can honestly distinguish "already saved" from "first sighting, confirming".
  const myPokemonCommittedKeysRef = useRef<Set<string>>(new Set());
  // Sightings whose preferred name has already been put on the clipboard
  // this session (2026-09-03). The scan keeps reading the same Pokémon frame
  // after frame, and once the trainer leaves the appraisal to paste, the
  // page still names it; copying on every frame would clobber whatever they
  // are doing with the clipboard. Once per Pokémon, when its facts are in.
  const myPokemonCopiedKeysRef = useRef<Set<string>>(new Set());
  // Session counters for the finish log + the fallback-prompt suppression.
  const myPokemonReadsRef = useRef<number>(0);
  const myPokemonSavesRef = useRef<number>(0);
  // Dwell continuity: the record THIS latch committed, so deep-scrolled
  // frames (name and CP off-screen, moveset on) can attach their payload to
  // the Pokémon the user is still looking at. Cleared when the identity
  // changes, when the latch releases, and per session.
  const myPokemonActiveRecordIdRef = useRef<string | null>(null);
  // Pro is read once per render into a ref so the frame loop (which is not a
  // React render) always sees the current entitlement without re-subscribing.
  const isProRef = useRef<boolean>(opts.isPro === true);
  isProRef.current = opts.isPro === true;
  // G-Max QA — stable id for the current scan, generated at session start and
  // stamped onto the QA event emitted at finish() so the backend monitor can
  // dedupe per scan.
  const scanIdRef = useRef<string | null>(null);
  const pipelineRef = useRef<DexPipelineRunner | null>(null);
  // b17d Fix 3 — region-change latch. The per-frame classifier occasionally
  // misreads the region for a single frame (TestFlight log: 8 frames latched
  // `hisui` before settling `kanto`). Routing a write off one noisy frame
  // corrupts the wrong region's dex, so we require N consecutive agreeing
  // frames above a confidence floor before trusting a region change. Created
  // per session in `start()` and reset on teardown.
  const regionStabilizerRef = useRef<DexRegionStabilizer | null>(null);
  // Latency hardening (2026-06-22) — bounded sticky scan-context tracker. Locks
  // a provisional (region/variant/colour) context from strong page-level signals
  // within 1-2 good frames so a weak/failed-OCR frame on the SAME dex page can
  // reuse it instead of being dropped as `no_variant`, and revalidates/resets the
  // moment a real region/variant/colour switch (or leaving the dex page) is seen.
  // It only ever yields a ROUTING HINT; every write still clears the unchanged
  // candidate gates. Created per session in `start()`, reset on teardown.
  const scanContextRef = useRef<ScanContextTracker | null>(null);
  /** When the My Pokémon HUD last saw its page (time-based release). */
  const myPokemonLastSeenAtRef = useRef<number>(0);
  /** Last capture frame received (the stall watchdog's clock). */
  const lastFrameAtRef = useRef<number>(0);
  const frameWatchdogRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Sole-colour carry inputs (see scanVariantInput): the variant the last
  // confident pill read named, and the last frame's page-colour family.
  const lastPillVariantRef = useRef<PokedexVariant | null>(null);
  const lastHudLineRef = useRef<string>("");
  const lastPageColourFamilyRef = useRef<string | null>(null);
  // Pre-context top-row buffer (Push 11). Retains the dex numbers OCR read just
  // BEFORE the variant context locked — tagged with the provisional variant they
  // were read under — and promotes them for re-observation once a lock arrives
  // under the SAME variant. This is the G-Max row-1 (003/006/009) retention fix:
  // a user who opens at the top and scrolls before the tab settles no longer
  // loses the top row. A promoted row only PRIMES corroboration (one synthetic
  // sighting); it never clears the 2-frame/sprite/member gates on its own, so it
  // can never write without a live re-confirmation under the locked variant.
  // Created per session in `start()`, reset on teardown.
  const preContextRef = useRef<PreContextBuffer | null>(null);
  // b13c.6 — the Build-13a session-wide sticky variant lock is removed. The
  // Build 195 device test showed every frame logging `"variant":"lucky"` even
  // as raw frames clearly transitioned through perfect/purified/shadow: the
  // first-seen lock forced ALL later frames back to lucky, so those variants'
  // catches were silently dropped. Variant is now re-derived per frame from the
  // adapter's classifier output and routed straight into the session, which
  // keeps an independent (variant, dexId) dedupe slot for each.
  const phaseRef = useRef<DexLiveScanPhase>("idle");
  const initialVariantRef = useRef<PokedexVariant>(opts.initialVariant);
  const onNewAddsRef = useRef<UseDexLiveScanOptions["onNewAdds"]>(opts.onNewAdds);
  onNewAddsRef.current = opts.onNewAdds;
  const frameListenerRef = useRef<PluginListenerHandle | null>(null);
  const visibilityListenerRef = useRef<(() => void) | null>(null);
  const lifecycleListenerRef = useRef<(e: Event) => void>((e: Event) => {
    // eslint-disable-next-line no-console
    console.log("[live-scan] page lifecycle", { event: e.type, phase: phaseRef.current });
  });
  const finishedListenerRef = useRef<PluginListenerHandle | null>(null);
  const pollerRef = useRef<ReturnType<typeof createAppGroupFramePoller> | null>(null);
  const ocrModuleRef = useRef<typeof import("@/lib/pokedexOcr") | null>(null);
  const inFlightRef = useRef(0);
  // scan-speed (2026-06-22) — duplicate-frame suppressor. The 1 Hz broadcast
  // extension re-emits the SAME JPEG every second whenever the trainer holds
  // steady on a row, and the App-Group poller dispatches each one; re-OCRing
  // byte-identical pixels is pure wasted work (the read can only repeat). This
  // drops exact-content duplicates BEFORE the OCR fanout. It is NOT a write
  // gate: any change in content hashes differently and is always admitted, so
  // no candidate / observation / commit can be lost. Created per session in
  // start(), reset on teardown.
  const frameDedupRef = useRef<FrameDedup | null>(null);
  // Vision-only trial: the page-identity classify cache (which memoised the
  // deleted pre-OCR palette classification per content hash) was removed along
  // with the pre-OCR gate.
  // b17u — inter-frame scroll-stability gate state. `prevStabilityProfileRef`
  // holds the 1D luma band profile of the last frame we measured, so the next
  // frame can be diffed against it to detect mid-scroll motion the single-frame
  // blur gate cannot see. `scrollGateSkippedRef` counts frames skipped as
  // transitional this session; `scrollGatePassedRef` counts frames that cleared
  // the gate (b18-recall-investigation: pass-path telemetry). Both reset per
  // session.
  const prevStabilityProfileRef = useRef<number[] | null>(null);
  const scrollGateSkippedRef = useRef(0);
  // b18-recall-investigation: pass-side counter for skip-rate calculation.
  const scrollGatePassedRef = useRef(0);
  // 2026-07 device pass — was the last ADMITTED frame a re-read of a STATIC view
  // (no scroll) rather than genuinely new content? True when the dedup admitted a
  // `forced_reingest` / `cold_retry` (a held-steady view re-run through OCR),
  // false on a normal `admit` (content changed = the user scrolled). Drives the
  // low-confidence guidance: while stationary, "Hold steadier" is the wrong nudge
  // (re-reading the same frame can't resolve an ambiguous cell — only a fresh view
  // can), so we coach a scroll instead. Reset implicitly by each admitted frame.
  const admitWasStaticRef = useRef(false);
  /**
   * Scroll-stability diff for the frame currently being processed; null when
   * the gate is disabled so "unmeasured" is distinguishable from "still".
   * Read by the scan telemetry so a false write can be traced to (or cleared
   * of) mid-scroll motion — the 20260720 trace could do neither.
   */
  const lastStabilityDiffRef = useRef<number | null>(null);
  // Round-12 Fix 3 — PiP hold-steady coaching. The debounced detector decides
  // when to actively prompt the trainer to slow down / hold steady, driven by two
  // per-frame signals: motionHigh (a blur or scroll-stability drop this frame) and
  // lowCapture (numbers are being read but few reach confirmation, i.e. scrolling
  // past each row too fast). It owns NO write/gate/veto decision — it only chooses
  // the guidance phase the PiP pill shows. Created/reset per session.
  const holdSteadyRef = useRef<HoldSteadyDetector | null>(null);
  // Consecutive processed frames that read ≥1 dex number but committed nothing —
  // the low-sighting-accumulation signal that feeds the hold-steady detector's
  // `lowCapture` input. Reset to 0 the moment a frame commits. Mirrored into
  // `holdSteadyLowCaptureRef` so the motion-skip paths (which read no new numbers)
  // can carry the last computed value into the detector instead of forcing it false.
  const holdSteadyReadWithoutApplyRef = useRef(0);
  const holdSteadyLowCaptureRef = useRef(false);
  // Round-13 Fix B — present dexIds from the PREVIOUS processed frame, used to
  // detect ACTIVE scroll motion: if a dexId enters the grid that was not present
  // last frame, the view advanced (the trainer is scrolling). When the present
  // set is unchanged the grid is stationary, and a read-without-commit pattern is
  // a hard-to-confirm cell sitting still — NOT a "scroll slower" situation. This
  // gates the `lowCapture` signal so the prompt never fires (or sticks) while the
  // trainer is holding still. Reset per session.
  const prevPresentIdsRef = useRef<Set<number>>(new Set());
  // Vision-only trial: the pre-OCR palette gate (dexPreOcrGate) + its
  // SEARCHING/SCANNING hysteresis state + per-class tally counters were removed.
  // The gate was an OCR-cost heuristic for the retired Tesseract engine; Vision
  // OCRs every frame that clears the blur/scroll gates.
  // b18-coverage-fix: dedup concurrent finish() calls so all callers get the
  // same resolved value and setState fires only once per logical finish.
  const finishPromiseRef = useRef<Promise<{
    totals: DexLiveSessionTotals | null;
    pendingFallback: { count: number; ids: number[] } | null;
    reviewSummary: DexScanReviewSummary | null;
    conflicts: DexReconciliationConflict[];
    myPokemon: { reads: number; writes: number; pokemonSeen: number };
  }> | null>(null);
  // b18g — per-session coverage tracking.
  const frameIdHistoryRef = useRef<number[][]>([]);
  const allSeenIdsRef = useRef<Set<number>>(new Set());
  const consecutiveGapCountRef = useRef(0);
  const nudgeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // PR-E60 — the grid row the live steer is currently asking the user to
  // return to (0-based), or null when no steer is active. Cleared when a later
  // frame reads that row (user scrolled back) or the user taps Skip.
  const steerTargetRowRef = useRef<number | null>(null);
  const CONSECUTIVE_GAP_THRESHOLD = 2;
  // PR-C Problem 1: session-sticky last confidently-resolved variant. The
  // two-signal vote falls back to this when both signals are inconclusive on a
  // frame, so a brief glare/motion blur on the tab pill doesn't reset us to
  // "pokemon". Only confident reads (tab+tint / tab-only / tint-only) update it;
  // a "sticky" or "unresolved" vote leaves it untouched.
  const lastKnownVariantRef = useRef<TabVariant | null>(null);
  // Latest known PiP-active state, so the per-frame OCR can mask the PiP
  // overlay rectangle out of its number-ROI candidates. Updated from the
  // PiP diagnostics read below and from start/stop transitions.
  // Session-cumulative DISTINCT genuine new-to-dex adds. The user-facing
  // "N dex entries updated" count derives from this so it matches the "Added:"
  // id grid (both fed only by frameApply.newlyAdded), instead of totals.applied,
  // which sums the store's per-frame applied count (re-confirmations included)
  // and therefore overcounts.
  const newAddsRef = useRef<Set<number>>(new Set());
  // scan-speed 2026-06-30 — wall-clock timing recorder for the session. Pure
  // bookkeeping: per-frame compute ms + OCR-lane ms + real-time dwell between
  // commits. Answers "where did the 20-40s of holding actually go: slow OCR or
  // too-tight gating?" — the frame-index telemetry can't see seconds. Never
  // affects a write or a gate.
  const scanTimingRef = useRef(createScanTiming());
  // scan-speed 2026-06-30 — guard so the timing snapshot emits exactly once per
  // session, regardless of how the session ends. finish() calls stop(true), and
  // a tester can also end a scan via a direct stop()/cancel — both must surface
  // the wall-clock line. Reset in start(); set on first emit inside stop().
  const scanTimingLoggedRef = useRef(false);
  const pipActiveRef = useRef(false);
  // The HUD's own state pushes (2026-09-22): the per-frame
  // `await getPipDiagnostics()` in processFrame was a bridge round trip on
  // every frame purely to fill pipActive.
  const pipDiagRef = useRef<PipDiagnostics | null>(null);
  useEffect(() => subscribePipState((d) => { pipDiagRef.current = d; }), []);
  // b18-pre-ocr-gate-pip fix: the live-scan PiP HUD is shown the moment Start
  // Live Scan is tapped (autoStartPip, inside start()), and the scan owns its
  // lifecycle until stop(). pipActiveRef alone is driven by the per-frame native
  // PiP diagnostics (pipDiag.isActive), which only update once a frame has been
  // OCR'd and committed — so during the initial SEARCHING phase it can still read
  // false and the gate skips PiP-slab zone exclusion, letting the parked dark PiP
  // card poison the backdrop average and trip the dark-frame fast path
  // (nexus_dex_ui -> drop) on every real-dex frame, leaving the gate stuck in
  // SEARCHING. This flag is true for the whole session so the gate excludes the
  // PiP slab from frame one, independent of when native diagnostics catch up.
  const pipOverlayActiveRef = useRef(false);
  // Vision-only trial: the per-session last-dex-colour-family ref AND the
  // colour-family transition-hysteresis tracker were removed. They mirrored the
  // deleted pre-OCR gate's colour tracker. The colour veto still runs — the
  // family is now derived per frame inside extractPokedexFrame
  // (deriveVetoOnlyFamily) and returned on gridFrame.pageColourFamily, which the
  // routing-side veto (resolveTrustedVariant / adapter) reads directly.
  // bug3 Part 4 — the last trusted live variant this session, for the PiP HUD
  // "Scanning [X] Dex" line. null until the first frame resolves a variant
  // (dumb-mode HUD shows "Open your Pokédex"); updated on every committed frame
  // so the HUD type tracks a mid-session dex switch (Part 3) live.
  const liveDexVariantRef = useRef<PokedexVariant | null>(null);

  // PiP gap-guidance: cache the missing/caught/total for the active variant+
  // region so the HUD can point the trainer at the gaps. Recomputed only when a
  // new catch commits or the variant/region changes (reading the store every
  // frame is wasteful and never necessary — the gap set only moves on a commit).
  const scanTargetsCacheRef = useRef<{ key: string; targets: ScanTargets } | null>(null);
  // Last lead (minimum) on-screen dex id, to derive scroll direction for the
  // directional guidance ("↓ keep scrolling" / "↑ scroll back").
  const lastLeadIdRef = useRef<number | null>(null);
  // OFF-GRID (2026-09-08 owner report): consecutive frames on which the reader
  // resolved no cell at all. Two in a row means the trainer has left the
  // Pokédex (a catch screen, the friends list, an encounter): the HUD then
  // says so instead of carrying the last grid's "Scroll down to the next rows"
  // coaching onto a screen that has no rows, and the Scanned chips stand down
  // until a grid is back. One blank frame is a blur or a scroll smear and keeps
  // the copy it had.
  const offGridFramesRef = useRef(0);
  const lastOnGridAtRef = useRef(0);
  // When Pokédex chrome (region / filter / progress) was last read: the
  // coaching's "on the grid" needs it, like the write gate does.
  const lastGridChromeAtRef = useRef(0);
  // Whether THIS run ever saw the Pokédex at all (lastGridChromeAtRef is not
  // reset per run): the run report's "wrong screen" reading (lib/scanOutcomes).
  const sawPokedexRef = useRef(false);
  // The guidance line CURRENTLY on the HUD. The scan pushes at frame rate, and the
  // per-frame phase/coaching signals flicker (a stray "Slow down" between
  // "Continue scanning slowly" frames, confidence flapping reading_grid ↔
  // low_confidence), which churned the copy far faster than anyone can read —
  // 2026-07-29: "jumps constantly from hold steady to slow down to scanning, over
  // and over". So this is a DEBOUNCE, not a plain throttle: a NEW message must
  // persist as the candidate for GUIDANCE_TEXT_MIN_MS before it replaces the
  // committed line, so a one-frame flicker never reaches the HUD and the copy
  // settles on whatever the scan is steadily saying. A genuine scroll-DIRECTION
  // change bypasses it — that is a real user action worth immediate feedback.
  const guidanceTextRef = useRef<
    {
      at: number;
      message: string;
      detail?: string;
      arrow: GuidanceArrow;
      bubble: "green" | "yellow" | "orange" | "red";
      /**
       * Which coaching reason (if any) produced the committed line. Coaching copy
       * is TECHNIQUE copy ("Hold steadier", "Slow down") and is only true while
       * the trainer is actually moving, so it must be releasable the moment we
       * learn the screen is still — see releaseCoachingOnStillFrame.
       */
      coaching: CoachingReason;
    } | null
  >(null);
  // The pending candidate for the guidance line and when it first appeared. Reset
  // whenever the candidate changes, so only a message that holds still for the
  // debounce window gets promoted. See guidanceTextRef.
  const guidancePendingRef = useRef<{ message: string; since: number } | null>(null);
  // Consecutive processed frames the "blurry" coaching has been emitted for. The
  // hold-steady detector has a hard auto-expire (maxShownFrames) so a wedged
  // signal can never pin "Slow down" on; the low-confidence "blurry" branch had
  // no such bound and could sit on the HUD for the rest of the session.
  const blurryCoachingFramesRef = useRef(0);

  // Keep the variant ref fresh so a swipe in the parent Dex page is
  // picked up by frames that arrive after the swipe.
  useEffect(() => {
    initialVariantRef.current = opts.initialVariant;
  }, [opts.initialVariant]);

  // Broadcast-liveness observer (2026-07-09 stop-label / desync fix). The ONLY
  // authority for "the iOS system broadcast actually ended (red bar off)" is the
  // `finished` Darwin notification — the user tapping Stop in the picker sheet, or
  // ending it from Control Center. stop()/finish() merely REQUEST a stop (they
  // present the sheet); they must NOT claim the broadcast is stopped, or the menu
  // desyncs when the user dismisses the sheet without stopping — the broadcast
  // stays live (red bar on) while our UI has already reset to idle/saved (the
  // reported bug). This hook-lifetime observer flips broadcastPhase to "stopped"
  // the instant iOS confirms, so the "still broadcasting" warning clears exactly
  // when the red bar does and never before. It does NOT finalise the scan — the
  // session-scoped finishedListenerRef (created in start(), torn down in stop())
  // still owns the Control-Center-stop-mid-scan → finish() path. Both can fire on
  // the same notification; finish() is idempotent (finishPromiseRef + phase guard).
  useEffect(() => {
    // Native-only: the broadcast plugin no-ops (and `addListener` is absent)
    // off native, so skip registration on web and under test — matches how
    // start() only touches the bridge after canAutoStartBroadcast(). iOS
    // (ReplayKit) and Android (MediaProjection) both emit `finished`.
    if (getPlatform() === "web") return;
    let handle: PluginListenerHandle | null = null;
    let cancelled = false;
    // Native picker diagnostics into the scan log (2026-09-24): one line per
    // picker tap with the picker window's state, so an export can say why a
    // Stop tap presented "Start".
    let logHandle: PluginListenerHandle | null = null;
    void onNativeLog((line) => {
      // eslint-disable-next-line no-console
      // "[live-scan]" prefix: the exported scan log keeps only [live-scan] /
      // [dex-scan] lines (dexScanLog CAPTURED_PREFIXES).
      console.log("[live-scan] native", line);
    }).then((h) => { logHandle = h; }).catch(() => null);
    void (async () => {
      try {
        const h = await onFinished(() => {
          setState((prev) =>
            prev.broadcastPhase === "active" ||
            prev.broadcastPhase === "starting" ||
            prev.broadcastPhase === "stopping"
              ? { ...prev, broadcastPhase: "stopped" }
              : prev,
          );
        });
        if (cancelled) {
          void h.remove();
          return;
        }
        handle = h;
      } catch {
        /* plugin unavailable — leave broadcastPhase to the stop()/start() path */
      }
    })();
    return () => {
      cancelled = true;
      if (handle) void handle.remove();
      if (logHandle) void logHandle.remove();
    };
  }, []);

  // b17d Fix 2 — single source of truth for the scanned variant.
  //
  // Returns the active dex pill tab from app state. `opts.initialVariant` is the
  // Pokédex page's `activeVariant` (the pill the user has selected); the ref is
  // kept current by the effect above so a mid-scan swipe is reflected on the
  // very next frame. The live scan loop routes EVERY frame's catches by this,
  // never by the pixel/hue/chip-text classifiers — those are diagnostics only.
  const getActiveDexVariant = useCallback(
    (): PokedexVariant => initialVariantRef.current,
    [],
  );

  // SOLE-COLOUR CARRY (2026-09-24 device pass: the 100% dex applied #143/#144
  // on its first frames, then held for the rest of the page). Perfect needs
  // the filter pill to AGREE with the peach page before it resolves, and the
  // pill read comes back empty on many frames of that page. Once the scan
  // context has locked a single-variant page (peach = Perfect, mint =
  // Purified, storm purple = Shadow) with that variant, which only happens
  // after a pill read named it, a frame with an unreadable pill keeps it
  // instead of holding. It stands in for the app-state variant, so a pill
  // read naming something else still overrides it, and the colour veto still
  // rejects it the moment the page colour changes (which also drops the lock).
  // Multi-variant colours (cyan, golden, pink) never carry: their tint cannot
  // tell their variants apart.
  //
  // Second device pass the same day: the pill was read as Perfect while the
  // page was still animating from cyan to peach, the colour change then reset
  // the context and it re-locked with NO variant, so the context route alone
  // never carried. The direct route: the last confident pill read named this
  // variant, and the last frame's page colour is that variant's sole colour.
  const scanVariantInput = useCallback((): PokedexVariant => {
    const ctx = scanContextRef.current?.current();
    if (ctx?.variant != null && ctx.colourFamily != null && soleVariantForColourFamily(ctx.colourFamily) === ctx.variant) {
      return ctx.variant;
    }
    const named = lastPillVariantRef.current;
    if (named != null && soleVariantForColourFamily(lastPageColourFamilyRef.current) === named) {
      return named;
    }
    return getActiveDexVariant();
  }, [getActiveDexVariant]);

  const setPhase = useCallback((next: DexLiveScanPhase, patch?: Partial<DexLiveScanState>) => {
    const prevPhase = phaseRef.current;
    phaseRef.current = next;
    // [live-scan] b13c.2 — every phase transition is logged centrally so the
    // device trace shows the full button-tap → start → scanning → finish path
    // even when zero frames arrive.
    // eslint-disable-next-line no-console
    console.log("[live-scan] phase", prevPhase, "→", next, patch ?? {});
    setState((prev) => ({ ...prev, phase: next, ...(patch ?? {}) }));
  }, []);

  // ── Frame processing ───────────────────────────────────────────────
  const processFrame = useCallback(async (frame: BroadcastFrame) => {
    // AUTO-SWITCH TO GO (2026-09-18 owner ask). The first frame is the proof
    // the broadcast is live (the picker has been confirmed, frames are
    // flowing) and at that moment the screen being captured is still Nexus
    // Dex, so hand the trainer straight over to Pokémon GO instead of making
    // them find it. Once per start(); the result is not awaited and a failed
    // hand-off is silent: the scan runs the same either way. ANDROID ONLY
    // here: on iOS a `location.href` to a custom scheme from this loop has no
    // user gesture behind it and WebKit drops it (device pass, same day), so
    // the BroadcastAutoStart plugin opens GO natively on its first frame.
    if (!handedOffToGoRef.current) {
      handedOffToGoRef.current = true;
      // The launch result reaches the scan log from the native side
      // ("[live-scan] native go launch from JS: ...", 2026-09-30).
      if (getPlatform() === "android") void openPokemonGo().catch(() => null);
    }
    // The storage box (a grid of caught Pokémon, a CP label under each) is
    // NOT a Pokédex grid: its CP figures read as dex numbers and the grid
    // scan ran on it (2026-09-04 device report). The my-Pokémon probe names
    // the box; the frame then idles instead of reaching the grid extractor.
    let storageBoxFrame = false;
    // [dex-scan] probe: fires for every frame the producers deliver, BEFORE
    // the guards below, so a device log proves the JS dex loop is reached
    // (vs. the native QA LiveScanPipeline). bytes/phase tell us why a frame
    // would then be dropped (wrong phase, no session).
    // eslint-disable-next-line no-console
    if (DEX_SCAN_VERBOSE) console.log("[dex-scan] frame loop tick", {
      ts: frame.ts || Date.now(),
      bytes: frame.bytes,
      phase: phaseRef.current,
      inFlight: inFlightRef.current,
    });
    // Limit OCR concurrency: Tesseract on iOS chews CPU and we don't
    // need to read every frame to update the Dex. Two in flight is
    // plenty given the 1 Hz broadcast cadence.
    if (inFlightRef.current >= 2) return;
    if (phaseRef.current !== "scanning") return;
    if (!sessionRef.current) return;
    // Our own screens are never scanned (2026-09-30 Galaxy run: the My
    // Pokémon record page read back as "Lugia CP 2607" while the trainer
    // was in Nexus Dex). Android only: on iOS the app cannot be in front of
    // a broadcast of itself in any way that matters, and its frames stop.
    if (getPlatform() === "android" && isAppActive()) {
      if (!appActiveSkipLoggedRef.current) {
        appActiveSkipLoggedRef.current = true;
        // eslint-disable-next-line no-console
        console.log("[live-scan] frames skipped while Nexus Dex is in front");
      }
      return;
    }
    appActiveSkipLoggedRef.current = false;
    // Frame geometry once per session (2026-09-30). Every ROI, pill and grid
    // constant is a fraction calibrated on 886x1918 iOS frames and ~580x1280
    // Android frames; an export from a phone that delivers something else
    // (a full-resolution ReplayKit buffer on an older iOS, a 16:9 screen)
    // needs to say so in its first lines. The decode is cached, so the
    // pipeline's own read of this frame costs nothing extra.
    if (!frameSizeLoggedRef.current) {
      frameSizeLoggedRef.current = true;
      const bytes = frame.bytes;
      void loadFrameImage(frame.dataUrl)
        .then((img) => {
          // eslint-disable-next-line no-console
          console.log("[dex-scan] frame size", {
            w: img.naturalWidth,
            h: img.naturalHeight,
            aspect: img.naturalWidth > 0 ? Number((img.naturalHeight / img.naturalWidth).toFixed(3)) : null,
            bytes,
            platform: getPlatform(),
            dpr: typeof window !== "undefined" ? window.devicePixelRatio : null,
            screen:
              typeof window !== "undefined" && window.screen
                ? `${window.screen.width}x${window.screen.height}`
                : null,
          });
        })
        .catch(() => null);
    }

    // Rebuild the exact detail-page HUD push from the latched refs, so every
    // in-between / re-assert site (a blur drop, an unstable animation frame, a
    // probe miss still inside the release grace window, and the main grid push)
    // repaints the SAME thing detail mode last showed — never grid-scan coaching
    // on top of a species page. Reads refs only, so it is safe to close over.
    //
    // Field contract (both native HUDs): `detailSpecies` is the species headline,
    // `message` the status/tease line under it, `detailVariantSlots` the variant
    // grid, `ctaText` the CTA. `detail` carries the species too because the iOS
    // renderer takes its headline from `detail` (Android takes it from
    // `detailSpecies`). BOTH tiers now carry the variant grid — Basic shows it as
    // a tease (proof the scan read the species) and is gated at the WRITE, not by
    // hiding the read; the CTA is the only Pro/Basic difference here.
    const buildDetailReassert = () => {
      const isPro = isProRef.current;
      const species = lastDetailSpeciesRef.current ?? undefined;
      return {
        phase: "reading_grid" as const,
        message: lastDetailMessageRef.current,
        detail: species,
        detailSpecies: species,
        detailVariantSlots: lastDetailSlotsRef.current,
        heroLines: [] as string[],
        // The league ball is an individual-Pokémon fact — a SPECIES page
        // must clear any ball a prior caught-page dwell lit.
        leagueBadge: "" as const,
        pvpLine: "" as const,
        evoLine: "" as const,
        targetSpriteUrl: "" as const,
        targetLeague: "" as const,
        targetRank: 0,
        targetName: "" as const,
        targetPercent: "" as const,
        targetAura: "" as const,
        targetLeagues: [] as string[],
        movePills: [] as string[],
ivPercent: 0,
        hudFocus: "" as const,
        moveRecs: [],
        ...showcaseHudFields(null),
        encounterCp: 0,
        ctaText: isPro ? "" : DETAIL_HUD_UPSELL_CTA,
      };
    };
    // True while detail mode owns the HUD for EITHER tier — the species ref is set
    // for Basic now too, so the upsell holds as steadily as the Pro variant grid.
    const detailReassertActive = () =>
      detailHudActiveRef.current && lastDetailSpeciesRef.current != null;

    // My-Pokémon-mode counterparts: rebuild the latched caught-Pokémon HUD push
    // (headline + status + fact slots) for the same in-between re-assert sites.
    const buildMyPokemonReassert = () => {
      const headline = lastMyPokemonHeadlineRef.current ?? undefined;
      return {
        phase: "reading_grid" as const,
        message: lastMyPokemonMessageRef.current,
        detail: headline,
        detailSpecies: headline,
        detailVariantSlots: lastMyPokemonSlotsRef.current,
        heroLines: lastMyPokemonHeroLinesRef.current,
        leagueBadge: lastMyPokemonLeagueBadgeRef.current ?? ("" as const),
        pvpLine: lastMyPokemonPvpLineRef.current ?? "",
        evoLine: lastMyPokemonEvoLineRef.current ?? "",
        targetSpriteUrl: lastMyPokemonTargetRef.current?.spriteUrl ?? "",
        targetLeague: lastMyPokemonTargetRef.current?.league ?? ("" as const),
        targetRank: lastMyPokemonTargetRef.current?.rank ?? 0,
        targetName: lastMyPokemonTargetRef.current?.name ?? "",
        targetPercent: lastMyPokemonTargetRef.current?.percentText ?? "",
        targetAura: lastMyPokemonTargetRef.current?.aura ?? ("" as const),
        targetLeagues: lastMyPokemonTargetRef.current?.leagues ?? [],
        movePills: lastMyPokemonMovePillsRef.current,
        movePillColors: lastMyPokemonMovePillColorsRef.current,
        ivPercent: lastMyPokemonIvPercentRef.current ?? 0,
        hudFocus: lastMyPokemonFocusRef.current,
        moveRecs: lastMyPokemonMoveRecsRef.current,
        ...showcaseHudFields(lastMyPokemonShowcaseRef.current),
        // The card's buttons re-read from the store on every re-assert
        // (2026-09-30: a Star tap did not stay lit; the next push must say
        // what the store says, and most pushes on a held page are these).
        ...cardRecordFields(),
        encounterCp: 0,
        ctaText: "",
      };
    };
    /** The record the card's Star / Trade / Open act on, and their lit state. */
    const cardRecordFields = () => {
      const id = myPokemonActiveRecordIdRef.current ?? lastMyPokemonHudRecordIdRef.current;
      const rec = id ? getMyPokemon(id) : null;
      return {
        recordId: rec ? rec.id : "",
        recordStarred: !!rec?.isFavourite,
        recordListed: !!(rec && isOnTradeList(rec.id)),
      };
    };
    const myPokemonReassertActive = () =>
      myPokemonHudActiveRef.current && lastMyPokemonHeadlineRef.current != null;

    /**
     * Clear every HUD panel that is scoped to ONE dex, because the user just
     * moved to a different one. Display-only: no write, gate, veto or committed
     * total is touched, and `newAddsRef` is deliberately NOT reset — that is the
     * session-wide "N entries saved this scan" count the finish summary reports,
     * which spans every dex the user visited.
     *
     * Three things go stale together on a dex change:
     *   1. the rolling "Added #…" grid (resetDexRecentAdds) — the original
     *      2026-06-22 bug, a Kanto G-Max screenshot still showing "#345" from a
     *      prior Shadow apply;
     *   2. the scanned/target progress — `scanned` counts distinct cells SEEN
     *      while `target` is recomputed per dex, so after a switch the numerator
     *      described the old dex and the denominator the new one. Resetting the
     *      numerator loses earlier coverage if the user switches back, which is
     *      the right trade for a progress chip: a wrong ratio is worse than a
     *      restarted one, and per-variant coverage sets would be real machinery
     *      for a display value;
     *   3. the debounced guidance line, which names the variant — without this
     *      the old dex's label stays up for GUIDANCE_TEXT_MIN_MS after the switch.
     */
    const clearPerDexHudState = (): void => {
      resetDexRecentAdds();
      scannedIdsRef.current = new Set();
      guidanceTextRef.current = null;
      guidancePendingRef.current = null;
      lastLeadIdRef.current = null;
      scanTargetsCacheRef.current = null;
    };

    // Re-assert the last COMMITTED grid-guidance line on a DROPPED frame (blur or
    // mid-scroll) instead of pushing that frame's own phase message. The main push
    // debounces its line (guidanceTextRef), but the blur / scroll-drop paths
    // bypassed the debounce and pushed their own hold-steady / "Scanning…" copy —
    // so on a still-but-noisy grid (shiny sparkle, micro-motion, a stray blur
    // frame) the HUD flickered between the debounced line and per-drop coaching.
    // This is the grid-page counterpart to buildDetailReassert: hold the stable
    // line. Returns false when there is no line yet (startup) so the caller falls
    // through to the directive copy ("Open Pokédex" / "Start scrolling").
    const reassertHeldGuidance = (): boolean => {
      const line = guidanceTextRef.current;
      if (line == null) return false;
      void setDexPipPhase({
        phase: "reading_grid",
        message: line.message,
        detail: line.detail,
        bubble: line.bubble,
        appliedCount: newAddsRef.current.size,
      });
      return true;
    };

    /**
     * Release a latched COACHING line once we have proof the screen is still.
     *
     * Coaching copy is TECHNIQUE copy — "Hold steadier / Too blurry to read: ease
     * the scroll", "Slow down" — and it is only ever true while the trainer is
     * MOVING. A byte-identical duplicate frame is direct proof they are not: the
     * capture produced the exact same pixels, so nothing on screen moved.
     *
     * Device log 2026-08-10: the "blurry" line got stuck up on a stationary
     * screen. Once a held view exhausts the dedup's MAX_FORCED_REINGESTS_PER_VIEW
     * budget, EVERY later duplicate returns early below without pushing the HUD at
     * all, so whatever line was last committed — a coaching line, if the last real
     * read happened to be low-confidence — is frozen there until the user scrolls.
     * Telling a stationary trainer to hold steadier is both wrong and unactionable.
     *
     * So on a duplicate frame we swap the committed coaching line for the resting
     * instruction, keeping the same sub-line (the variant label) and dropping the
     * orange bubble. Runs once per latch (the line is rewritten with coaching:null),
     * and never touches a gate, veto, or write — HUD copy only.
     */
    const releaseCoachingOnStillFrame = (): void => {
      const line = guidanceTextRef.current;
      if (line == null || line.coaching == null) return;
      guidanceTextRef.current = {
        ...line,
        message: DEX_SCAN_RESTING_INSTRUCTION,
        arrow: "hold",
        bubble: "green",
        coaching: null,
      };
      guidancePendingRef.current = null;
      blurryCoachingFramesRef.current = 0;
      // eslint-disable-next-line no-console
      if (DEX_SCAN_VERBOSE) console.log("[dex-scan] coaching released (still frame)", {
        was: line.coaching,
        message: DEX_SCAN_RESTING_INSTRUCTION,
      });
      reassertHeldGuidance();
    };
    // scan-speed — duplicate-frame suppression BEFORE the OCR fanout. A frame
    // whose content is byte-identical to one already in flight or just
    // processed (a held-steady screen the 1 Hz extension re-emitted) carries no
    // new evidence, so re-OCRing it can only repeat the last read. Drop it here
    // so it never occupies an OCR concurrency slot. Exact-match only: a scroll,
    // a newly-caught sprite, or a variant swipe changes the content hash and is
    // always admitted, so the write path is unaffected.
    const dedup = frameDedupRef.current;
    // DEMAND-DRIVEN INTAKE (2026-07-23). Re-reading a held-steady screen is only
    // worth the power when something is actually waiting on another look: a cell
    // holding partial evidence, or a species page the detail gate has not probed
    // yet. Both are cheap to ask. With demand the dedup re-reads on the tighter
    // cadence so a held row fills in instead of needing a scroll; without it the
    // screen is left alone, and the per-view budget stops even a demanded view
    // re-reading forever. See dexFrameDedup's MAX_FORCED_REINGESTS_PER_VIEW.
    const demand =
      (sessionRef.current?.pendingEvidenceCount() ?? 0) > 0 ||
      detailGateRef.current.stillFrames > 0;
    const dedupDecision = dedup?.consider(frame.dataUrl, { demand }) ?? null;
    if (dedupDecision && !dedupDecision.admit) {
      recordScanFrameSkippedDuplicate();
      // A suppressed frame is byte-identical to one we already read, which is
      // the strongest evidence there is that the screen is NOT MOVING — exactly
      // what the detail-page dwell is waiting to establish. The dedup drops it
      // because the GRID reader gains nothing from a repeat, but the dwell does,
      // so count it here (see noteHeldFrame).
      //
      // Before this, the dwell counted only ADMITTED frames while the dedup
      // admits ~1 static frame in 3, so a 2-frame dwell took ~6s of wall clock
      // on a page the user was holding perfectly still. That is the "slow to
      // register a screen change / sometimes needs a scroll before it does
      // anything" symptom: scrolling was what produced non-duplicate frames.
      //
      // `duplicate_in_flight` is excluded — that one means OCR is merely busy,
      // not that the screen is still, and it can fire on a moving page.
      if (dedupDecision.verdict === "duplicate_recent") {
        detailGateRef.current.noteHeldFrame();
        // Same proof, used for the HUD: the screen is still, so any latched
        // technique coaching ("ease the scroll" / "Slow down") is false and must
        // come down — this path is otherwise the one that freezes it, because it
        // returns without pushing the HUD at all. See releaseCoachingOnStillFrame.
        releaseCoachingOnStillFrame();
      }
      // eslint-disable-next-line no-console
      if (DEX_SCAN_VERBOSE) console.log("[dex-scan] frame skipped duplicate", {
        reason: dedupDecision.verdict,
        ts: frame.ts || Date.now(),
        bytes: frame.bytes,
        inFlight: inFlightRef.current,
        detailStillFrames: detailGateRef.current.stillFrames,
      });
      return;
    }
    if (dedupDecision?.verdict === "cold_retry") {
      // scan-speed (cold start): a held-steady frame whose prior read was
      // unproductive is being re-admitted so a warmed OCR engine can re-read it.
      // eslint-disable-next-line no-console
      if (DEX_SCAN_VERBOSE) console.log("[dex-scan] frame cold-start retry", {
        ts: frame.ts || Date.now(),
        bytes: frame.bytes,
      });
    }
    if (dedupDecision?.verdict === "forced_reingest") {
      // Round-8: a near-static view emitted its Nth consecutive byte-identical
      // frame, so instead of dropping it as duplicate_recent we force it back
      // through to OCR — the classifier needs the repeat frames to corroborate
      // low-id species across passes. Telemetry so we can see this firing on the
      // next on-device scan.
      // eslint-disable-next-line no-console
      if (DEX_SCAN_VERBOSE) console.log("[dex-scan] duplicate re-ingest forced", {
        reason: "duplicate_recent",
        dupCount: dedupDecision.dupCount,
        interval: dedupDecision.interval,
        ts: frame.ts || Date.now(),
        bytes: frame.bytes,
      });
    }
    // A `forced_reingest` / `cold_retry` admit is a re-read of a STATIC view (the
    // content hash is unchanged — the user is holding still); a plain `admit` means
    // the content changed, i.e. they scrolled. The low-confidence guidance below
    // reads this to decide "hold steadier" (moving/blur) vs "keep scrolling"
    // (stationary and stuck). Null dedup (disabled) → treat as moving, unchanged.
    admitWasStaticRef.current =
      dedupDecision?.verdict === "forced_reingest" ||
      dedupDecision?.verdict === "cold_retry";
    // A plain `admit` means the CONTENT changed. On a species page that is the
    // swipe to the next species — which the scroll-stability gate cannot see,
    // because two detail pages differ only by a sprite and a title (measured
    // band diffs of 0.3–4.0 against a threshold of 8 on 2026-07-23). Without
    // this the dwell never ends and the HUD stays latched on the species the
    // user has already left. See noteViewChanged for the full trace.
    if (dedupDecision?.verdict === "admit") {
      detailGateRef.current.noteViewChanged();
    }
    if (dedup && dedupDecision) dedup.markInFlight(dedupDecision.hash);
    inFlightRef.current += 1;
    // scan-speed 2026-06-30 — wall-clock timing for this frame. `frameStartMs`
    // is taken AFTER the cheap early-return gates (dedup/phase/inFlight) so the
    // recorder only times frames that actually do OCR/ingest work — the ones
    // whose seconds the user feels. `ocrMs` is filled around the extractor call;
    // `committedThisFrame` from frameApply.newlyAdded. Recorded once in `finally`.
    const frameStartMs =
      typeof performance !== "undefined" ? performance.now() : Date.now();
    let frameOcrMs = 0;
    // Dropped at the blur gate below (scan_perf.blur_drops, 2026-10-26): with
    // the frames that reached the grid OCR, this splits a "Nothing readable"
    // run into "never on the grid" and "the OCR ran and read nothing".
    let frameBlurDropped = false;
    // Cell number-ROIs this frame batched into its one native OCR call, carried
    // to the timing recorder so batch effectiveness lands in the session
    // `timing snapshot` (the per-frame log is DEX_SCAN_VERBOSE-only).
    let frameOcrRois = 0;
    let frameYieldMs = 0;
    let frameCommittedCount = 0;
    // scan-speed (cold start) — did this frame yield usable evidence (≥1 dex
    // number)? Drives the dedup's cold-start retry: an UNPRODUCTIVE read of a
    // held-steady frame (cold OCR engine / mid-load empty read) stays
    // re-admittable so a warmed engine can re-read the same static frame the
    // first pass failed on, instead of the scanner stalling until the user
    // scrolls. Set true the moment OCR reads any number; defaults false so any
    // early-return / failure path leaves the frame re-admittable.
    let frameProductive = false;
    // Round-12 (Fix 2): the variant this frame ultimately ingested under, handed
    // to the dedup's `markDone` so a held-steady PURIFIED view re-ingests its
    // duplicate frames on the relaxed interval (purified-scoped). Stays null for
    // any frame that never resolved/ingested a variant, so the dedup keeps the
    // unchanged R8 interval for everything else.
    let frameVariantHint: string | null = null;
    // Round-12 Fix 3 — feed the debounced hold-steady detector once per processed
    // frame. `motionHigh` is true on the blur / scroll-stability skip paths (the
    // camera is moving fast enough to drop frames); `lowCapture` carries the
    // running read-but-never-confirm signal. The detector debounces both, so a
    // single noisy frame never flips the prompt. On a state transition we emit
    // `[dex-scan] hold steady prompt` exactly once. Returns the decision so the
    // caller can fold it onto the PiP phase via `holdSteadyPipPhase`. Guidance
    // only — it touches no write, gate, veto, or confirmation decision.
    const observeHoldSteady = (motionHigh: boolean, lowCapture: boolean) => {
      const det = holdSteadyRef.current;
      if (!det) return { shown: false, reason: null as HoldSteadyReason | null };
      const decision = det.observe({ motionHigh, lowCapture });
      if (decision.changed) {
        // eslint-disable-next-line no-console
        if (DEX_SCAN_VERBOSE) console.log("[dex-scan] hold steady prompt", {
          reason: decision.reason,
          shown: decision.shown,
        });
      }
      return decision;
    };
    try {
      // Build 13a — frame quality gate. Estimate motion blur from a cheap
      // downsampled decode BEFORE the expensive OCR pass. The b12.3 live test
      // wasted Tesseract on frames smeared mid-scroll (0 cells on 64% of
      // frames); a too-blurry frame is dropped here so it never reaches OCR.
      // Confidence is only known post-OCR, so the confidence half of the gate
      // still lives in the session's gateDexLiveFrame — this is purely the
      // blur half. sharpnessFromDataUrl returns 1 (never gate) when it can't
      // decode, so this can never silently swallow every frame.
      // b13c: the blur gate is now flag-controlled. The b13a default (0.35)
      // dropped EVERY real broadcast frame on device, and because this block
      // `return`ed before `session.ingest()`, framesObserved never left 0 and
      // the pill stuck on "Open your Pokédex". The gate can now be retuned or
      // fully bypassed without a code change (frameQualityFlag).
      const blurCfg = getBlurGateConfig();
      const sharpness = await sharpnessFromDataUrl(frame.dataUrl);
      const blurGate = blurCfg.enabled
        ? evaluateFrameQuality(
            // Pass full confidence here so only the sharpness threshold can
            // reject: the real confidence gate runs downstream once OCR has a
            // read.
            { sharpness, confidence: 1 },
            { ...DEFAULT_FRAME_QUALITY_THRESHOLDS, minSharpness: blurCfg.minSharpness },
          )
        : { pass: true as const, sharpness, confidence: 1 };
      if (!blurGate.pass) {
        frameBlurDropped = true;
        // b13c: count the dropped frame as OBSERVED so the pill advances off
        // "Open your Pokédex" (framesObserved > 0) and diagnostics show frames
        // ARE arriving but are being blur-dropped — instead of a silent stall
        // that looks identical to "no broadcast frames at all".
        sessionRef.current?.noteFramePresented(frame.ts || Date.now());
        const totals = sessionRef.current?.snapshot() ?? null;
        // A confirmed detail page owns the HUD (Pro path). A single blurry frame
        // must not repaint grid coaching over it — re-assert the detail HUD and
        // wait for a sharp frame. Without this the HUD flickered species → "hold
        // steady" → species on Android, whose live capture never yields the
        // byte-identical still frames iOS holds on.
        if (detailReassertActive()) {
          void setDexPipPhase(buildDetailReassert());
          if (totals) setState((prev) => ({ ...prev, totals }));
          return;
        }
        // Same contract for a latched caught-Pokémon page: a blurry frame must
        // not repaint grid coaching over it.
        if (myPokemonReassertActive()) {
          // eslint-disable-next-line no-console
          console.log("[dex-scan] my-pokemon hold at blur gate", {
            sharpness: Number(sharpness.toFixed(3)),
            minSharpness: blurCfg.minSharpness,
          });
          void setDexPipPhase(buildMyPokemonReassert());
          if (totals) setState((prev) => ({ ...prev, totals }));
          return;
        }
        // eslint-disable-next-line no-console
        if (DEX_SCAN_VERBOSE) console.log("[dex-scan] frame gated pre-OCR (blur)", {
          reason: blurGate.reason,
          sharpness: Number(sharpness.toFixed(3)),
          minSharpness: blurCfg.minSharpness,
          framesObserved: totals?.framesObserved ?? 0,
        });
        if (totals) {
          // B37 (Hold-steady-stuck-off-dex): only coach "hold steady" when we
          // are actually on a dex page. Vision-only trial: the pre-OCR gate's
          // SEARCHING/SCANNING state was removed, so "on dex" is now inferred
          // from whether a live dex variant has been resolved this session
          // (liveDexVariantRef, set from the trusted per-frame variant). On a
          // sustained NON-dex view no variant resolves, so we RESET the detector
          // (and drop the held guidance line) and let pipPhaseFromTotals's off-dex
          // directive show through.
          const onDexNow = liveDexVariantRef.current != null;
          if (!onDexNow) {
            holdSteadyRef.current?.reset();
            guidanceTextRef.current = null;
            guidancePendingRef.current = null;
          }
          // Keep the hold-steady detector fed with this dropped (high-motion)
          // frame so the main push still coaches correctly, but do NOT let its
          // output paint the HUD here.
          const hs = onDexNow
            ? observeHoldSteady(true, holdSteadyLowCaptureRef.current)
            : { shown: false, reason: null as HoldSteadyReason | null };
          // On a recognised dex with a stable line already up, HOLD it instead of
          // flickering per-drop coaching over it (see reassertHeldGuidance).
          if (!(onDexNow && reassertHeldGuidance())) {
            const pipPhase = holdSteadyPipPhase(
              pipPhaseFromTotals(totals, phaseRef.current, onDexNow),
              hs.shown,
              hs.reason,
            );
            void setDexPipPhase({
              phase: pipPhase,
              message: dexScanningHudMessage(pipPhase, liveDexVariantRef.current),
              detail: debugOverlayDetail({
                frameTs: frame.ts || Date.now(),
                ocr: `blur drop s=${sharpness.toFixed(2)}`,
              }),
            });
          }
          setState((prev) => ({ ...prev, totals }));
        }
        return;
      }
      // b13c.4: prove pass-through. The build-193 trace had ZERO OCR
      // invocations because every frame was blur-gated; this log fires
      // immediately before OCR so the next device test confirms frames now
      // clear the gate and reach extractPokedexFrame.
      // eslint-disable-next-line no-console
      if (DEX_SCAN_VERBOSE) console.log("[dex-scan] gate pass", {
        // `sharpness` is the real per-frame metric (varies); the second field is
        // the static blur THRESHOLD it cleared, not a motion value. It was
        // previously logged as `motion`, which read as a "motion metric stuck at
        // 0.03 every frame" — there is no motion signal at this pre-OCR blur gate.
        sharpness: Number(sharpness.toFixed(3)),
        minSharpness: blurCfg.enabled ? blurCfg.minSharpness : "off",
      });

      // b17u — inter-frame scroll-stability gate. The blur gate above only sees
      // a single frame's sharpness; it passes a sharp frame even when the user
      // is scrolling. A mid-scroll frame is not a valid grid snapshot (numbers
      // and sprites belong to different rows), so the per-cell classifier emits
      // bogus "never seen" cells (the post-b17t log: 77% center/sprite-none).
      // Compare this frame's luma band against the previous measured frame; if
      // it shifted past the threshold the frame is transitional, so skip the
      // expensive OCR + classify + ingest entirely (like the blur gate) rather
      // than poison the cell observations. We still note the frame as presented
      // so framesObserved advances and the PiP pill never stalls. Flag-
      // controlled (frameStabilityFlag) so it can be retuned or bypassed.
      const scrollCfg = getScrollStabilityConfig();
      // Detail-page probe authorisation for THIS frame, decided by the same
      // stability verdict the grid scanner uses. Set on the stable path below.
      let shouldProbeDetail = false;
      // A frame the stability gate would have held that goes to the My Pokémon
      // probe anyway (see MYPOKE_HOLD_FORCE_PROBE_AFTER). Never a grid frame.
      let forcedMyPokemonProbe = false;
      if (scrollCfg.enabled) {
        // A confirmed detail page (Pro) latches the HUD onto that species. On
        // Android the live-captured detail page animates (3D model bob, shiny
        // sparkle) and re-encodes every frame, so the luma-band stability gate
        // below trips intermittently; without a latch the dwell is torn down and
        // the HUD churns species → grid coaching → species. While latched we hold
        // the HUD on unstable frames and force a re-probe on stable ones.
        const detailHold = detailReassertActive();
        const myPokemonHold = myPokemonReassertActive();
        const currProfile = await lumaBandProfileFromDataUrl(frame.dataUrl);
        const stability = decideFrameStability({
          prevProfile: prevStabilityProfileRef.current,
          currProfile,
          diffMax: scrollCfg.diffMax,
        });
        if (stability.advanceBaseline) {
          prevStabilityProfileRef.current = currProfile;
        }
        if (!stability.stable && myPokemonHold) {
          myPokemonHeldUnstableRef.current += 1;
          const held = myPokemonHeldUnstableRef.current;
          forcedMyPokemonProbe = held >= MYPOKE_HOLD_FORCE_PROBE_AFTER;
          if (held === 1 || forcedMyPokemonProbe) {
            // Not verbose-gated: this hold used to be silent, and it is the
            // one place a "stuck" card can come from without any read at all.
            // eslint-disable-next-line no-console
            console.log("[dex-scan] my-pokemon hold at stability gate", {
              diff: Number(stability.diff.toFixed(2)),
              threshold: scrollCfg.diffMax,
              held,
              forcedProbe: forcedMyPokemonProbe,
            });
          }
          if (forcedMyPokemonProbe) myPokemonHeldUnstableRef.current = 0;
        } else if (stability.stable) {
          myPokemonHeldUnstableRef.current = 0;
        }
        if (!stability.stable && !forcedMyPokemonProbe) {
          // Detail-page latch: hold the detail HUD through Android's animation
          // jitter instead of resetting the dwell and painting grid coaching over
          // it. A real navigation away lands on a grid frame, whose probe (forced
          // on the stable path below) returns "not a detail page" and releases the
          // latch. iOS never reaches here on a held page — its byte-identical
          // frames are deduped and counted via noteHeldFrame.
          if (detailHold) {
            sessionRef.current?.noteFramePresented(frame.ts || Date.now());
            void setDexPipPhase(buildDetailReassert());
            const heldTotals = sessionRef.current?.snapshot() ?? null;
            if (heldTotals) setState((prev) => ({ ...prev, totals: heldTotals }));
            return;
          }
          // A latched caught-Pokémon page animates just like a species page (3D
          // model bob) — hold its HUD through the jitter the same way.
          if (myPokemonHold) {
            sessionRef.current?.noteFramePresented(frame.ts || Date.now());
            void setDexPipPhase(buildMyPokemonReassert());
            const heldTotals = sessionRef.current?.snapshot() ?? null;
            if (heldTotals) setState((prev) => ({ ...prev, totals: heldTotals }));
            return;
          }
          // Motion ends any detail dwell: the next run of still frames earns a
          // fresh probe, which is exactly the "scroll down to reveal the lower
          // variant cards, then stop" flow.
          detailGateRef.current.noteFrame(false);
          scrollGateSkippedRef.current += 1;
          sessionRef.current?.noteFramePresented(frame.ts || Date.now());
          // The grid is mid-move: this frame is dropped by the scroll gate, and
          // the blend frame that follows reads one row's numbers against another
          // row's sprites. The evidence engine's corroboration requirement (an
          // ambiguous cell needs multiple agreeing frames — see dexLiveScanSession)
          // already prevents a lone blend frame from committing, so this branch
          // does NOT open main's single-frame-era settle window (not ported to
          // the evidence session).
          // eslint-disable-next-line no-console
          if (DEX_SCAN_VERBOSE) console.log("[dex-scan] scroll gate transitional frame skipped", {
            diff: Number(stability.diff.toFixed(2)),
            threshold: scrollCfg.diffMax,
            frame: scrollGateSkippedRef.current,
          });
          const totals = sessionRef.current?.snapshot() ?? null;
          if (totals) {
            // A mid-scroll drop is the high-motion failure mode — feed the
            // detector's motion signal so the MAIN push coaches correctly. B37:
            // only while we are on a dex. Vision-only trial: "on dex" is inferred
            // from liveDexVariantRef (a resolved live dex variant) now that the
            // pre-OCR gate's SCANNING state is gone (see the blur path above).
            const onDexNow = liveDexVariantRef.current != null;
            if (!onDexNow) {
              holdSteadyRef.current?.reset();
              guidanceTextRef.current = null;
              guidancePendingRef.current = null;
            }
            const hs = onDexNow
              ? observeHoldSteady(true, holdSteadyLowCaptureRef.current)
              : { shown: false, reason: null as HoldSteadyReason | null };
            // On a recognised dex with a stable line already up, HOLD it instead
            // of flickering per-drop coaching over it (reassertHeldGuidance) — the
            // grid-page counterpart to the detail latch. Startup / off-dex fall
            // through to the directive copy.
            if (!(onDexNow && reassertHeldGuidance())) {
              const pipPhase = holdSteadyPipPhase(
                pipPhaseFromTotals(totals, phaseRef.current, onDexNow),
                hs.shown,
                hs.reason,
              );
              void setDexPipPhase({
                phase: pipPhase,
                message: dexScanningHudMessage(pipPhase, liveDexVariantRef.current),
                detail: debugOverlayDetail({
                  frameTs: frame.ts || Date.now(),
                  ocr: `scroll skip d=${stability.diff.toFixed(1)}`,
                }),
              });
            }
            setState((prev) => ({ ...prev, totals }));
          }
          return;
        }
        // b18-recall-investigation: log every gate PASS with its diff so the
        // next scan gives us the full diff distribution (pass avg vs skip avg).
        // eslint-disable-next-line no-console
        if (DEX_SCAN_VERBOSE) console.log("[dex-scan] scroll gate frame passed", {
          diff: Number(stability.diff.toFixed(2)),
          threshold: scrollCfg.diffMax,
          advanceBaseline: stability.advanceBaseline,
        });
        scrollGatePassedRef.current += 1;
        lastStabilityDiffRef.current = stability.diff;
        // While a detail page is latched, probe EVERY stable frame (not just once
        // per dwell) so the HUD stays pinned to it and no stray stable frame falls
        // through to the grid extractor (which would misread the species page as a
        // grid). When the user leaves, the probe returns "not a detail page" and
        // the detail block below clears the latch, restoring grid scanning.
        shouldProbeDetail =
          detailGateRef.current.noteFrame(true) || detailHold || myPokemonHold;
      } else {
        // Log the DISABLED case too. The 20260720 trace had no stability record
        // at all on the two frames that produced false writes, which is
        // indistinguishable from "the frame was perfectly still" when reading
        // the log back — the one reading that would exonerate scroll motion.
        // An absent measurement must never look like a passing one.
        lastStabilityDiffRef.current = null;
        // No stability measurement means no stillness gate, and the detail probe
        // exists precisely to avoid reading a moving page — so with the scroll
        // gate off we never probe rather than probing blind.
        detailGateRef.current.noteFrame(false);
        // eslint-disable-next-line no-console
        if (DEX_SCAN_VERBOSE) console.log("[dex-scan] scroll gate disabled (frame unmeasured)", {
          frameTs: frame.ts || Date.now(),
        });
      }

      // ── DETAIL-PAGE PROBE ────────────────────────────────────────────────
      // The user has held still long enough that this frame is worth asking
      // "is this a species detail page?". The probe is the SAME call the
      // screenshot-upload path makes (ScanPokedexModal probes detail first,
      // then falls through to the grid), so a live frame and an uploaded
      // screenshot are read by identical code — a full-screen JPEG either way.
      //
      // `extractPokemonDetailPage` self-identifies via `isDetailPage`, so no
      // separate screen classifier is involved. On a miss we fall straight
      // through to the grid extractor below and nothing is lost but one probe.
      //
      // BASIC accounts probe too, and are refused at the WRITE, not at the
      // probe. Gating the probe itself made the feature invisible: a Basic user
      // parked on a species page saw nothing happen and had no way to learn the
      // capability exists — while standing in the single highest-intent moment
      // in the app, looking at the exact screen the feature reads. So we spend
      // one Vision pass per DWELL (not per frame — that is what the stillness
      // gate bought us) to name their species and tell them what Pro would do.
      // Nothing is written, and the variant grid is withheld: the upsell states
      // the capability, it does not hand over the result.
      if (shouldProbeDetail) {
        // While a caught-Pokémon page is latched, the screen cannot also be a
        // Pokédex species page (the two latches are mutually exclusive), so
        // skip that extractor's Vision pass and go straight to the My-Pokémon
        // probe below.
        const probeSpeciesDetail = !myPokemonReassertActive();
        if (probeSpeciesDetail) try {
          if (!detailModuleRef.current) {
            detailModuleRef.current = await import("@/lib/pokemonDetailDexOcr");
          }
          const detailFrame = await detailModuleRef.current.extractPokemonDetailPage(
            frame.dataUrl,
          );
          // Tell the gate whether this probe was WASTED. A detail page we could
          // not name (caught mid-swipe cross-fade) earns a retry on the next
          // frame instead of stranding the HUD on the previous species until the
          // user moves; an ordinary grid frame is an answer, not a failure, and
          // must not re-arm (that would probe every frame of a grid scan).
          detailGateRef.current.noteProbeOutcome(
            detailFrame.isDetailPage && detailFrame.dexNumber == null,
            detailFrame.isDetailPage,
          );
          // EVERY probe outcome, not just the ones that produced a read. The
          // 2026-07-23 device pass burned ~80 dwells to land 10 reads, and the
          // failures were invisible: only `detail page read` was logged, so a
          // probe that decided "not a detail page" and a probe that saw the page
          // but could not name it were indistinguishable from no probe at all —
          // which is exactly the difference between a detection bug and an OCR
          // bug. The HUD stranding on a stale species (BLASTOISE #9 while the
          // screen showed METAPOD #11) is the user-visible face of that gap.
          // eslint-disable-next-line no-console
          console.log("[dex-scan] detail probe", {
            isDetailPage: detailFrame.isDetailPage,
            dexNumber: detailFrame.dexNumber ?? null,
            name: detailFrame.name ?? null,
            confidence: detailFrame.confidence,
            variantsRead: detailFrame.variants.length,
            warnings: detailFrame.warnings,
            outcome: !detailFrame.isDetailPage
              ? "not-a-detail-page"
              : detailFrame.dexNumber == null
                ? "unnamed-retryable"
                : "read",
          });
          if (detailFrame.isDetailPage && detailFrame.dexNumber != null) {
            const { species, slots } = detailPipSlots(detailFrame);
            const isPro = isProRef.current;
            const already =
              isPro &&
              detailGateRef.current.hasApplied(
                detailFrame.dexNumber,
                detailFrame.formId,
              );
            let applied = 0;
            if (isPro && !already) {
              const res = detailModuleRef.current.applyDetailFrame(detailFrame);
              applied = res.applied;
              detailGateRef.current.noteApplied(
                detailFrame.dexNumber,
                detailFrame.formId,
              );
            }
            // eslint-disable-next-line no-console
            console.log("[dex-scan] detail page read", {
              dexNumber: detailFrame.dexNumber,
              name: detailFrame.name,
              formId: detailFrame.formId ?? null,
              variantsRead: detailFrame.variants.length,
              caughtSlots: slots.filter((s) => s.length > 0).length,
              confidence: detailFrame.confidence,
              warnings: detailFrame.warnings,
              applied,
              alreadyAppliedThisSession: already,
              gated: !isPro ? "pro_required" : null,
            });
            // A recognised species page — whichever tier. Reset the release grace
            // window: this is positive proof the user is still on a detail page,
            // so any earlier animation-blip misses no longer count toward leaving.
            detailReleaseMissesRef.current = 0;
            if (isPro) {
              // Mirror the page into the PiP: species line + the variant labels
              // in their own on-screen positions, blank where not caught.
              lastDetailSlotsRef.current = slots;
              detailHudActiveRef.current = true;
              lastDetailSpeciesRef.current = species ?? null;
              // Say plainly what came of the read and then HOLD it (the user's
              // 2026-07-29 ask). Nothing written means one of two things the user
              // cannot tell apart from the HUD: the page held nothing new
              // (`applied === 0` — every write skipped as already-in-that-state)
              // or this session already wrote it (`already`). Both are "your dex
              // is current". A write means the dex was just updated. Either way we
              // name the outcome rather than leaving "Reading species page" up,
              // which looked hung.
              lastDetailMessageRef.current =
                applied === 0 || already
                  ? DETAIL_HUD_UP_TO_DATE_MESSAGE
                  : DETAIL_HUD_UPDATED_MESSAGE;
              void setDexPipPhase({
                phase: "reading_grid",
                message: lastDetailMessageRef.current,
                // The SPECIES IS THE HEADLINE (2026-07-21). This push used to
                // set `message` but leave `detail` alone, and omitted fields are
                // STICKY (see nexPip.ts) — so the big line kept whatever the last
                // grid frame put there ("↓ XXS · Looking for: #1 #2"), which is
                // grid-scan guidance for a screen the user has left. On a species
                // page the one thing worth the headline is the species.
                detail: species ?? undefined,
                detailSpecies: species ?? undefined,
                detailVariantSlots: slots,
                heroLines: [],
                // Pokédex scan (owner direction 2026-09-23): a species page is a
                // Pokédex screen, so the HUD lies landscape like the grid, unless
                // the card is mid-way through Pokémon pages (see the helper).
                orientation: markHudOrientation(speciesPageOrientation()),
                // Species pages have no individual IVs — drop any league ball
                // a prior caught-page dwell lit.
                leagueBadge: "",
                pvpLine: "",
                evoLine: "",
              targetSpriteUrl: "",
              targetLeague: "",
              targetRank: 0,
              targetName: "",
              targetPercent: "",
              targetAura: "",
              targetLeagues: [],
              movePills: [],
ivPercent: 0,
              hudFocus: "" as const,
              moveRecs: [],
              ...showcaseHudFields(null),
              encounterCp: 0,
                // A Pro account never carries the upsell — clear it in case this
                // session started before the entitlement resolved.
                ctaText: "",
              });
            } else {
              // Basic TEASE (2026-07-29 v2): SHOW the detected variant grid — the
              // same read Pro gets — so the user sees the scan worked and exactly
              // which of this species' dexes it found, then gate at the WRITE. The
              // page is NEVER applied (no applyDetailFrame call above for Basic),
              // and the "Upgrade now" CTA states that Pro is what saves them, so
              // showing the read reads as a tease, not a bug. This reverses the
              // earlier withhold-the-grid rule at the user's explicit request.
              lastDetailSlotsRef.current = slots;
              detailHudActiveRef.current = true;
              // Latch the species so the in-between grid frames and the blur /
              // animation re-assert paths hold the tease steady instead of leaking
              // grid-scan coaching over it. buildDetailReassert() carries the same
              // grid + species and re-applies the "Upgrade now" CTA.
              lastDetailSpeciesRef.current = species ?? null;
              lastDetailMessageRef.current = DETAIL_HUD_UPSELL_MESSAGE;
              void setDexPipPhase({
                phase: "reading_grid",
                message: DETAIL_HUD_UPSELL_MESSAGE,
                // Species headline for both HUDs (iOS reads it from `detail`,
                // Android from `detailSpecies`).
                detail: species ?? undefined,
                detailSpecies: species ?? undefined,
                detailVariantSlots: slots,
                heroLines: [],
                // Pokédex scan (owner direction 2026-09-23): a species page is a
                // Pokédex screen, so the HUD lies landscape like the grid, unless
                // the card is mid-way through Pokémon pages (see the helper).
                orientation: markHudOrientation(speciesPageOrientation()),
                leagueBadge: "",
                pvpLine: "",
                evoLine: "",
              targetSpriteUrl: "",
              targetLeague: "",
              targetRank: 0,
              targetName: "",
              targetPercent: "",
              targetAura: "",
              targetLeagues: [],
              movePills: [],
ivPercent: 0,
              hudFocus: "" as const,
              moveRecs: [],
              ...showcaseHudFields(null),
              encounterCp: 0,
                // The ask, deliberately WITHOUT a gesture. A PiP overlay is a
                // rendered video layer: tapping it only reveals the system
                // control strip (play / restore / close) — it does NOT return
                // the user to the app, so "tap to…" would describe an action
                // that does not do what it claims. Returning to the app IS
                // possible via that strip's restore button, but only once the
                // restore delegate is implemented (NexPip3D's PiP delegate has
                // will/didStart and will/didStop, not
                // restoreUserInterfaceForPictureInPictureStop). Until that
                // exists this states the ask and promises no mechanism.
                ctaText: DETAIL_HUD_UPSELL_CTA,
              });
            }
            // A detail frame is not a grid frame — running the grid extractor on
            // it would read a screen it was never designed for. Stop here.
            sessionRef.current?.noteFramePresented(frame.ts || Date.now());
            return;
          }
          // A detail page we RECOGNISED but could not NAME (title band caught
          // mid-swipe cross-fade, so dexNumber came back null) is STILL not a
          // grid. Falling through to the grid extractor here is exactly the
          // corruption the user hit on Android: the species page's SEEN / CAUGHT
          // stat numbers get read as dex cell numbers under whatever variant the
          // chip row happens to resolve. The probe already re-armed for a retry
          // on the next stable frame (noteProbeOutcome above), so skip the grid
          // and wait for a clean title read rather than writing garbage.
          if (detailFrame.isDetailPage) {
            // Still a detail page (just not nameable this frame) — cancel any
            // pending release so an unnamed blip cannot leak toward leaving.
            detailReleaseMissesRef.current = 0;
            // eslint-disable-next-line no-console
            console.log("[dex-scan] detail page unnamed, grid extractor skipped", {
              confidence: detailFrame.confidence,
              warnings: detailFrame.warnings,
            });
            sessionRef.current?.noteFramePresented(frame.ts || Date.now());
            return;
          }
        } catch (err) {
          // A probe failure must never take the grid scan down with it.
          // eslint-disable-next-line no-console
          console.log("[dex-scan] detail probe failed", {
            error: err instanceof Error ? err.message : String(err),
          });
        }

        // ── MY-POKÉMON PAGE PROBE ──────────────────────────────────────────
        // The frame is not a Pokédex species page (that probe just said no, or
        // was skipped because a caught-Pokémon page is already latched). Before
        // treating it as a grid, ask whether it is an INDIVIDUAL caught-Pokémon
        // page — the screen this scan mode exists for. Same dwell, so the extra
        // Vision pass is paid once per stop, not per frame. A hit both feeds
        // the My Pokémon database and keeps the grid extractor off a screen it
        // was never designed to read.
        let encounterRead: EncounterBannerRead | null = null;
        try {
          if (!myPokemonModuleRef.current) {
            myPokemonModuleRef.current = await import("@/lib/myPokemonOcr");
          }
          const mp = await myPokemonModuleRef.current.extractMyPokemonPage(
            frame.dataUrl,
          );
          storageBoxFrame = mp.isStorageBox === true;
          storageBoxLatchRef.current = storageBoxFrame;
          encounterRead = mp.isMyPokemonPage ? null : (mp.encounter ?? null);
          // eslint-disable-next-line no-console
          console.log("[dex-scan] my-pokemon probe", {
            isMyPokemonPage: mp.isMyPokemonPage,
            encounter: mp.encounter ? `${mp.encounter.speciesName} CP ${mp.encounter.cp}` : null,
            view: mp.view,
            speciesId: mp.speciesId ?? null,
            displayName: mp.displayName ?? null,
            cp: mp.cp ?? null,
            hpMax: mp.hpMax ?? null,
            iv: mp.iv ? `${mp.iv.atk}/${mp.iv.def}/${mp.iv.hp}` : null,
            ivDiag: mp.ivDiag ?? null,
            moves: mp.moves.length,
            confidence: mp.confidence,
            warnings: mp.warnings,
          });
          if (mp.isMegaLevelPage && mp.megaLevelPage) {
            // GO's Mega Level page (2026-09-03): the level banner, "N more
            // times", the rest period and the form's energy, straight into
            // the Mega store and the wallet. No record to attach to.
            const applied = applyMegaFrame(mp);
            // eslint-disable-next-line no-console
            console.log("[dex-scan] mega level page", { ...mp.megaLevelPage, applied });
          }
          if (mp.isMyPokemonPage) {
            // Positively NOT a species page — release that latch immediately
            // instead of letting it ride out its miss grace window.
            detailReleaseMissesRef.current = 0;
            if (hasAnySlot(lastDetailSlotsRef.current) || detailHudActiveRef.current) {
              lastDetailSlotsRef.current = [];
              detailHudActiveRef.current = false;
              lastDetailSpeciesRef.current = null;
              lastDetailMessageRef.current = DETAIL_HUD_MESSAGE;
            }
            // Mark the dwell's probe as having found a detail-like page, so a
            // content change (swiping to the next Pokémon) re-arms a fresh
            // probe via noteViewChanged instead of stranding the HUD.
            detailGateRef.current.noteProbeOutcome(false, true);
            myPokemonReleaseMissesRef.current = 0;

            // Commit gates. (1) Only a frame that RESOLVED THE SPECIES may
            // write — an unmatched name is a nickname, and a record keyed on
            // nothing but a nickname's spelling would duplicate on every OCR
            // wobble; the appraisal sheet names the species even on nicknamed
            // Pokémon, so the HUD coaches opening it. (2) TWO consecutive
            // frames must agree on (name, CP, max HP) — the page probes every
            // stable frame while latched, so confirmation costs ~1s, and a
            // one-frame CP misread can no longer mint a duplicate record.
            myPokemonReadsRef.current += 1;
            let identified = mp.speciesId != null;
            // The trainer's wallet, read off their own page for free — kept
            // for future spend-aware recommendations, never displayed.
            if (mp.stardust != null || mp.candyFamilyName != null || mp.megaEnergy != null) {
              const familyId = mp.candyFamilyName
                ? myPokemonModuleRef.current.speciesIdFromDisplayName(mp.candyFamilyName)
                : null;
              recordTrainerResources({
                stardust: mp.stardust,
                candyFamilySpeciesId: familyId,
                candy: mp.candy,
                candyXl: mp.candyXl,
                // Mega Energy, for the Mega Evolutions page (2026-09-03).
                megaEnergySpeciesId: mp.megaEnergySpeciesName
                  ? myPokemonModuleRef.current.speciesIdFromDisplayName(mp.megaEnergySpeciesName)
                  : null,
                megaEnergy: mp.megaEnergy,
                megaEnergies: (mp.megaEnergies ?? []).flatMap((e) => {
                  const speciesId = myPokemonModuleRef.current!.speciesIdFromDisplayName(e.speciesName);
                  return speciesId != null ? [{ speciesId, formLabel: e.formLabel, energy: e.energy }] : [];
                }),
              });
            }
            // The Mega Level circles and the rest badge (2026-09-03): the
            // species-wide level, progress, rest and the Mega dex cell. The
            // record's own level lands through the upsert below.
            if (mp.speciesId != null && ((mp.megaReads?.length ?? 0) > 0 || mp.megaRestMs != null)) {
              const megaApplied = applyMegaFrame(mp);
              if (megaApplied.forms.length > 0) {
                // eslint-disable-next-line no-console
                console.log("[dex-scan] mega circles", { reads: mp.megaReads, restMs: mp.megaRestMs, ...megaApplied });
              }
            }
            // CP consistency gate: when the IVs, max HP and species are all
            // known, a real CP must sit on SOME level of the CP curve. A
            // sprite-occluded banner reads a truncated number consistently
            // ("CP5397" → 539 on both confirm frames, 2026-08-20 device
            // report), so cross-frame agreement can't catch it — the formula
            // can. An inconsistent CP is dropped from this observation; a
            // later clean frame supplies it.
            let mpChecked = mp;
            // APPRAISAL CONTINUITY (2026-09-28 owner report: a Chansey scanned
            // without IVs, then appraised, kept "Already in My Pokémon" while
            // its record never got the IVs). The appraisal overlay reads the
            // bars but can hide the CP and max HP, and a frame without them
            // could neither confirm against the detail frames (strict CP/HP
            // equality) nor corroborate the record in the store (IV-only
            // never joins a record without IVs): it was read-only, or it
            // minted a CP-less duplicate. The dwell's latched record IS this
            // Pokémon, so its CP / max HP / name stand in for what the overlay
            // hides, and the curve check below then judges the bars against
            // the real CP and HP instead of skipping.
            // Also without IVs (2026-09-30 Galaxy run): the HUD card itself
            // now sits over the name and HP lines of the page it reads, so a
            // named frame with the CP or max HP hidden used to fall out of
            // the two-frame agreement and repaint as "Reading your Pokémon".
            // The latched record fills what the card hides.
            if (mp.speciesId != null && myPokemonActiveRecordIdRef.current != null) {
              const latched = findRecordById(myPokemonActiveRecordIdRef.current);
              if (
                latched != null &&
                latched.speciesId === mp.speciesId &&
                (mp.cp == null || latched.cp == null || latched.cpDerived || mp.cp === latched.cp) &&
                (mp.hpMax == null || latched.hpMax == null || mp.hpMax === latched.hpMax) &&
                (mp.cp == null || mp.hpMax == null || mp.displayName == null)
              ) {
                const carriedCp = mp.cp ?? (latched.cpDerived ? null : latched.cp);
                const carriedHp = mp.hpMax ?? latched.hpMax;
                const carriedName = mp.displayName ?? latched.nickname ?? latched.speciesName ?? null;
                if (carriedCp !== mp.cp || carriedHp !== mp.hpMax || carriedName !== mp.displayName) {
                  // eslint-disable-next-line no-console
                  console.log("[dex-scan] my-pokemon appraisal carries the latched record", {
                    recordId: latched.id,
                    cp: carriedCp,
                    hpMax: carriedHp,
                    displayName: carriedName,
                  });
                  mpChecked = { ...mp, cp: carriedCp, hpMax: carriedHp, displayName: carriedName };
                }
              }
            }
            const mpIn = mpChecked;
            // An inconsistent frame supplies NOTHING (2026-09-23 Galaxy log):
            // the check cannot say whether the CP or the IVs are the misread.
            // A Gigalith read 12/13/13 on two frames 25 apart (12/13/15 on
            // the 40 between), each time with a CP the wrong triple could not
            // explain; the CP was dropped, the IVs kept, and the two wrong
            // reads agreed with each other into a second record at 84.4%
            // that the HUD then showed. Held frames neither save nor count
            // toward the two-frame agreement; a clean frame follows.
            let inconsistentFrame = false;
            if (
              mpIn.speciesId != null &&
              mpIn.cp != null &&
              mpIn.hpMax != null &&
              mpIn.iv != null
            ) {
              // The frame names the species, never the form, and an
              // alternate form has its own curve (Origin Dialga, 2026-09-22):
              // the CP is kept when it sits on ANY of the species' curves.
              const rows = baseStatsCandidatesForSpecies(mpIn.speciesId);
              if (rows.length > 0) {
                // ACCURACY (2026-09-28 sweep): a bar read one step off
                // usually has exactly one neighbour that fits the CP and HP,
                // and that neighbour is the truth (lib/ivReconcile). Only a
                // read that no neighbour explains is held.
                const rec = reconcileIvRead(
                  rows,
                  { atk: mpIn.iv.atk, def: mpIn.iv.def, hp: mpIn.iv.hp },
                  mpIn.cp,
                  mpIn.hpMax,
                );
                if (rec.status === "corrected") {
                  // eslint-disable-next-line no-console
                  console.log("[dex-scan] my-pokemon iv read corrected by CP/HP", {
                    speciesId: mpIn.speciesId,
                    cp: mpIn.cp,
                    hpMax: mpIn.hpMax,
                    read: `${mpIn.iv.atk}/${mpIn.iv.def}/${mpIn.iv.hp}`,
                    iv: `${rec.iv.atk}/${rec.iv.def}/${rec.iv.hp}`,
                    level: rec.level,
                    ivDiag: mpIn.ivDiag ?? null,
                  });
                  mpChecked = {
                    ...mpIn,
                    iv: { ...mpIn.iv, atk: rec.iv.atk, def: rec.iv.def, hp: rec.iv.hp },
                    appraisalStars: null,
                  };
                } else if (
                  rec.status === "none" &&
                  cpLooksMisread(rows, { atk: mpIn.iv.atk, def: mpIn.iv.def, hp: mpIn.iv.hp }, mpIn.cp, mpIn.hpMax)
                ) {
                  // The bars and the max HP agree with each other and the CP
                  // is nowhere near what they predict: a digit the model
                  // covered (Xerneas' antlers over "CP3478", read as 78 on
                  // every frame, 2026-10-06 Galaxy log). The CP is dropped
                  // and the bars kept; they still need three agreeing frames.
                  // eslint-disable-next-line no-console
                  console.log("[dex-scan] my-pokemon cp misread against the bars, dropped", {
                    speciesId: mpIn.speciesId,
                    cp: mpIn.cp,
                    hpMax: mpIn.hpMax,
                    iv: `${mpIn.iv.atk}/${mpIn.iv.def}/${mpIn.iv.hp}`,
                    ivDiag: mpIn.ivDiag ?? null,
                  });
                  mpChecked = { ...mpIn, cp: null };
                } else if (rec.status !== "exact") {
                  // eslint-disable-next-line no-console
                  console.log("[dex-scan] my-pokemon cp inconsistent, frame held", {
                    speciesId: mpIn.speciesId,
                    cp: mpIn.cp,
                    hpMax: mpIn.hpMax,
                    iv: `${mpIn.iv.atk}/${mpIn.iv.def}/${mpIn.iv.hp}`,
                    reconcile: rec.status,
                    candidates: rec.candidates.map((c) => `${c.atk}/${c.def}/${c.hp}`),
                    ivDiag: mpIn.ivDiag ?? null,
                  });
                  mpChecked = { ...mpIn, cp: null, iv: null };
                  inconsistentFrame = true;
                }
              }
            } else if (mpIn.speciesId != null && mpIn.cp != null && mpIn.hpMax == null && mpIn.iv != null) {
              // The appraisal overlay hides the max HP, so the curve check
              // above cannot run; but the IVs alone give the ladder of legal
              // CPs, and a truncated banner read ("CP5397" as 539) almost
              // never lands on one (2026-09-20: a mis-read CP with no HP to
              // judge it by was one way one Electrode became four records).
              const onLadder = cpOnAnyFormLadder(
                { speciesId: mpIn.speciesId, ivAtk: mpIn.iv.atk, ivDef: mpIn.iv.def, ivHp: mpIn.iv.hp },
                mpIn.cp,
              );
              if (onLadder === false) {
                // eslint-disable-next-line no-console
                console.log("[dex-scan] my-pokemon cp off the IV ladder, dropped", {
                  speciesId: mpIn.speciesId,
                  cp: mpIn.cp,
                });
                mpChecked = { ...mpIn, cp: null };
              }
            }
            // A nameless frame may ride the latched record ONLY while nothing
            // it does read contradicts that record (2026-09-04 device report:
            // after tapping out to paste a name, the NEXT Pokémon's early
            // frames were attached to the previous record, which then kept
            // its name, lost its sprite and never copied). A different
            // species, CP or max HP is a different Pokémon: drop the latch.
            // A contradicting frame does not RIDE the latch; only a different
            // species DROPS it (2026-09-30 Galaxy run). A CP or max HP the
            // HUD card truncated ("CP 308" for 3083) used to null the latch,
            // and with it every merge and hold for the rest of the dwell: the
            // card demoted to "Reading your Pokémon" and back on every frame.
            const latchContradicts = (fr: typeof mpChecked): boolean => {
              const id = myPokemonActiveRecordIdRef.current;
              if (id == null) return false;
              const latched = findRecordById(id);
              if (!latched) {
                myPokemonActiveRecordIdRef.current = null;
                return true;
              }
              const speciesDiffers =
                fr.speciesId != null && latched.speciesId != null && fr.speciesId !== latched.speciesId;
              if (speciesDiffers) {
                myPokemonActiveRecordIdRef.current = null;
                return true;
              }
              return (
                (fr.cp != null && latched.cp != null && fr.cp !== latched.cp) ||
                (fr.hpMax != null && latched.hpMax != null && fr.hpMax !== latched.hpMax)
              );
            };
            // NAME CARRY (2026-09-30 Galaxy, Ho-Oh): once the card opens it
            // covers the page's name, so every later frame is nameless and
            // the two-frame agreement never completed: "Reading your
            // Pokémon" for the whole dwell, no record, no Shadow flag. A
            // nameless frame whose CP AND max HP both read and equal the
            // last named sighting's on this dwell IS that Pokémon: it
            // inherits the species and name and can confirm.
            {
              const hudLatch = myPokemonHudLatchRef.current;
              if (
                mpChecked.speciesId == null &&
                mpChecked.displayName == null &&
                hudLatch.speciesId != null &&
                hudLatch.key !== "?" &&
                mpChecked.cp != null &&
                mpChecked.hpMax != null &&
                mpChecked.cp === hudLatch.cp &&
                mpChecked.hpMax === hudLatch.hpMax
              ) {
                mpChecked = { ...mpChecked, speciesId: hudLatch.speciesId, displayName: hudLatch.displayName };
                identified = true;
                // eslint-disable-next-line no-console
                console.log("[dex-scan] my-pokemon nameless frame carries the last name", {
                  speciesId: hudLatch.speciesId,
                  displayName: hudLatch.displayName,
                  cp: mpChecked.cp,
                  hpMax: mpChecked.hpMax,
                });
              }
            }
            // Species-keyed when the name is the species name (with OCR
            // debris allowed), letters-and-digits only otherwise: see
            // lib/myPokemonSightingKey for the Xerneas dwell this fixes.
            const sightingKey = myPokemonSightingKey(mpChecked.displayName, mpChecked.speciesId);
            const prevSighting = lastMyPokemonSightingRef.current;
            // A NEW named identity ends dwell continuity with the old record,
            // when it resolves to a different species (2026-09-30): a name the
            // HUD card half covers ("GIGALI"), an OCR variant, or the "#526"
            // key an appraisal sheet reads is still this Pokémon.
            if (
              prevSighting != null &&
              sightingKey !== "?" &&
              prevSighting.key !== "?" &&
              prevSighting.key !== sightingKey &&
              myPokemonActiveRecordIdRef.current != null
            ) {
              const latchedSpecies = findRecordById(myPokemonActiveRecordIdRef.current)?.speciesId ?? null;
              if (mpChecked.speciesId == null || latchedSpecies == null || mpChecked.speciesId !== latchedSpecies) {
                myPokemonActiveRecordIdRef.current = null;
              }
            }
            // IVs are part of the agreement too (2026-09-03 device video: a
            // Fletchling read 4/4/15 one frame and 4/4/14 the next, and each
            // frame committed, so the record, the verdict and the target
            // flip-flopped in front of the trainer). Two frames that both
            // read the bars must read the same triple; a frame without the
            // bars (the overlay closed) neither agrees nor disagrees.
            const ivKey = mpChecked.iv
              ? `${mpChecked.iv.atk}/${mpChecked.iv.def}/${mpChecked.iv.hp}`
              : null;
            // IV AGREEMENT IS THREE FRAMES (2026-09-30 Galaxy, Giratina): the
            // appraisal bars animate and the HP bar reads unsteadily, so two
            // frames agreed on 12/10/15 and the record was created before the
            // next read said 12/10/12. A triple must hold on three consecutive
            // reads before it is written or shown; CP and max HP keep the
            // two-frame rule.
            const ivAgreeCount =
              ivKey == null
                ? 0
                : prevSighting != null && prevSighting.key === sightingKey && prevSighting.iv === ivKey
                  ? (prevSighting.ivAgree ?? 1) + 1
                  : 1;
            const ivConfirmed = ivKey == null || ivAgreeCount >= 3;
            // What this dwell has seen but is not writing (2026-10-07 owner
            // report: two same-species Pokémon with one CP became one record).
            // Carried to the store as vetoes only: the bars read before they
            // settle (this frame's, else the chain's last read), and the max
            // HP seen before the overlay hid it.
            const sameDwell =
              prevSighting != null && prevSighting.key === sightingKey && prevSighting.cp === mpChecked.cp;
            const chainIv = sameDwell && prevSighting.iv ? prevSighting.iv.split("/").map(Number) : null;
            const ivVeto =
              mpChecked.iv != null
                ? { atk: mpChecked.iv.atk, def: mpChecked.iv.def, hp: mpChecked.iv.hp }
                : chainIv && chainIv.length === 3 && chainIv.every((n) => Number.isFinite(n))
                  ? { atk: chainIv[0], def: chainIv[1], hp: chainIv[2] }
                  : null;
            const hpSeen = mpChecked.hpMax ?? (sameDwell ? prevSighting.hpSeen : null);
            const hpVeto = mpChecked.hpMax == null ? hpSeen : null;
            const confirmed =
              prevSighting != null &&
              prevSighting.key === sightingKey &&
              prevSighting.cp === mpChecked.cp &&
              prevSighting.hpMax === mpChecked.hpMax &&
              (prevSighting.iv == null || ivKey == null || prevSighting.iv === ivKey) &&
              ivConfirmed;
            // An unconfirmed triple is not shown either (owner: "let's not
            // give users incorrect information"): the frame the HUD renders
            // carries no IVs until the third agreeing read, and the card says
            // "Appraise it" until then, as it does with no bars at all.
            if (!ivConfirmed && mpChecked.iv != null) {
              mpChecked = { ...mpChecked, iv: null };
            }
            // SHINY NEEDS TWO FRAMES (2026-09-28): one frame's palette vote
            // can be a leak (a cream egg, a warm backdrop); the store never
            // turns the flag off and it ticks the Shiny dex, so a lone read
            // is treated as not shiny until the next frame agrees.
            const shinyRead = mpChecked.isShiny;
            const shinyAgreed =
              shinyRead && prevSighting != null && prevSighting.key === sightingKey && prevSighting.shiny;
            if (shinyRead && !shinyAgreed) mpChecked = { ...mpChecked, isShiny: false };
            // A "?" frame (no name read: scrolled off, or under the HUD card)
            // is not a sighting; it used to overwrite the chain so two named
            // frames separated by one covered frame never agreed (2026-09-30).
            if (!inconsistentFrame && sightingKey !== "?") {
              lastMyPokemonSightingRef.current = {
                key: sightingKey,
                cp: mpChecked.cp,
                hpMax: mpChecked.hpMax,
                iv: ivKey,
                ivAgree: ivAgreeCount,
                shiny: shinyRead,
                hpSeen,
              };
            }
            let saved: "created" | "updated" | "none" = "none";
            let hasIvs = mpChecked.iv != null;
            let hudRecord: MyPokemonRecord | null = null;
            if (
              identified &&
              confirmed &&
              !inconsistentFrame &&
              (mpChecked.cp != null || mpChecked.hpMax != null || mpChecked.iv != null)
            ) {
              // How it was added, for the admin's "scan vs upload" question
              // (2026-09-16). Only a CREATE reads it.
              const res = upsertScannedPokemon({ ...mpChecked, addedVia: "live_scan", ivVeto, hpVeto });
              if (res.changed) {
                saved = res.created ? "created" : "updated";
                myPokemonSavesRef.current += 1;
              }
              // New records land in the scan-history session as Pokémon rows
              // (created only; merges are updates to a pre-scan record).
              // In-memory append, off the hot path.
              if (res.created && res.record.speciesId != null) {
                recordMyPokemonScanRow({
                  myPokemonId: res.record.id,
                  speciesId: res.record.speciesId,
                  formId: res.record.formId,
                });
              }
              hasIvs = res.record.ivAtk != null;
              hudRecord = res.record;
              myPokemonActiveRecordIdRef.current = res.record.id;
              myPokemonCommittedKeysRef.current.add(sightingKey);
            } else if (identified) {
              // Unconfirmed frame: still enrich the HUD from the stored record
              // (read-only), so an appraisal re-visit shows CP/moves it
              // already knows rather than blanks. "Already in My Pokémon"
              // must speak for the RECORD (2026-09-28): this used to keep the
              // frame's own IV read as hasIvs, so a card said "Already" over
              // IVs the record never received.
              hasIvs = false;
              hudRecord = findMatchingRecord(mpChecked);
              // A record that matches this frame IS this Pokémon: latch it
              // (2026-09-30). Only a commit used to set the latch, so a
              // Pokémon already in the store showed "Already in My Pokémon"
              // with no buttons, no merges and no hold for the whole dwell.
              if (hudRecord != null && myPokemonActiveRecordIdRef.current == null) {
                myPokemonActiveRecordIdRef.current = hudRecord.id;
              }
              // A frame whose IVs DISAGREE with the record finds no match (the
              // store treats a different triple as a different Pokémon). While
              // the dwell latch still points at this species, keep the HUD on
              // the latched record rather than repainting the one-frame read;
              // if the new triple is real, the next agreeing frame commits it.
              // The hold needs the CP to agree too (or be unread): the NEXT
              // Pokémon can share this one's name with different IVs, and its
              // different CP is what says so. It then shows its own frame read
              // until its second agreeing frame commits it.
              if (
                hudRecord == null &&
                myPokemonActiveRecordIdRef.current != null
              ) {
                const latched = findRecordById(myPokemonActiveRecordIdRef.current);
                if (
                  latched != null &&
                  latched.speciesId === mpChecked.speciesId &&
                  (mpChecked.cp == null || latched.cp == null || mpChecked.cp === latched.cp)
                ) {
                  hudRecord = latched;
                }
              }
              if (hudRecord?.ivAtk != null) hasIvs = true;
            } else if (
              mpChecked.displayName == null &&
              myPokemonActiveRecordIdRef.current != null &&
              !latchContradicts(mpChecked) &&
              mpChecked.moves.length === 0 &&
              mpChecked.provenance.length === 0 &&
              mpChecked.iv == null
            ) {
              // The name and CP scrolled off and this frame carries nothing to
              // merge (an HP line at most). The dwell is still on the latched
              // record, so the HUD keeps showing it instead of collapsing to a
              // nameless "Reading your Pokémon" panel and back twice a second
              // (2026-09-03 device video, Metang).
              hudRecord = findRecordById(myPokemonActiveRecordIdRef.current);
              if (hudRecord?.ivAtk != null) hasIvs = true;
            } else if (
              mpChecked.displayName == null &&
              myPokemonActiveRecordIdRef.current != null &&
              !latchContradicts(mpChecked) &&
              (mpChecked.moves.length > 0 ||
                mpChecked.provenance.length > 0 ||
                mpChecked.iv != null ||
                mpChecked.maxMoves != null)
            ) {
              // DWELL CONTINUITY (the Zamazenta fix): a tall page scrolls the
              // name and CP off-screen before the moveset scrolls on, so this
              // frame has payload but no identity. The latch knows which
              // record this dwell committed. Attach the payload to it.
              const res = mergeIntoRecordById(
                myPokemonActiveRecordIdRef.current,
                mpChecked,
              );
              if (res) {
                hudRecord = res.record;
                hasIvs = res.record.ivAtk != null;
                if (res.changed) {
                  saved = "updated";
                  myPokemonSavesRef.current += 1;
                }
              }
            }
            // A nameless frame that found no record while a card is up holds
            // the card (2026-09-30 Galaxy run): the open card covers the
            // page's name and HP lines, so half the frames on a latched
            // Pokémon read only HP and moves. Those used to repaint the card
            // as a "Reading your Pokémon" facts panel and back, twice a
            // second. The release grace below still ends a real navigation.
            if (
              hudRecord == null &&
              mpChecked.displayName == null &&
              mpChecked.speciesId == null &&
              myPokemonHudActiveRef.current &&
              lastMyPokemonHudRecordIdRef.current != null &&
              (mpChecked.moves.length > 0 ||
                mpChecked.provenance.length > 0 ||
                mpChecked.iv != null ||
                mpChecked.maxMoves != null)
            ) {
              // The payload belongs to the Pokémon on the card (2026-09-30
              // Galaxy run: "the moves added onto the HUD don't show"). The
              // card's record is the merge target when nothing this frame
              // read contradicts it; the latch is not required.
              const shown = findRecordById(lastMyPokemonHudRecordIdRef.current);
              const contradicts =
                shown == null ||
                (mpChecked.cp != null && shown.cp != null && !shown.cpDerived && mpChecked.cp !== shown.cp) ||
                (mpChecked.hpMax != null && shown.hpMax != null && mpChecked.hpMax !== shown.hpMax);
              if (!contradicts) {
                const res = mergeIntoRecordById(lastMyPokemonHudRecordIdRef.current, mpChecked);
                if (res) {
                  hudRecord = res.record;
                  hasIvs = res.record.ivAtk != null;
                  if (res.changed) {
                    saved = "updated";
                    myPokemonSavesRef.current += 1;
                  }
                  // eslint-disable-next-line no-console
                  console.log("[dex-scan] my-pokemon payload merged into the shown record", {
                    recordId: res.record.id,
                    moves: mpChecked.moves.length,
                    changed: res.changed,
                  });
                }
              }
            }
            if (
              hudRecord == null &&
              mpChecked.displayName == null &&
              mpChecked.speciesId == null &&
              myPokemonHudActiveRef.current &&
              lastMyPokemonHeadlineRef.current != null
            ) {
              // eslint-disable-next-line no-console
              console.log("[dex-scan] my-pokemon partial frame holds the card", {
                headline: lastMyPokemonHeadlineRef.current,
                cp: mpChecked.cp ?? null,
                hpMax: mpChecked.hpMax ?? null,
                moves: mpChecked.moves.length,
              });
              myPokemonLastSeenAtRef.current = Date.now();
              void setDexPipPhase(buildMyPokemonReassert());
              sessionRef.current?.noteFramePresented(frame.ts || Date.now());
              return;
            }
            const committedBefore = myPokemonCommittedKeysRef.current.has(sightingKey);
            // Derived stats (level from CP+HP+IVs, IV%, PvP league percents),
            // the Poke-Genie-class readout the HUD exists for.
            // The HUD frame: this frame's read with CP / max HP filled from the
            // sighting latch when this frame lacks them (see the ref). A "?"
            // key is a frame whose name scrolled off, not a new Pokémon, so it
            // keeps the latch it has.
            const latch = myPokemonHudLatchRef.current;
            if (sightingKey !== "?" && latch.key !== sightingKey) {
              latch.key = sightingKey;
              latch.cp = null;
              latch.hpMax = null;
              latch.speciesId = null;
              latch.displayName = null;
            }
            if (mpChecked.cp != null) latch.cp = mpChecked.cp;
            if (mpChecked.hpMax != null) latch.hpMax = mpChecked.hpMax;
            if (mpChecked.speciesId != null) {
              latch.speciesId = mpChecked.speciesId;
              latch.displayName = mpChecked.displayName ?? latch.displayName;
            }
            const hudFrame = {
              ...mpChecked,
              cp: mpChecked.cp ?? latch.cp,
              hpMax: mpChecked.hpMax ?? latch.hpMax,
            };
            // THE EXPECTED CP (2026-09-20 owner ask): when no side read the
            // CP banner but the max HP and IVs are known, the maths names
            // the CP (exact for most wild catches; a tilde on the HUD when
            // the HP fits two half-levels). The record stores it the same
            // way (fillDerivedCp in the store), so the HUD and the record
            // agree.
            const hudSpeciesId = hudRecord?.speciesId ?? hudFrame.speciesId;
            const hudIv = {
              ivAtk: hudRecord?.ivAtk ?? hudFrame.iv?.atk ?? null,
              ivDef: hudRecord?.ivDef ?? hudFrame.iv?.def ?? null,
              ivHp: hudRecord?.ivHp ?? hudFrame.iv?.hp ?? null,
            };
            const hudHpMax = hudRecord?.hpMax ?? hudFrame.hpMax;
            const readCp = hudRecord != null && hudRecord.cp != null && !hudRecord.cpDerived ? hudRecord.cp : hudFrame.cp;
            const expectedCp =
              readCp == null
                ? cpFromHp({
                    speciesId: hudSpeciesId,
                    formId: hudRecord?.formId ?? null,
                    gender: hudRecord?.gender ?? null,
                    hpMax: hudHpMax,
                    ...hudIv,
                  })
                : null;
            const derived = deriveMyPokemon({
              speciesId: hudSpeciesId,
              cp: readCp ?? expectedCp?.cp ?? hudRecord?.cp ?? null,
              hpMax: hudHpMax,
              ...hudIv,
              formId: hudRecord?.formId ?? null,
              gender: hudRecord?.gender ?? null,
            });
            const { headline, slots, leagueBadge, pvpLine, evoLine, target, heroLines, movePills, movePillColors, moveRecs, hudFocus, ivPercent } =
              myPokemonPipSlots(myPokemonHudFacts(hudFrame, hudRecord, derived, expectedCp));
            // A top-5 showcase standing (2026-09-22): ranked against every
            // scanned record, cached per record + box size.
            let showcase: ShowcaseHudPick | null = null;
            if (hudRecord != null) {
              const all = listMyPokemon();
              const key = `${hudRecord.id}:${all.length}:${hudRecord.heightM ?? ""}:${hudRecord.weightKg ?? ""}`;
              if (showcaseCacheRef.current?.key !== key) {
                showcaseCacheRef.current = { key, pick: showcaseHudPick(showcaseStandingFor(all, hudRecord)) };
              }
              showcase = showcaseCacheRef.current.pick;
            }
            myPokemonHudActiveRef.current = true;
            myPokemonLastSeenAtRef.current = Date.now();
            if (hudRecord != null) lastMyPokemonHudRecordIdRef.current = hudRecord.id;
            lastMyPokemonHeadlineRef.current = headline ?? "My Pokémon";
            lastMyPokemonSlotsRef.current = slots;
            lastMyPokemonLeagueBadgeRef.current = leagueBadge;
            lastMyPokemonPvpLineRef.current = pvpLine;
            lastMyPokemonEvoLineRef.current = evoLine;
            lastMyPokemonTargetRef.current = target;
            lastMyPokemonHeroLinesRef.current = heroLines;
            lastMyPokemonMovePillsRef.current = movePills;
            lastMyPokemonMovePillColorsRef.current = movePillColors;
            lastMyPokemonIvPercentRef.current = ivPercent;
            lastMyPokemonFocusRef.current = hudFocus;
            lastMyPokemonMoveRecsRef.current = moveRecs;
            lastMyPokemonShowcaseRef.current = showcase;
            lastMyPokemonMessageRef.current =
              saved === "created"
                ? hasIvs
                  ? MYPOKE_HUD_SAVED_MESSAGE
                  : MYPOKE_HUD_SAVED_NEED_APPRAISAL_MESSAGE
                : saved === "updated"
                  ? MYPOKE_HUD_UPDATED_MESSAGE
                  : !identified
                    ? mpChecked.displayName != null
                      ? MYPOKE_HUD_NEED_SPECIES_MESSAGE
                      : MYPOKE_HUD_MESSAGE
                    : committedBefore
                      ? hasIvs
                        ? MYPOKE_HUD_UP_TO_DATE_MESSAGE
                        : MYPOKE_HUD_NEED_APPRAISAL_MESSAGE
                      : MYPOKE_HUD_MESSAGE;
            lastMyPokemonLatchAtRef.current = Date.now();
            void setDexPipPhase({
              phase: "reading_grid",
              message: lastMyPokemonMessageRef.current,
              detail: lastMyPokemonHeadlineRef.current ?? undefined,
              detailSpecies: lastMyPokemonHeadlineRef.current ?? undefined,
              detailVariantSlots: slots,
              heroLines,
              // Pokémon scan: the HUD stands as a narrow column top-right, clear of the CP.
              orientation: markHudOrientation("portrait"),
              leagueBadge: leagueBadge ?? "",
              pvpLine: pvpLine ?? "",
              evoLine: evoLine ?? "",
              // The target visual (art + ball + oversized rank, drawn
              // natively); cleared alongside the hero lines when absent.
              targetSpriteUrl: target?.spriteUrl ?? "",
              targetLeague: target?.league ?? "",
              targetRank: target?.rank ?? 0,
              targetName: target?.name ?? "",
              targetPercent: target?.percentText ?? "",
              targetAura: target?.aura ?? ("" as const),
              // The card's buttons (Android, 2026-09-29): which record, starred, listed.
              ...cardRecordFields(),
              targetLeagues: target?.leagues ?? [],
              movePills,
              movePillColors,
              ivPercent: ivPercent ?? 0,
              hudFocus,
              moveRecs,
              ...showcaseHudFields(showcase),
              encounterCp: 0,
              ctaText: "",
            });
            // COPY THE NAME ONCE (2026-09-03 owner ask): the moment a Pokémon's
            // facts are all read, its name in the trainer's template goes on
            // the clipboard, so they can leave the appraisal and paste it in
            // on the spot. Gated on the record (not the frame) having IVs, on
            // the template rendering with nothing missing, and on this
            // sighting not having been copied before. A naming session owns
            // the clipboard while it runs, so it is left alone.
            // Keyed by the RECORD, not the frame's name: the same Pokémon can
            // read back under slightly different names frame to frame, and
            // each new key was another copy (and another "Copied" toast).
            // Keyed by record AND rendered name (2026-09-04): a second Pokémon
            // that lands in the same record (a same-species neighbour whose CP
            // the appraisal seal hid) renders a different name, and that name
            // must reach the clipboard too. Frame-to-frame jitter of the same
            // Pokémon renders the same name, so it still copies once.
            if (
              hudRecord != null &&
              hudRecord.ivAtk != null &&
              getDexScanSettings().copyNicknameOnScan &&
              !getNamingSessionState().active
            ) {
              const rendered = renderPreferredNickname(hudRecord, derived, {
                isPro: isProRef.current,
              });
              const copyKey = `${hudRecord.id}:${rendered.name.trim()}`;
              if (
                rendered.missing.length === 0 &&
                rendered.name.trim() !== "" &&
                !myPokemonCopiedKeysRef.current.has(copyKey)
              ) {
                myPokemonCopiedKeysRef.current.add(copyKey);
                void copyNameToClipboard(rendered.name).then((ok) => {
                  // eslint-disable-next-line no-console
                  console.log("[dex-scan] my-pokemon name copied", {
                    name: rendered.name,
                    ok,
                  });
                });
              }
            }
            // eslint-disable-next-line no-console
            console.log("[dex-scan] my-pokemon page read", {
              speciesId: mp.speciesId ?? null,
              headline: lastMyPokemonHeadlineRef.current,
              saved,
              confirmed,
              hasIvs,
              sightingKey,
              recordId: hudRecord?.id ?? null,
              cp: mpChecked.cp ?? null,
              hpMax: mpChecked.hpMax ?? null,
              iv: ivKey,
              shiny: shinyRead ? (shinyAgreed ? "agreed" : "pending") : null,
              stars: mp.appraisalStars ?? null,
              ivDiag: mp.ivDiag ?? null,
              warnings: mp.warnings,
            });
            // A caught-Pokémon page is not a grid — never run the grid
            // extractor on it.
            sessionRef.current?.noteFramePresented(frame.ts || Date.now());
            return;
          }
          // Not a caught-Pokémon page this probe. The live page animates like
          // the species page does, so hold a latched HUD across a short run of
          // misses; a sustained run (a real navigation away) releases it.
          if (myPokemonReassertActive()) {
            // An EMPTY read is not a different screen (2026-09-30 Galaxy log:
            // the Arcanine card was released after 12 probes that read
            // nothing at all, confidence 0, on the page it was still
            // showing). Only a probe that read something counts as a miss;
            // the stale window below still bounds an empty run.
            const emptyRead =
              !mp.isMyPokemonPage &&
              mp.isStorageBox !== true &&
              encounterRead == null &&
              mp.speciesId == null &&
              mp.cp == null &&
              mp.hpMax == null &&
              mp.moves.length === 0 &&
              !((mp.confidence ?? 0) > 0);
            if (emptyRead && Date.now() - myPokemonLastSeenAtRef.current < MYPOKE_RELEASE_STALE_MS) {
              void setDexPipPhase(buildMyPokemonReassert());
              sessionRef.current?.noteFramePresented(frame.ts || Date.now());
              return;
            }
            myPokemonReleaseMissesRef.current += 1;
            // A latched (committed) Pokémon holds much longer: scrolling down
            // to its moves hides everything the probe keys on. A recognised
            // Pokédex grid on the previous frame means the trainer has left,
            // so that releases at once.
            const grace =
              myPokemonActiveRecordIdRef.current != null
                ? MYPOKE_RELEASE_MISS_FRAMES
                : DETAIL_RELEASE_MISS_FRAMES;
            const onGrid = Date.now() - lastRecognisedGridAtRef.current < 2000;
            // The storage box (a grid of CP labels) is the other screen the
            // trainer leaves to; it clears the card at once (2026-09-03).
            const onBox = mp.isStorageBox === true;
            const staleMs = Date.now() - myPokemonLastSeenAtRef.current;
            // A wild-encounter banner is a different screen: it releases the
            // card at once and takes the HUD below (2026-09-30: encounters
            // right after a Pokémon page were held back for the whole grace).
            if (!onGrid && !onBox && encounterRead == null && myPokemonReleaseMissesRef.current < grace && staleMs < MYPOKE_RELEASE_STALE_MS) {
              void setDexPipPhase(buildMyPokemonReassert());
              sessionRef.current?.noteFramePresented(frame.ts || Date.now());
              return;
            }
            // eslint-disable-next-line no-console
            console.log("[dex-scan] my-pokemon card released", {
              misses: myPokemonReleaseMissesRef.current,
              grace,
              staleMs,
              onGrid,
              onBox,
              encounter: encounterRead != null,
              view: mp.view ?? null,
            });
            myPokemonReleaseMissesRef.current = 0;
            myPokemonHudActiveRef.current = false;
            lastMyPokemonHudRecordIdRef.current = null;
            lastMyPokemonHeadlineRef.current = null;
            lastMyPokemonSlotsRef.current = [];
            lastMyPokemonLeagueBadgeRef.current = null;
            lastMyPokemonPvpLineRef.current = null;
            lastMyPokemonEvoLineRef.current = null;
            lastMyPokemonTargetRef.current = null;
            lastMyPokemonHeroLinesRef.current = [];
            // The pills and the CP/HP latch go with the card (2026-09-04): the
            // next Pokémon's first reassert used to carry the previous one's
            // moves and CP.
            lastMyPokemonMovePillsRef.current = [];
            lastMyPokemonMovePillColorsRef.current = [];
            myPokemonHudLatchRef.current = { key: "?", cp: null, hpMax: null, speciesId: null, displayName: null };
            lastMyPokemonMessageRef.current = MYPOKE_HUD_MESSAGE;
            // The confirmation chain is broken by leaving the page — the next
            // Pokémon must earn its own two agreeing frames.
            lastMyPokemonSightingRef.current = null;
            myPokemonActiveRecordIdRef.current = null;
            void setDexPipPhase({
              phase: "reading_grid",
              // "" CLEARS detail mode natively; undefined would leave it, and
              // the Android tab/badge key off detailSpecies (2026-09-25: the
              // destiny badge lingered over the Pokédex region list).
              detailSpecies: "",
              detailVariantSlots: [],
              // The card keeps its shape here (2026-09-18): leaving a Pokémon's
              // page is not the grid. Landscape returns when a grid is read.
              heroLines: [],
              leagueBadge: "",
              pvpLine: "",
              evoLine: "",
              targetSpriteUrl: "",
              targetLeague: "",
              targetRank: 0,
              targetName: "",
              targetPercent: "",
              targetAura: "",
              targetLeagues: [],
              movePills: [],
ivPercent: 0,
              hudFocus: "" as const,
              moveRecs: [],
              ...showcaseHudFields(null),
              encounterCp: 0,
              ctaText: "",
            });
          }
        } catch (err) {
          // A probe failure must never take the grid scan down with it.
          // eslint-disable-next-line no-console
          console.log("[dex-scan] my-pokemon probe failed", {
            error: err instanceof Error ? err.message : String(err),
          });
        }
        // ── WILD ENCOUNTER (2026-09-22) ────────────────────────────────────
        // The banner over a wild Pokémon names it and prints its CP, and the
        // CP alone pins the IV percent to a range (encounterIvRange). Not a
        // grid, not a Pokémon page: the HUD shows the range and the frame
        // ends here. Every Pokémon-page field is cleared with it so the
        // destiny badge never stands over an encounter.
        if (encounterRead) {
          const range = encounterIvRangeFor(encounterRead.speciesId, encounterRead.cp);
          if (range) {
            myPokemonHudActiveRef.current = false;
            lastMyPokemonHeadlineRef.current = null;
            myPokemonReleaseMissesRef.current = 0;
            detailHudActiveRef.current = false;
            lastDetailSpeciesRef.current = null;
            detailReleaseMissesRef.current = 0;
            void setDexPipPhase({
              phase: "reading_grid",
              message: `${encounterRead.speciesName} · CP ${encounterRead.cp}`,
              detail: undefined,
              bubble: "green",
              hideRecent: true,
              detailSpecies: undefined,
              detailVariantSlots: [],
              heroLines: [],
              leagueBadge: "",
              pvpLine: "",
              evoLine: "",
              targetSpriteUrl: "",
              targetLeague: "",
              targetRank: 0,
              targetName: "",
              targetPercent: "",
              targetAura: "",
              targetLeagues: [],
              movePills: [],
              ivPercent: 0,
              hudFocus: "",
              moveRecs: [],
              ...showcaseHudFields(null),
              encounterName: encounterRead.speciesName,
              encounterCp: encounterRead.cp,
              encounterIvMin: range.minPercent,
              encounterIvMax: range.maxPercent,
              encounterLevelMin: range.levelMin,
              encounterLevelMax: range.levelMax,
              encounterSpreads: range.combos,
              ctaText: "",
            });
            // eslint-disable-next-line no-console
            console.log("[dex-scan] wild encounter", {
              species: encounterRead.speciesName,
              cp: encounterRead.cp,
              iv: `${range.minPercent}-${range.maxPercent}%`,
              levels: `${range.levelMin}-${range.levelMax}`,
              spreads: range.combos,
            });
          } else {
            // eslint-disable-next-line no-console
            console.log("[dex-scan] wild encounter without a range", {
              species: encounterRead.speciesName,
              speciesId: encounterRead.speciesId,
              cp: encounterRead.cp,
            });
          }
          // A recognised encounter banner ENDS the frame whether or not a
          // range came back (owner question 2026-09-22: "is the Dex updating
          // from a wild CP / IV estimate?" It never did: this branch only
          // pushes the HUD. But a species without base stats, or a CP that
          // fits no spread, used to fall through to the grid extractor, and
          // an encounter screen is never a Pokédex grid.)
          detailGateRef.current.noteFrame(false);
          sessionRef.current?.noteFramePresented(frame.ts || Date.now());
          return;
        }

        if (forcedMyPokemonProbe) {
          // This frame came through the stability gate for the My Pokémon
          // probe only. It was not a caught-Pokémon page (that path returned
          // above), and it is still an unstable frame, so it ends here rather
          // than reaching the species-detail release or the grid extractor.
          detailGateRef.current.noteFrame(false);
          sessionRef.current?.noteFramePresented(frame.ts || Date.now());
          return;
        }

        // Species-detail latch grace/release. Runs AFTER the My-Pokémon probe
        // so a dex-page → storage-page navigation latches the new page instead
        // of idling through this grace window first. On Android the live detail
        // page animates and re-encodes, so an occasional probe frame caught
        // mid-animation reads "not a detail page" even though the user has not
        // left. Releasing the latch on that single miss is exactly the churn the
        // user reported (species → grid coaching → species). So while a page is
        // latched, hold it across a short run of misses and RE-ASSERT what detail
        // mode last showed; only a sustained run (a real navigation away, which
        // lands on many grid frames) releases it.
        if (detailReassertActive()) {
          detailReleaseMissesRef.current += 1;
          if (detailReleaseMissesRef.current < DETAIL_RELEASE_MISS_FRAMES) {
            void setDexPipPhase(buildDetailReassert());
            sessionRef.current?.noteFramePresented(frame.ts || Date.now());
            return;
          }
        }
        // Release: clear whatever the detail path last put on the HUD — the Pro
        // variant grid OR the Basic upsell — so a grid scan is never headed by a
        // stale species name or left asking a Basic user to upgrade for a screen
        // they have already navigated away from.
        detailReleaseMissesRef.current = 0;
        if (hasAnySlot(lastDetailSlotsRef.current) || detailHudActiveRef.current) {
          lastDetailSlotsRef.current = [];
          detailHudActiveRef.current = false;
          lastDetailSpeciesRef.current = null;
          lastDetailMessageRef.current = DETAIL_HUD_MESSAGE;
          void setDexPipPhase({
            phase: "reading_grid",
            detailSpecies: undefined,
            detailVariantSlots: [],
            // The card keeps its shape here (2026-09-18): see the My Pokémon
            // release above. Landscape returns when a grid is read.
            heroLines: [],
            leagueBadge: "",
            pvpLine: "",
            evoLine: "",
              targetSpriteUrl: "",
              targetLeague: "",
              targetRank: 0,
              targetName: "",
              targetPercent: "",
              targetAura: "",
              targetLeagues: [],
              movePills: [],
ivPercent: 0,
              hudFocus: "" as const,
              moveRecs: [],
              ...showcaseHudFields(null),
              encounterCp: 0,
            ctaText: "",
          });
        }
      }

      // STORAGE BOX: stay idle. The my-Pokémon and species latches above have
      // already released on this frame; nothing here is scannable until the
      // trainer opens a Pokédex page, a species page or a Pokémon's page.
      if (storageBoxFrame || storageBoxLatchRef.current) {
        void setDexPipPhase({
          phase: "reading_grid",
          message: STORAGE_BOX_HUD_MESSAGE,
          // No `detail`: that slot is the big headline (2026-09-18 device pass).
          detail: undefined,
          bubble: undefined,
        });
        sessionRef.current?.noteFramePresented(frame.ts || Date.now());
        return;
      }

      // Vision-only trial: the pixel-only pre-OCR palette gate (dexPreOcrGate)
      // and its SEARCHING/SCANNING hysteresis + page-identity classify cache +
      // colour-family transition tracker were REMOVED. The gate was primarily an
      // OCR-cost-saving heuristic for the retired Tesseract engine; Apple Vision
      // has enough headroom to OCR every frame. The frame loop now processes
      // every frame that clears the blur + scroll gates directly via
      // extractPokedexFrame — the POST-OCR frame gate (dexFrameGate.ts) still
      // filters low-confidence / non-dex results downstream.

      if (!ocrModuleRef.current) {
        ocrModuleRef.current = await import("@/lib/pokedexOcr");
      }
      // b17o: pass the app-state variant into the per-cell classifier so it runs
      // the correct sprite test (hue-variance silhouette for Main/Shiny/Mega/
      // G-Max/Shadow/Purified, empty-vs-full only for Lucky/Perfect/XXL/XXS).
      // This is the SCANNED variant from app state — the same source of truth
      // resolveTrustedVariant uses below; the hardened chip-text override (b17n,
      // CHIP_TEXT_AUTHORITY_THRESHOLD) still decides ROUTING afterwards.
      // scan-speed 2026-06-30 — time the OCR lane (the 5x Tesseract fanout in
      // extractPokedexFrame). This is the dominant per-frame compute cost; the
      // session summary's ocrMeanMs/ocrP95Ms isolate it from the rest of the
      // frame so we can tell slow OCR apart from too-tight gating.
      const ocrStartMs =
        typeof performance !== "undefined" ? performance.now() : Date.now();
      const gridFrame = await ocrModuleRef.current.extractPokedexFrame(frame.dataUrl, {
        pipActive: pipActiveRef.current,
        variant: scanVariantInput(),
        // perf/scan-efficiency (2026-07-06): the session's pill-OCR pHash cache.
        // Lets classifyActivePillBand skip the Vision pill-band OCR when the
        // pill ROI is perceptually unchanged from the previous frame (the pill
        // is screen-fixed while scrolling). Reset on scan-context change inside
        // the session's resetConfirmations; omitted here would run Vision every
        // frame (unchanged pre-perf behaviour).
        pillOcrCache: sessionRef.current?.pillOcrCache(),
        // Vision-only trial: the pre-OCR palette gate + its colour tracker were
        // removed. extractPokedexFrame now derives the VETO-ONLY page-colour
        // family internally (deriveVetoOnlyFamily) and applies the same colour
        // veto to the per-cell commit; it also returns that family on the grid
        // frame (gridFrame.pageColourFamily) for the routing-side veto below.
      });
      frameOcrMs =
        (typeof performance !== "undefined" ? performance.now() : Date.now()) -
        ocrStartMs;
      // Sole-colour carry inputs (scanVariantInput). Only a dex page colour
      // counts, so a map frame between two dex frames never clears it.
      if (isDexPageColourFamily(gridFrame.pageColourFamily)) {
        lastPageColourFamilyRef.current = gridFrame.pageColourFamily ?? null;
      }
      if (
        gridFrame.variantChip?.source === "chip-text" &&
        gridFrame.variantChip.variant != null &&
        gridFrame.variantChip.confidence >= 0.8
      ) {
        lastPillVariantRef.current = gridFrame.variantChip.variant;
      }
      frameOcrRois = gridFrame.numberRoiBatchSize ?? 0;
      frameYieldMs = gridFrame.cellLoopYieldMs ?? 0;
      // b13c.3: defensive OCR-result log. The b13c device trace was SILENT on
      // frames that passed the blur gate — there was no way to tell whether OCR
      // ran and found nothing, or never produced dex numbers at all. Log the raw
      // dex numbers the extractor read from the grid cells (BEFORE the slot→
      // national-dex remap in adaptDexLiveFrame) so the next test shows exactly
      // what each clean frame yielded. This fires on EVERY frame that clears the
      // gate, whether or not anything was read.
      const ocrDexIds = gridFrame.cells
        .map((c) => c.dexNumber)
        .filter((n): n is number => n != null);
      // scan-speed (cold start): a frame that read ≥1 number is a PRODUCTIVE
      // read of its content — identical repeats can be suppressed normally. A
      // frame that read nothing leaves `frameProductive` false so the dedup
      // keeps the static frame re-admittable through the cold-start budget.
      frameProductive = ocrDexIds.length > 0;
      // E49 telemetry: one OCR fanout invocation; hit when it yielded any number.
      recordScanOcrAttempt(ocrDexIds.length > 0);
      if (ocrDexIds.length > 0) {
        // eslint-disable-next-line no-console
        if (DEX_SCAN_VERBOSE) console.log("[dex-scan] frame OCR result", {
          dexIds: ocrDexIds,
          count: ocrDexIds.length,
          region: gridFrame.activeRegion,
          filter: gridFrame.activeFilter,
          confidence: Number(gridFrame.confidence.toFixed(1)),
        });
      } else {
        // eslint-disable-next-line no-console
        if (DEX_SCAN_VERBOSE) console.log("[dex-scan] no dex IDs detected in frame", {
          cells: gridFrame.cells.length,
          region: gridFrame.activeRegion,
          confidence: Number(gridFrame.confidence.toFixed(1)),
          warnings: gridFrame.warnings,
        });
      }
      // b17d Fix 3 — region-change latch. Feed this frame's classified region
      // and confidence to the stabilizer. It only promotes the long-lived STABLE
      // region once REGION_STABLE_FRAMES consecutive frames agree above
      // REGION_MIN_CONFIDENCE, so a single-frame misread (the TestFlight `hisui`
      // latch) can't flip the committed region identity.
      //
      // Strong-same-frame-wins (telemetry 2026-06-22): we slot-map under
      // `routingRegion`, NOT the stable region. When THIS frame read a confident
      // region of its own (≥ floor) that contradicts a not-yet-superseded stale
      // stable region, the frame's own region wins for routing — the stale region
      // must never slot-map a confident current read (the G-Max Kanto
      // #003/#006/#009 → Galar #810/#811/#812 bug). A weak/unknown frame carries
      // no region of its own, so `routingRegion` falls back to the stable region
      // (sticky context only fills a missing/weak region). We drop only when
      // there is neither a confident current region nor a stable one.
      //
      // The SCAN CONTEXT LOCK takes the STABLE region instead — see the
      // boundary-thrash note below. Falls back to the routing region until the
      // stabilizer's first promotion, when there is no lock yet to thrash.
      let contextRegion: PokedexRegionLite = gridFrame.activeRegion;
      const stabilizer = regionStabilizerRef.current;
      if (stabilizer) {
        const stabilized = stabilizer.next({
          region: gridFrame.activeRegion,
          confidence: gridFrame.confidence,
        });
        if (stabilized.routingRegion == null) {
          // eslint-disable-next-line no-console
          if (DEX_SCAN_VERBOSE) console.log("[dex-scan] region not yet stable, frame dropped", {
            frameRegion: gridFrame.activeRegion,
            confidence: Number(gridFrame.confidence.toFixed(1)),
            framesAgreed: stabilized.framesAgreed,
          });
          return;
        }
        // Route this frame under the region that wins for THIS frame: the frame's
        // own confident region when it contradicts a stale stable region, else
        // the stable region (which also absorbs a single-frame misread).
        gridFrame.activeRegion = stabilized.routingRegion;
        // REGION-BOUNDARY THRASH (device log 2026-08-10, purified Kanto→Johto).
        //
        // `routingRegion` is deliberately un-hysteresised: one confident frame may
        // slot-map under its OWN region so a stale stable region can never remap a
        // good read (the G-Max #003/#006/#009 → Galar bug). That is right for
        // ROUTING and wrong for the context LOCK, which used to be fed the same
        // value. `dexScanContext` treats a region contradiction as `immediate`
        // (no debounce — "a hard navigation signal, never the flicker bucket"),
        // so at a dex boundary, where the visible rows genuinely straddle two
        // regions (#144–#155 spans Kanto/Johto) and the classifier legitimately
        // alternates frame to frame, EVERY flip tore the lock down and called
        // `resetConfirmations`. That clears cellEvidence (all accrued corroboration),
        // appliedThisSession (so committed cells are re-classified and re-OCR'd)
        // and the pill-OCR cache — then the variant reads null for several frames
        // while the context re-locks, so nothing can write. The trace shows five
        // resets in ~10 frames and a 70–78 frame (~45s) gap between the purified
        // rows being OBSERVED and anything COMMITTING.
        //
        // The lock is a long-lived identity, so it takes the STABLE region — which
        // is exactly the REGION_STABLE_FRAMES hysteresis the stabilizer already
        // computes. A real region switch still resets the context, ~3 frames later.
        // Routing, slot-mapping and every write gate are untouched.
        contextRegion = stabilized.region ?? stabilized.routingRegion;
      }

      const hudSkippedApplied: Array<{ dexId: number; slotIndex: number }> = [];

      const result: DexLiveFrameResult = adaptDexLiveFrame(gridFrame, {
        initialVariant: initialVariantRef.current,
        // PR-C Problem 1: signals A (bottom tab-pill OCR lines) and B (upper-grid
        // tint) are populated by the native layer onto the OCR frame when the b5
        // extension is installed; absent on web/older binaries, where the vote
        // falls back to the legacy filter keyword + sticky last-known.
        tabPillLines: gridFrame.tabPillLines,
        tintSample: gridFrame.tintSample,
        lastKnownVariant: lastKnownVariantRef.current,
        // Vision-only trial: the routing-side colour veto family now comes from
        // the frame's internally-derived deriveVetoOnlyFamily result on the grid
        // frame (was the deleted pre-OCR gate's colour tracker). Single-variant
        // families still resolve directly; NEVER from app_state.
        pageColourFamily: gridFrame.pageColourFamily ?? null,
        colourNarrowedVariant: gridFrame.colourNarrowedVariant ?? null,
        // perf/scan-efficiency (2026-07-06): DUMP-ON-APPLY. Let the adapter skip
        // the sprite classification pipeline for cells the session has already
        // committed this session (cleared on context change / scroll reverse).
        isCellApplied: (dexId) => sessionRef.current?.isCellApplied(dexId) ?? false,
        // HUD mirror map only: the skipped cells still belong in their columns.
        onCellSkippedApplied: (dexId, slotIndex) => { hudSkippedApplied.push({ dexId, slotIndex }); },
      });

      // b17d Fix 2 — app state is the SINGLE SOURCE OF TRUTH for variant.
      // b17n amendment — chip-text wins when confidence >= 0.9 and it disagrees.
      //
      // The TestFlight session (53 frames) showed the pixel-based variant
      // detectors contradicting themselves and the screen: a frame on the All
      // dex reported variant:"lucky" (chip-text, conf 0.99); hue-source detects
      // flipped between perfect/xxl/lucky/null across consecutive frames. The
      // b17d decision was to trust app_state exclusively. b17n relaxes this for
      // the one case where the chip-text signal is unambiguous: when chip-text
      // reports a variant at ≥ 0.9 confidence AND it disagrees with app_state,
      // chip-text is the on-screen ground truth and must win.
      //
      // All other pixel signals (hue/tint, chip-text < 0.9) remain diagnostic
      // only and never decide routing. A frame rejected as mode "unknown" still
      // applies nothing regardless.
      const appStateVariant = scanVariantInput();
      // b17n: pass the chip-text read from the adapter result so resolveTrustedVariant
      // can apply the authority rule. result.variantSource === "tab-only" means the
      // adapter short-circuited via the chip-text path (chip.source === "chip-text").
      //
      // colour-narrowed pill re-read (2026-06-19): the adapter recovered the
      // variant from the page COLOUR after the active-pill read failed.
      //   - "colour-narrowed-pill": a WIDE pill OCR re-read matched a real keyword
      //     filtered to the family's allowed tokens — it IS a chip-text read, so
      //     feed it as one so the unified resolver commits it (and the colour veto
      //     re-validates it, never resurrecting an impossible variant).
      //   - "colour-sole": a single-variant family fixed the variant from colour
      //     alone; resolveVariant resolves it from the family veto/colour-resolve
      //     (or accepts a matching app_state), so no synthetic chip read is needed.
      const chipTextRead =
        (result.variantSource === "tab-only" ||
          result.variantSource === "colour-narrowed-pill") &&
        result.variant != null
          ? {
              variant: result.variant,
              confidence: result.variantConfidence,
              hueAgrees: result.variantHueAgrees,
            }
          : null;
      // bug3 Part 2/3 — pass the live page-colour family so the colour veto can
      // reject a frozen app_state variant impossible for the page on screen and
      // re-resolve to the chip-text read. After a Part-3 transition this ref
      // already holds the NEW family, so subsequent cells route to the new dex.
      const trusted = resolveTrustedVariant(
        result,
        appStateVariant,
        chipTextRead,
        gridFrame.pageColourFamily ?? null,
      );
      const trustedVariant = trusted.variant;
      const detectedVariant = result.variant;
      // bug3 Part 4 — remember the live dex type for the PiP HUD. A null variant
      // means this frame committed nothing (mode "unknown"); keep the prior known
      // type rather than dropping the HUD back to "Open your Pokédex" mid-scan.
      if (trustedVariant != null) {
        liveDexVariantRef.current = trustedVariant;
      }
      let trustedResult: DexLiveFrameResult = {
        ...result,
        variant: trusted.variant,
        // Canonical in both cases: app_state and chip-text-override are both
        // authoritative reads, not inferences.
        variantInferred: trusted.variantInferred,
      };

      // ── Bounded scan-context: fast page acquisition + sticky-but-bounded reuse.
      //
      // The per-frame adapter has no cross-frame memory, so a weak/failed-OCR
      // frame on a page we have CLEARLY been scanning (region read, chip read, a
      // committed dex colour family) returns mode:"unknown"/variant:null and the
      // session drops it as `no_variant` — the long pre-acquisition stall the
      // telemetry showed. We feed this frame's page-level signals to a bounded
      // tracker that locks a provisional context within 1-2 good frames and then,
      // for a small consecutive-miss budget, lets a weak frame BORROW the locked
      // region/variant rather than be dropped. A real region/variant/colour switch
      // (or leaving the dex page) resets the lock immediately, so the borrowed
      // context can NEVER apply entries to the wrong dex.
      //
      // `isDexPage`: the pre-OCR gate already ALLOWED this frame (we only reach
      // here on an allowed frame), AND we still see a dex signal — a committed dex
      // colour family, a known region, or parsed numbers. If none of those hold the
      // tracker treats it as off-page and drops the lock.
      const ctxTracker = scanContextRef.current;
      if (ctxTracker) {
        const committedFamily = gridFrame.pageColourFamily ?? null;
        if (committedFamily != null && committedFamily.startsWith("dex_")) {
          lastRecognisedGridAtRef.current = Date.now();
          // A recognised Pokédex grid is the ONE thing that turns the card
          // back to landscape (2026-09-18). Once; the grid's own pushes below
          // carry no orientation, so the shape is left alone until a Pokémon
          // page latches again.
          if (hudOrientationRef.current !== "landscape") {
            void setDexPipPhase({
              phase: "reading_grid",
              message: dexScanningHudMessage("reading_grid", liveDexVariantRef.current),
              orientation: markHudOrientation("landscape"),
            });
          }
        }
        const isDexColourFamily =
          committedFamily != null && committedFamily.startsWith("dex_");
        const ctxDecision = ctxTracker.observe({
          isDexPage:
            isDexColourFamily ||
            gridFrame.activeRegion !== "unknown" ||
            result.numbersRead > 0,
          // The HYSTERESISED region, not this frame's routing region — see the
          // region-boundary thrash note at the stabilizer call site.
          region: contextRegion,
          // Use the TRUSTED variant (app_state / chip-text override) as the lock
          // identity — the same authority the session routes under — not the raw
          // pixel guess, so the lock can't latch a flickered hue read.
          variant: trustedVariant ?? detectedVariant,
          variantConfidence:
            trustedVariant != null
              ? Math.max(result.variantConfidence, 0.6)
              : result.variantConfidence,
          colourFamily: committedFamily,
          hasNumbers: result.numbersRead > 0,
          // MID-SESSION SWITCH (telemetry 2026-06-22, focused #476 fix). Feed the
          // RAW chip-text label straight off the OCR frame's `variantChip` — the
          // pill OCR's direct read BEFORE the unified resolver's colour veto could
          // hold it to null. The earlier wiring sourced this from `chipTextRead`,
          // which is itself null whenever `result.variant` was vetoed — exactly the
          // hold/unstable frame this switch path exists to survive. On the focused
          // trace the PURIFIED pill read cleanly (chip-text, 0.85) yet the frame was
          // held to variant_hold, so `chipTextRead` was null and the tracker never
          // even SAW the label: the only PURIFIED evidence in the run was dropped and
          // the stale Perfect lock was reaffirmed. Reading `variantChip` directly
          // routes the un-vetoed label as switch evidence the veto cannot suppress.
          strongVariantText:
            gridFrame.variantChip?.source === "chip-text"
              ? gridFrame.variantChip.variant ?? null
              : null,
          strongVariantTextConfidence:
            gridFrame.variantChip?.source === "chip-text"
              ? gridFrame.variantChip.confidence ?? 0
              : 0,
          // R14 FIX 1 — feed the RAW per-frame PIXEL detector read (the adapter's
          // own `result.variant`, before the app_state authority rule chose the
          // routed variant) so the context tracker can tell a single-frame
          // app_state flicker (pixel still reads the LOCKED variant → noise, no
          // reset) from a genuine switch (pixel AGREES with the new variant →
          // reset now). This is exactly the signal behind the R13 `variant
          // pixel-detector ignored` resets. Diagnostic-routing only — the apply
          // gate and the app_state-authoritative `variant` above are unchanged.
          pixelVariant: detectedVariant,
          pixelVariantConfidence: result.variantConfidence,
        });

        // Mid-session switch telemetry (2026-06-22): mirror the tracker's
        // switch_requested / switch_locked / switch_rejected events one-to-one so
        // a device trace shows whether a tab change was detected, locked, or
        // rejected as noise. Pure bookkeeping — never affects a write.
        if (
          ctxDecision.event === "switch_requested" ||
          ctxDecision.event === "switch_locked" ||
          ctxDecision.event === "switch_rejected"
        ) {
          const rec = sessionRef.current.telemetryRecorder();
          const frameIndexNow = sessionRef.current.snapshot().framesObserved + 1;
          if (ctxDecision.event === "switch_requested") {
            rec.noteContextSwitchRequested({
              from: ctxDecision.switchFrom ?? null,
              target: ctxDecision.switchTarget ?? null,
              votes: ctxDecision.switchVotes ?? 0,
              // A switch raised on a frame whose routed variant was vetoed/held is
              // exactly the strong-chip-text-on-hold case this path recovers; a
              // vote raised while the frame still produced a routable read is an
              // ordinary disagreement. Distinguish them for the device trace.
              reason:
                trustedResult.mode === "unknown" || trustedResult.variant == null
                  ? "strong_chip_text_on_hold"
                  : "strong_chip_text",
              frameIndex: frameIndexNow,
            });
          } else if (ctxDecision.event === "switch_locked") {
            rec.noteContextSwitchLocked({
              from: ctxDecision.switchFrom ?? null,
              target: ctxDecision.context?.variant ?? null,
              votes: ctxDecision.switchVotes ?? 0,
              frameIndex: frameIndexNow,
            });
            // Clear the stale apply/PiP status on a CONFIRMED mid-session switch.
            // The rolling "Added #…" grid the native HUD renders is keyed to the
            // OLD dex (telemetry 2026-06-22: the Kanto G-Max screenshot still
            // showed "Added #345" carried over from the prior Shadow apply). This
            // is DISPLAY/SESSION status only — it clears the PiP window, never
            // any database write or committed-count total. Shares one
            // implementation with the `reset` path below (see the note there):
            // a dex change reaches the HUD through either branch.
            clearPerDexHudState();
          } else {
            rec.noteContextSwitchRejected({ frameIndex: frameIndexNow });
          }
        }

        // R14 FIX 1 — context-change debounce telemetry. A variant/colour change
        // was SEEN this frame but SUPPRESSED (pixel detector still read the locked
        // variant, or the change has not yet persisted CONTEXT_CHANGE_DEBOUNCE_
        // FRAMES). The lock is HELD, so the in-progress streak + PiP overlay
        // survive the flicker. Logged separately so a device trace shows exactly
        // which flickers were absorbed instead of resetting the scan context.
        if (ctxDecision.event === "change_debounced") {
          // eslint-disable-next-line no-console
          if (DEX_SCAN_VERBOSE) console.log("[dex-scan] context change debounced", {
            reason: ctxDecision.reason ?? null,
            framesPending: ctxDecision.framesPending ?? 0,
            appState: trustedVariant,
            pixelDetected: detectedVariant,
            locked: ctxDecision.context?.variant ?? null,
          });
        }

        // Telemetry marker for latency debugging: emit on every state change so
        // the next device trace shows exactly when the page was acquired, when a
        // weak frame reused the lock, and when a switch forced a reset.
        if (ctxDecision.event !== "idle" && ctxDecision.event !== "acquiring") {
          // eslint-disable-next-line no-console
          console.log("[dex-scan] scan context", {
            event: ctxDecision.event,
            region: ctxDecision.context?.region ?? null,
            variant: ctxDecision.context?.variant ?? null,
            colourFamily: ctxDecision.context?.colourFamily ?? null,
            misses: ctxDecision.misses,
            reason: ctxDecision.reason ?? null,
            lockedThisFrame: ctxDecision.lockedThisFrame,
            // R14 FIX 1 — on a committed reset, how many consecutive frames
            // confirmed the change (1 = immediate region/pixel-corroborated; N =
            // debounce satisfied). Present only on reset.
            framesConfirmed: ctxDecision.framesConfirmed ?? null,
          });
        }

        // Stale-confirmation fix #1 — RESET INVALIDATION (2026-06-27). On ANY
        // committed scan-context reset (region_changed / colour_changed /
        // variant_changed / left_dex_page / miss_budget / text_switch) the grid
        // positions the session keys its pending confirmation buffers by are about
        // to be remapped to new dexIds. Clear every pending per-slot confirmation
        // (sprite-match / histSim / confidence / sighting count / two-frame lock)
        // BEFORE this frame is ingested, so a newly-remapped dexId can never
        // inherit the previous cell's score and apply on a single post-reset
        // sighting. Committed writes are untouched (see resetConfirmations).
        if (ctxDecision.event === "reset") {
          sessionRef.current.resetConfirmations(ctxDecision.reason);
          // …and when that reset means a DIFFERENT DEX, clear the per-dex HUD
          // panels too. Device log 2026-08-11: "the PiP HUD didn't clear on
          // switch". A perfect → purified tab change arrives HERE, as an ordinary
          // reset with reason "variant_changed" (the trace shows exactly that,
          // then an immediate re-lock onto purified) — not via the `switch_locked`
          // branch above, which was the only place that cleared the HUD. So the
          // stale-panel bug that branch was written to fix simply recurred through
          // its sibling path. Both now agree via resetMeansDifferentDex.
          if (resetMeansDifferentDex(ctxDecision.reason)) {
            clearPerDexHudState();
          }
        }

        // REUSE GATE. Only backfill when ALL hold:
        //   - the tracker handed back a live context (null on reset/expired, so a
        //     contradiction or an exhausted budget can never bridge),
        //   - this frame's OWN read could not resolve a routable variant
        //     (otherwise the frame stands on its own — never override a good read),
        //   - the frame actually parsed dex numbers (a reuse with zero observations
        //     conjures nothing — there is simply nothing to apply), and
        //   - the reused context carries a non-main variant + known region so the
        //     session has a concrete dex to route under.
        //
        // The reused (region, variant) is ONLY a routing hint: every observation
        // still clears the unchanged candidate gates (membership, sprite, slot
        // range, two-frame corroboration, cross-check, overlay text) downstream.
        const reuse = ctxDecision.context;
        const ownReadFailed =
          trustedResult.mode === "unknown" || trustedResult.variant == null;
        if (
          reuse &&
          ownReadFailed &&
          result.numbersRead > 0 &&
          reuse.variant != null &&
          reuse.region !== "unknown"
        ) {
          // eslint-disable-next-line no-console
          if (DEX_SCAN_VERBOSE) console.log("[dex-scan] scan context reused for routing", {
            region: reuse.region,
            variant: reuse.variant,
            numbersRead: result.numbersRead,
            misses: ctxDecision.misses,
          });
          trustedResult = {
            ...trustedResult,
            mode: "grid_4x4",
            variant: reuse.variant,
            // Borrowed from a prior frame's read, not this frame's own OCR.
            variantInferred: true,
            region: reuse.region as DexLiveFrameResult["region"],
          };
          liveDexVariantRef.current = reuse.variant;
        }

        // ── Pre-context top-row retention (Push 11). ───────────────────────
        //
        // The telemetry repro: a user opens the Pokédex at the TOP of a curated
        // page, OCR reads the top rows cleanly, then the user scrolls — all
        // BEFORE the variant context has locked. Those top-row numbers are read
        // under a provisional (chip-text / app-state) variant and, today, thrown
        // away; by the time the context locks (frame 115 in the trace) row 1 is
        // off-screen, so G-Max #003/#006/#009 are never committed.
        //
        // We RETAIN those pre-lock rows in a short-lived, frame-bounded buffer
        // tagged with the provisional variant, and PROMOTE them once a lock lands
        // under the SAME variant. Promotion re-injects them through the unchanged
        // session.ingest gates as ONE synthetic sighting: the 2-frame/sprite/
        // member gates still govern the write, so a promoted row can only PRIME
        // corroboration — it can never write without a live re-confirmation under
        // the locked variant. A variant switch clears the buffer so rows read
        // under the old provisional variant never leak into a different dex.
        const preBuf = preContextRef.current;
        if (preBuf) {
          // The lock identity drives both promote (on lock) and the switch reset.
          const lockedVariant = ctxDecision.context?.variant ?? null;
          // PROMOTE on the frame the context first locks (or re-locks after a
          // reset) under a concrete variant. We re-observe the buffered rows
          // under that variant so a top row read pre-lock is not lost.
          if (
            ctxDecision.lockedThisFrame &&
            lockedVariant != null &&
            preBuf.size() > 0
          ) {
            const promo = preBuf.promote({
              lockedVariant,
              frameIndex: sessionRef.current.snapshot().framesObserved + 1,
            });
            const rec = sessionRef.current.telemetryRecorder();
            const frameIndexNow = sessionRef.current.snapshot().framesObserved + 1;
            if (promo.expired > 0) {
              rec.notePreContextExpired({ count: promo.expired, frameIndex: frameIndexNow });
            }
            if (promo.event === "variant_switch_reset") {
              rec.notePreContextVariantSwitchReset({ frameIndex: frameIndexNow });
            }
            if (promo.event === "promoted" && promo.rows.length > 0) {
              const promotedObs: VariantCellObservation[] = [];
              for (const row of promo.rows) {
                for (let i = 0; i < row.ids.length; i += 1) {
                  const dexNumber = row.ids[i];
                  const yPct = row.yPcts[i];
                  promotedObs.push({
                    dexNumber,
                    state: "present",
                    yPct: Number.isFinite(yPct) ? yPct : undefined,
                    // Scan overhaul v2 (Phase 3): the weakGmaxEvidence staging
                    // flag is gone. G-Max promotions re-inject as normal present
                    // observations and are held only by the standard 2-frame
                    // corroboration streak (ingest's sole write authority).
                  });
                }
              }
              const promotedIds = promotedObs.map((o) => o.dexNumber);
              rec.notePreContextPromoted({
                variant: lockedVariant,
                ids: promotedIds,
                frameIndex: frameIndexNow,
              });
              // Re-inject as a synthetic grid frame routed under the locked
              // variant. It primes corroboration only — ingest's 2-frame/sprite/
              // member gates remain the sole write authority.
              const promotedResult: DexLiveFrameResult = {
                ...trustedResult,
                mode: "grid_4x4",
                variant: lockedVariant,
                variantInferred: true,
                observations: promotedObs,
                numbersRead: promotedObs.length,
              };
              sessionRef.current.ingest(promotedResult, frame.ts || Date.now());
            }
          }

          // RETAIN this frame's pre-lock rows. Only buffer while we have NOT yet
          // routed this frame under a locked, stable variant of its own (own read
          // failed / context still acquiring) AND OCR actually read numbers under
          // a provisional variant. A frame that stands on its own (committed under
          // its own resolved variant) needs no buffering — it is already applied.
          const provisionalVariant = trustedVariant ?? detectedVariant;
          const ownFrameCommitted =
            trustedResult.mode === "grid_4x4" && trustedResult.variant != null;
          if (
            !ctxDecision.lockedThisFrame &&
            !ownFrameCommitted &&
            provisionalVariant != null &&
            result.numbersRead > 0 &&
            result.observations.length > 0
          ) {
            const rowsById = new Map<string, { ids: number[]; yPcts: number[] }>();
            for (const o of result.observations) {
              if (o.state !== "present") continue;
              const rk = rowKeyForYPct(o.yPct);
              const entry = rowsById.get(rk) ?? { ids: [], yPcts: [] };
              entry.ids.push(o.dexNumber);
              entry.yPcts.push(o.yPct ?? Number.NaN);
              rowsById.set(rk, entry);
            }
            if (rowsById.size > 0) {
              const frameIndexNow =
                sessionRef.current.snapshot().framesObserved + 1;
              const retain = preBuf.retain({
                variant: provisionalVariant,
                rows: Array.from(rowsById, ([rowKey, v]) => ({
                  rowKey,
                  ids: v.ids,
                  yPcts: v.yPcts,
                })),
                frameIndex: frameIndexNow,
              });
              const rec = sessionRef.current.telemetryRecorder();
              if (retain.expired > 0) {
                rec.notePreContextExpired({
                  count: retain.expired,
                  frameIndex: frameIndexNow,
                });
              }
              if (retain.event === "variant_switch_reset") {
                rec.notePreContextVariantSwitchReset({ frameIndex: frameIndexNow });
              } else if (retain.event === "observed") {
                rec.notePreContextObserved({
                  buffered: retain.buffered,
                  frameIndex: frameIndexNow,
                });
              }
            }
          }
        }
      }

      // Diagnostic-only: surface when a pixel detector diverges from the trusted
      // variant. Post-b17n this only fires when chip-text was below threshold
      // or a non-chip-text pixel signal disagreed — chip-text at >= 0.9 overrides
      // app_state rather than being "ignored".
      if (
        trustedVariant != null &&
        detectedVariant != null &&
        detectedVariant !== trustedVariant &&
        !trusted.variantOverride
      ) {
        // eslint-disable-next-line no-console
        if (DEX_SCAN_VERBOSE) console.log("[dex-scan] variant pixel-detector ignored", {
          appState: trustedVariant,
          pixelDetected: detectedVariant,
          pixelSource: result.variantSource,
        });
      }

      // b17d — the session-sticky pixel-variant ref is no longer authoritative
      // (app state is). Keep it updated purely so the diagnostic vote in the
      // adapter still has a sticky fallback for its logging-only output.
      if (
        detectedVariant != null &&
        (result.variantSource === "tab+tint" ||
          result.variantSource === "tab-only" ||
          result.variantSource === "tint-only")
      ) {
        lastKnownVariantRef.current = detectedVariant as TabVariant;
      }

      // b17d Fix 2 — ingest by the app-state variant (`trustedResult`). A scan
      // commits every readable frame under the active pill tab, so the
      // contradictory per-frame pixel flips in the TestFlight log can no longer
      // route catches to the wrong dex.
      const frameApply = sessionRef.current.ingest(trustedResult, frame.ts || Date.now());
      // Frame dump for the exported log (export unlocked only): the exact
      // JPEG this ingest read, kept when it brought a new dex number.
      recordDexFrame(frame, trustedResult.observations.map((o) => o.dexNumber));
      // Round-12 (Fix 2): record the variant this frame ingested under for the
      // dedup interval selection in `markDone` below.
      frameVariantHint = trustedResult.variant ?? null;
      // Round-12 Fix 3 — low-capture signal. A frame that READ numbers but
      // committed nothing means the trainer is scrolling past rows faster than the
      // multi-frame confirmation can corroborate them. Count consecutive such
      // frames; reset the instant a frame commits. The hold-steady detector
      // debounces on top, so a couple of lean frames never trips the prompt. This
      // reads only the frame's OWN read/commit counts — it never changes them.
      if (frameApply.applied > 0) {
        holdSteadyReadWithoutApplyRef.current = 0;
      } else if (trustedResult.numbersRead > 0) {
        holdSteadyReadWithoutApplyRef.current += 1;
      }
      // Round-13 Fix B — gate lowCapture on ACTIVE scroll motion. The grid
      // "advanced" this frame iff a present dexId appeared that was not present
      // last frame (new rows scrolled into view). A stationary grid (same present
      // set) is not scrolling, so the read/no-commit pattern is a hard cell held
      // still — not a "scroll slower" situation — and must NOT raise lowCapture
      // (it was pinning the prompt on). This reads only the present-cell set; it
      // never changes any read/commit count, gate, or veto.
      const presentIdsThisFrame = new Set<number>();
      let scrollAdvanced = false;
      for (const o of trustedResult.observations) {
        if (o.state === "present" && o.dexNumber != null) {
          presentIdsThisFrame.add(o.dexNumber);
          // A present dexId not seen last frame means new rows scrolled in.
          if (!prevPresentIdsRef.current.has(o.dexNumber)) {
            scrollAdvanced = true;
          }
        }
      }
      prevPresentIdsRef.current = presentIdsThisFrame;
      holdSteadyLowCaptureRef.current = isLowCaptureActive({
        readWithoutApplyStreak: holdSteadyReadWithoutApplyRef.current,
        threshold: HOLD_STEADY_LOW_CAPTURE_FRAMES,
        scrollAdvanced,
      });
      // Surface genuine new-to-dex adds from this frame to the scanner UI so it
      // can keep a rolling "Added" list. Already-present and manual entries are
      // filtered out inside the session, so anything here is a real new add.
      if (frameApply.newlyAdded && frameApply.newlyAdded.length > 0) {
        // scan-speed 2026-06-30 — this frame's genuine new-to-dex commit count
        // feeds the timing recorder's real-time commit-dwell metric.
        frameCommittedCount = frameApply.newlyAdded.length;
        // Accumulate the distinct committed-new set for the user-facing count.
        for (const id of frameApply.newlyAdded) newAddsRef.current.add(id);
        const cb = onNewAddsRef.current;
        // The session only ingests frames with a resolved variant, so by the
        // time `newlyAdded` is non-empty `trustedResult.variant` is always
        // non-null in practice. The type still admits null (mode="unknown"
        // frames upstream), so we narrow explicitly before invoking the
        // callback rather than asserting non-null.
        const variant = trustedResult.variant;
        if (cb && variant != null) {
          cb(
            frameApply.newlyAdded.map((dexNumber) => ({
              dexNumber,
              variant,
            })),
          );
        }
      }
      // THE HUD MIRROR MAP (2026-09-24 owner direction): every number this
      // frame read, in its on-screen column, coloured by what the scanner SEES.
      // The colour comes from the frame's own read plus the dex store, NOT from
      // the commit result: a frame that commits nothing (a row of uncaught
      // cards, a row still being confirmed) returns before the session lists
      // what was already registered, and the first device pass showed exactly
      // those rows as gaps.
      //   committed new this frame / earlier this scan -> added
      //   the dex holds it                             -> already
      //   read as caught, not confirmed yet            -> reading
      //   a silhouette                                 -> seen (orange-red)
      //   a blank card (state "unknown" = the reader saw a centred number
      //   with no sprite: registered, not caught)      -> missing (red)
      // Display-only; never a write.
      {
        const hudVariant = trustedResult.variant;
        // The adapter's slotIndex is 1-BASED (rowIndex * stride + colIndex + 1);
        // the HUD store takes a 0-based lattice slot. The first device pass put
        // every chip one column right (#010 under the third column).
        const hudSlot = (slotIndex: number | undefined) => (slotIndex != null && slotIndex >= 1 ? slotIndex - 1 : undefined);
        const slotOf = new Map<number, number>();
        for (const o of trustedResult.observations) {
          const slot = hudSlot(o.slotIndex);
          if (o.dexNumber != null && slot != null) slotOf.set(o.dexNumber, slot);
        }
        const hudCells: DexHudCell[] = [];
        const placed = new Set<number>();
        for (const n of frameApply.newlyAdded ?? []) {
          hudCells.push({ n, kind: "added", slot: slotOf.get(n) });
          placed.add(n);
        }
        if (hudVariant != null) {
          for (const o of trustedResult.observations) {
            const n = o.dexNumber;
            if (n == null || placed.has(n)) continue;
            placed.add(n);
            // WHAT THE SCANNER SEES wins (2026-09-24 owner direction): a card
            // read as blank is red even when the Nexus Dex says caught, so the
            // trainer sees the disagreement and can fix whichever side is
            // wrong. The dex only decides between the caught shades.
            const held = getDexEntryState(hudVariant, n)?.state === "present";
            let kind: DexHudCell["kind"];
            if (o.state === "present") {
              // newAddsRef is not per variant, so "added" also needs this dex
              // to hold it.
              kind = held && newAddsRef.current.has(n) ? "added" : held ? "already" : "reading";
            } else if (o.state === "missing" && o.spriteState === "silhouette") {
              kind = "seen";
            } else {
              kind = "missing";
            }
            hudCells.push({ n, kind, slot: hudSlot(o.slotIndex) });
          }
        }
        for (const n of frameApply.alreadyPresent ?? []) {
          if (!placed.has(n)) hudCells.push({ n, kind: "already", slot: slotOf.get(n) });
        }
        // Cells the adapter skipped because this scan already applied them:
        // without them a row whose other cells were applied earlier arrived
        // as a fragment and the map started a new row for it (device pass 3).
        for (const { dexId, slotIndex } of hudSkippedApplied) {
          if (placed.has(dexId)) continue;
          placed.add(dexId);
          hudCells.push({ n: dexId, kind: newAddsRef.current.has(dexId) ? "added" : "already", slot: hudSlot(slotIndex) });
        }
        if (hudCells.length > 0) {
          noteDexFrameCells(hudCells, {
            columns: hudVariant != null ? gridColumnsForVariant(hudVariant) : undefined,
            // The VARIANT only: dex numbers are national, so the map flows
            // across a region boundary instead of clearing (device pass 3:
            // the HUD emptied at the Kanto/Johto edge).
            context: hudVariant ?? "?",
          });
          // One line whenever what the HUD draws changes, so an exported log
          // shows the map the trainer saw (rows of "n:kind", "-" = blank).
          const hudNums = dexRecentNumbersForPip();
          const hudKinds = dexRecentKindsForPip();
          const hudLine = hudNums
            .map((n, i) => (n > 0 ? `${n}:${(hudKinds[i] || "?").slice(0, 3)}` : "-"))
            .reduce<string[]>((rows, cell, i) => {
              if (i % 4 === 0) rows.push(cell);
              else rows[rows.length - 1] += ` ${cell}`;
              return rows;
            }, [])
            .join(" | ");
          if (hudLine !== lastHudLineRef.current) {
            lastHudLineRef.current = hudLine;
            // eslint-disable-next-line no-console
            console.log("[live-scan] hud map", { rows: hudLine });
          }
        }
      }
      const totals = sessionRef.current.snapshot();
      // b13c.6: prove the broadcast → OCR handoff reached ingest, and surface
      // what was read. `appliedThisFrame` is broken out from the running
      // `applied` total so the device trace shows live streaming progress and
      // exactly which variant THIS frame committed under (the Build 195 log only
      // ever showed "lucky" — this now reports the real per-frame variant).
      // PR b17f Change 5 — make the source of the trusted variant + region
      // explicit in the log so a TestFlight trace can answer "is variant from
      // app state?" without code spelunking.
      // b17n: when chip-text overrode app_state, variantSource is "chip-text"
      // and variantOverride is true so the frame ingested log is self-evident.
      const variantSource = variantSourceLabel(trustedResult.variant, trusted.variantOverride);
      const regionSource: "app_state" | "unknown" =
        trustedResult.region && trustedResult.region !== "unknown"
          ? "app_state"
          : "unknown";
      // eslint-disable-next-line no-console
      if (DEX_SCAN_VERBOSE) console.log("[dex-scan] frame ingested", {
        region: trustedResult.region,
        regionSource,
        mode: trustedResult.mode,
        // variant is from app state (b17d), or from chip-text when chip-text
        // overrode at >= 0.9 confidence (b17n). variantOverride: true signals
        // the latter case; variantDetected is diagnostic only.
        variant: trustedResult.variant,
        variantSource,
        variantOverride: trusted.variantOverride,
        variantDetected: detectedVariant,
        confidence: Number(trustedResult.confidence.toFixed(1)),
        appliedThisFrame: frameApply.applied,
        framesObserved: totals.framesObserved,
        numbersRead: totals.numbersRead,
        applied: totals.applied,
        // Scroll-stability diff for THIS frame, so a false write can be traced
        // to (or cleared of) mid-scroll motion. null = the gate was disabled
        // and the frame is UNMEASURED, which is not the same as still. The
        // 20260720 trace carried neither value on the frames that produced its
        // false writes, so the smear hypothesis could not be tested at all.
        // Pair this with the `apply observations` line in the same frame to
        // answer "was the frame that wrote #N moving?" directly.
        stabilityDiff: lastStabilityDiffRef.current,
      });

      // b18g — scroll-gap detection + live nudge.
      // Collect present-cell IDs from this frame (post-gate, post-cross-check).
      const frameIds = trustedResult.observations
        .filter((o) => o.state === "present")
        .map((o) => o.dexNumber)
        .filter((n): n is number => n != null);
      const frameReadCount = trustedResult.observations.filter((o) => o.dexNumber != null).length;
      // "Saw a grid" is the reader's view, before any gate: a resolved cell,
      // or a Pokédex region tab / filter chip in the signature.
      //
      // NOT raw `numbersRead` (2026-09-22 Galaxy photos): the map is full of
      // numbers (raid timers, "19 m", gym counters) and every one of them
      // counted as "still on the grid", so the Pokédex coaching ("Slow down:
      // scrolling past rows before they lock", orange bubble) followed the
      // trainer onto the map and stayed there. A resolved cell needs a dex
      // number in a lattice position; a timer never resolves to one.
      const nowMs = Date.now();
      // ...and (2026-09-22 Galaxy photos) resolved cells alone are not enough
      // either: raid timers, XP totals and power-spot CPs resolve into
      // "cells" on screens that are not the Pokédex, and the coaching then
      // told the trainer to scroll slower on the map. A grid sighting needs
      // the Pokédex's own chrome on this frame or within the last few
      // seconds, the same lock the write gate uses (dexLiveScanSession).
      const chromeNow = hasPokedexChrome(result);
      if (chromeNow) {
        lastGridChromeAtRef.current = nowMs;
        sawPokedexRef.current = true;
      }
      const chromeRecent = lastGridChromeAtRef.current > 0 && nowMs - lastGridChromeAtRef.current < 4000;
      const frameSawGrid = chromeNow || (frameReadCount > 0 && chromeRecent);
      if (frameSawGrid) {
        offGridFramesRef.current = 0;
        lastOnGridAtRef.current = nowMs;
      } else {
        offGridFramesRef.current += 1;
      }
      const offGrid =
        offGridFramesRef.current >= OFF_GRID_FRAMES && nowMs - lastOnGridAtRef.current >= OFF_GRID_MS;
      // SCAN COVERAGE: every cell the scanner resolved this frame, caught or NOT.
      // A scan's objective is to sweep the whole dex, so this — not the caught
      // count — is the progress the PiP reports.
      for (const o of trustedResult.observations) {
        if (o.state !== "unknown" && o.dexNumber != null) {
          scannedIdsRef.current.add(o.dexNumber);
        }
      }

      // SELF-CORRECTION: feed this frame's reads to the reverse ratchet. When the
      // scanner has contradicted one of its OWN earlier catches enough times, at
      // high confidence, we undo that write instead of letting a false apply
      // stand forever. 2026-09-24 (owner direction: auto-correct): a card GO
      // draws BLANK (centred number) read that way over and over also undoes a
      // catch the scan did not write, manual ticks included; see
      // BLANK_CARD_RETRACTION_THRESHOLD for why that evidence is sprite-proof.
      const selfCorrectVariant = trustedResult.variant;
      if (selfCorrectVariant != null) {
        const toRetract = selfCorrectionRef.current.observe(
          trustedResult.observations,
          (id) => {
            const entry = getDexEntryState(selfCorrectVariant, id);
            return entry?.source === "scan" && entry.state === "present";
          },
          (id) => getDexEntryState(selfCorrectVariant, id)?.state === "present",
        );
        for (const id of toRetract) {
          retractScanPresent(selfCorrectVariant, id, null, { anySource: true });
        }
      }
      if (frameIds.length > 0) {
        for (const id of frameIds) allSeenIdsRef.current.add(id);
        frameIdHistoryRef.current.push(frameIds);

        // PR-E60 — resolution check FIRST. If a targeted steer is active and
        // this frame reads the target row (or any row above it — the user
        // scrolled back past the gap), the gap is covered: clear the steer.
        if (steerTargetRowRef.current !== null) {
          const minRowThisFrame = Math.min(...frameIds.map(dexIdToRow));
          if (minRowThisFrame <= steerTargetRowRef.current) {
            // eslint-disable-next-line no-console
            if (DEX_SCAN_VERBOSE) console.log("[dex-scan] steerResolved", {
              targetRow: steerTargetRowRef.current,
              minRowThisFrame,
            });
            steerTargetRowRef.current = null;
            consecutiveGapCountRef.current = 0;
            setState((prev) => ({ ...prev, showScrollNudge: false, scrollSteer: null }));
          }
        }

        const hist = frameIdHistoryRef.current;
        if (hist.length >= 2) {
          const recentTwo: number[][] = [hist[hist.length - 2], hist[hist.length - 1]];
          const gaps: RowGap[] = detectScrollGaps(recentTwo);
          const rowsSkipped = gaps.reduce((sum, g) => sum + g.skipped, 0);
          if (gaps.length > 0) {
            consecutiveGapCountRef.current += 1;
            // eslint-disable-next-line no-console
            if (DEX_SCAN_VERBOSE) console.log("[dex-scan] rowJumpDetected", {
              rowJumpDetected: true,
              rowsSkipped,
              consecutiveGaps: consecutiveGapCountRef.current,
            });
            if (
              consecutiveGapCountRef.current >= CONSECUTIVE_GAP_THRESHOLD &&
              steerTargetRowRef.current === null
            ) {
              // The first skipped row is one past the last row seen in the
              // earlier frame. PoGo forbids autoscroll, so we only GUIDE the
              // user back; the steer persists until they act (no auto-dismiss).
              const targetRow = gaps[0].fromRow + 1;
              const leadDexId = rowToDexIds(targetRow)[0] ?? targetRow * 4 + 1;
              steerTargetRowRef.current = targetRow;
              if (nudgeTimerRef.current) {
                clearTimeout(nudgeTimerRef.current);
                nudgeTimerRef.current = null;
              }
              // eslint-disable-next-line no-console
              if (DEX_SCAN_VERBOSE) console.log("[dex-scan] steerShown", { targetRow, leadDexId });
              setState((prev) => ({
                ...prev,
                showScrollNudge: true,
                scrollSteer: { targetRow, leadDexId },
              }));
            }
          } else {
            consecutiveGapCountRef.current = 0;
          }
        }
      }

      // Advanced image pipeline (Push 3): structured session JSON +
      // registration classification + crop capture. Runs on the SAME
      // extracted frame (no second OCR pass) and is strictly additive —
      // it never writes Dex state, only the structured-JSON/crop layer.
      let pipelineDiag: DexImagePipelineDiag | undefined;
      if (pipelineRef.current) {
        try {
          await pipelineRef.current.runFrame(frame.dataUrl, frame.ts || Date.now(), gridFrame);
          const d = pipelineRef.current.session.diagnostics;
          pipelineDiag = {
            region: pipelineRef.current.session.region,
            entriesScanned: d.entriesScanned,
            newRegistrations: d.newRegistrations,
            dedupedSkipped: d.dedupedSkipped,
            motionSkippedFrames: d.motionSkippedFrames,
            visibilitySkippedCells: d.visibilitySkippedCells,
            cropsCaptured: d.cropsCaptured,
            cropsUnavailable: d.cropsUnavailable,
            // DEV only (2026-09-22): the session grows every frame, so this was
            // an O(entries) stringify per frame for a panel production never shows.
            sessionJson: import.meta.env.DEV ? JSON.stringify(pipelineRef.current.toJson()) : "",
          };
        } catch (e) {
          // Pipeline is additive — a failure must not break the live scan.
          // eslint-disable-next-line no-console
          console.warn("[useDexLiveScan] image pipeline frame failed", e);
        }
      }

      // Publish diagnostic snapshot for the menu panel. We surface the
      // session-level cumulative counters so testers see scan progress
      // across frames instead of just the last frame's numbers.
      const pipDiag = pipDiagRef.current;
      const diag: DexScanDiagnostics = {
        ts: Date.now(),
        source: "live",
        // b17d Fix 2: the routed variant is the app-state pill tab. The pixel
        // detector's confidence/source/detail/disagreement ride along as
        // diagnostics only — they describe what the (ignored) detector saw.
        variant: trustedResult.variant,
        variantInferred: trustedResult.variantInferred,
        variantConfidence: result.variantConfidence,
        variantSource: result.variantSource,
        variantDetail: result.variantDetail,
        variantDisagreement: result.variantDisagreement,
        region: result.region,
        mode: trustedResult.mode,
        confidence: result.confidence,
        framesObserved: totals.framesObserved,
        framesApplied: totals.framesApplied,
        cellsObserved: totals.cellsObserved,
        numbersRead: totals.numbersRead,
        applied: totals.applied,
        skippedManual: totals.skippedManual,
        unknownCells: totals.unknownCells,
        gated: totals.gated,
        imagePipeline: pipelineDiag,
        // Reconcile the per-frame adapter note against the session-cumulative
        // numbersRead so the panel never shows "No Pokédex numbers visible"
        // next to a positive read count.
        note: resolveDiagnosticNote({
          numbersRead: totals.numbersRead,
          applied: totals.applied,
          frameNote: result.note,
          regionReason: gridFrame.regionReason,
        }),
        regionReason: gridFrame.regionReason,
        pipObscuredCells: gridFrame.pipObscuredCells,
      };
      publishDexScanDiagnostics(diag);

      // PiP messaging keyed on the session totals so it doesn't whiplash
      // on a single bad frame.
      const phase = phaseRef.current;
      // This frame cleared the pre-OCR gate as a dex-coloured frame and a dex
      // page-colour family is committed, so we ARE on a recognised Pokémon GO
      // dex page even when the frame was held before any number committed. Tell
      // pipPhaseFromTotals so a held frame coaches "scroll" instead of the
      // misleading "open a dex page".
      // Round-12 Fix 3 — this frame cleared the motion gates (it reached OCR), so
      // the motion signal is false; the low-capture signal carries the running
      // read-but-never-confirm streak. Fold a shown prompt onto the base phase.
      const hs = observeHoldSteady(false, holdSteadyLowCaptureRef.current);
      const pipPhase = holdSteadyPipPhase(
        pipPhaseFromTotals(
          totals,
          phase,
          isDexPageColourFamily(gridFrame.pageColourFamily ?? null),
        ),
        hs.shown,
        hs.reason,
      );
      // b13c.7 — when this frame committed entries, show WHICH dex slice was
      // written: "+4 to Hoenn / Perfect (252,253,254,255)". Region/variant come
      // from the last commit's dexId-derived write key (the single source of
      // truth), not the classifier region. Falls back to the running total when
      // no commit context is available yet.
      const commit = frameApply.applied > 0 ? totals.lastCommit : null;
      const newAddCount = newAddsRef.current.size;
      const baseDetail = commit
        ? `+${commit.ids.length} to ${commit.regionLabel} / ${variantPipLabel(commit.variant)} (${commit.ids.join(",")})`
        : newAddCount > 0
          ? `${newAddCount} dex entr${newAddCount === 1 ? "y" : "ies"} updated`
          : undefined;

      // ── PiP gap guidance ──────────────────────────────────────────────────
      // Turn the raw signals into "where are the gaps / which way to scroll /
      // how far to go" copy. DISPLAY-ONLY: like the telemetry, this reads the
      // store + frame signals but never touches a gate/veto/apply decision.
      const guidanceVariant = liveDexVariantRef.current;
      let guidance: ReturnType<typeof computeDexScanGuidance> | null = null;
      // The coaching reason this frame produced, recorded onto the committed
      // guidance line so a later still-frame can tell technique copy apart from
      // ordinary gap guidance and release it (see releaseCoachingOnStillFrame).
      let frameCoaching: CoachingReason = null;
      if (guidanceVariant != null && totals.numbersRead > 0) {
        const region = trustedResult.region ?? "unknown";
        // Recompute the gap set whenever anything it depends on moved: the
        // variant / region, the store (getDexStoreVersion bumps on every
        // write, so this can never go stale between commits the way the old
        // commit-keyed cache did) or the HUD's Added list. Otherwise reuse:
        // the full-dex walk was ~1ms on a desktop and a good deal more on the
        // Galaxy, every frame (2026-09-22).
        const targetsKey = `${guidanceVariant}:${region}:${getDexStoreVersion()}:${getDexRecentAdds().length}`;
        const cachedTargets = scanTargetsCacheRef.current;
        const targets =
          cachedTargets && cachedTargets.key === targetsKey
            ? cachedTargets.targets
            : scanTargetsForVariant(guidanceVariant, region);
        scanTargetsCacheRef.current = { key: targetsKey, targets };
        // Belt-and-braces: never offer a number as "next" that the HUD's own
        // Added grid is already showing as applied. Even if a store read lags a
        // commit, the two panels can then never contradict each other on screen.
        const shownAsAdded = new Set(getDexRecentAdds());
        const freshMissing = targets.missingIds.filter((id) => !shownAsAdded.has(id));
        const leadId = frameIds.length > 0 ? Math.min(...frameIds) : null;
        let scrollDir: ScrollDir = "none";
        if (leadId != null && lastLeadIdRef.current != null) {
          if (leadId > lastLeadIdRef.current) scrollDir = "down";
          else if (leadId < lastLeadIdRef.current) scrollDir = "up";
        }
        if (leadId != null) lastLeadIdRef.current = leadId;
        // Coaching reason for this frame — see decideCoaching for the static-vs-
        // moving split and the 2026-08-10 cap that stops a wedged low-confidence
        // run pinning "ease the scroll" on a screen that is not moving.
        //
        // REMOVED 2026-07-23 — the `hud_too_big` coaching. The trigger was
        // `obscured > (cellsObserved + obscured) / 2`, a majority of the frame's
        // cells, which sounds conservative but is not: `cellsObserved` collapses
        // on exactly the frames that are already hard (a blurred or mid-scroll
        // frame observes few cells), so the ratio crossed constantly even at the
        // PiP's smallest size and flashed a red "Shrink this window" through
        // whole scans. It also asked the user to fix our window for us. The
        // overlay geometry guard still runs and still protects the write path;
        // it just no longer editorialises. `result.overlayObscuredCells` remains
        // in telemetry if we want to measure real coverage properly.
        // A frame that just COMMITTED rows is proof the scan is reading fine
        // (2026-08-31 device report: "Too blurry to read" pinned itself over a
        // Perfect scan that was landing rows the whole time — those sparse
        // dexes read few numbers per frame, which kept confidence low). A
        // commit ends any blur episode outright: reset the streak, coach
        // nothing this frame, and release an already-committed coaching line.
        if (frameApply.applied > 0) {
          blurryCoachingFramesRef.current = 0;
          releaseCoachingOnStillFrame();
        }
        const coachingDecision = decideCoaching({
          lowConfidence: pipPhase === "low_confidence",
          admitWasStatic: admitWasStaticRef.current,
          holdSteadyShown: hs.shown,
          blurryFrames: blurryCoachingFramesRef.current,
        });
        blurryCoachingFramesRef.current = coachingDecision.blurryFrames;
        frameCoaching = frameApply.applied > 0 ? null : coachingDecision.coaching;
        guidance = computeDexScanGuidance({
          variantLabel: variantPipLabel(guidanceVariant),
          variantResolved: true,
          // Off the grid the brain gets its pre-grid inputs ("Show a Pokédex
          // or Pokémon screen"); on it, THIS frame's reads, not the session
          // total, which once non-zero kept gap coaching alive on every screen.
          basePhase: offGrid ? "scanning" : pipPhase,
          numbersRead: offGrid ? 0 : Math.max(1, frameReadCount),
          missingIds: freshMissing,
          caught: targets.total - freshMissing.length,
          scanned: scannedIdsRef.current.size,
          onScreenIds: frameIds,
          scrollDir,
          coaching: frameCoaching,
        });
      }
      // Let the guidance drive the copy while we're actively reading (grid,
      // held-for-confirm, or low-confidence — where the reason-based coaching
      // beats a blanket "hold steady"), or when the dex is complete. Pre-grid /
      // off-dex / saved phases keep their existing directive copy. Direction
      // rides as a ↑/↓ prefix on the detail line — the scanning HUD has no arrow
      // glyph, so no native change is needed for it.
      const useGuidance =
        guidance != null &&
        (pipPhase === "reading_grid" ||
          pipPhase === "hold_steady" ||
          pipPhase === "low_confidence" ||
          guidance.complete);

      // Debounce the guidance line so per-frame signal flicker never reaches the
      // HUD (see guidanceTextRef). A new message must persist as the candidate for
      // GUIDANCE_TEXT_MIN_MS before it replaces what's shown; a genuine scroll-
      // direction change is promoted immediately.
      let heldMessage: string | undefined;
      let heldDetail: string | undefined;
      let heldBubble: "green" | "yellow" | "orange" | "red" | undefined;
      if (useGuidance && guidance) {
        const now = Date.now();
        const nextMessage = guidance.message;
        const nextDetail = guidance.detail
          ? `${ARROW_PREFIX[guidance.arrow]}${guidance.detail}`
          : undefined;
        const prev = guidanceTextRef.current;
        const commit = () => {
          guidanceTextRef.current = {
            at: now,
            message: nextMessage,
            detail: nextDetail,
            arrow: guidance!.arrow,
            bubble: guidance!.bubble,
            coaching: frameCoaching,
          };
          guidancePendingRef.current = null;
        };
        // A real change of scroll direction (down ⇄ back) is a user action, not
        // signal noise — show it at once so the arrow never lags the scroll.
        const isDir = (a: GuidanceArrow) => a === "scroll-down" || a === "back";
        const directionChanged =
          prev != null && isDir(prev.arrow) && isDir(guidance.arrow) && prev.arrow !== guidance.arrow;
        // Coaching ENDING is promoted immediately (2026-08-31): the debounce
        // exists to stop coaching APPEARING on a flicker, but a committed
        // coaching line has arrow "hold", so the direction bypass could never
        // fire and the only exit was a 1400ms identical-candidate run — which
        // the gap copy's alternating down/continue wording kept resetting.
        // That asymmetry is exactly the reported "Too blurry got stuck".
        const coachingEnded = prev != null && prev.coaching != null && frameCoaching == null;
        if (prev == null || directionChanged || coachingEnded) {
          commit();
        } else if (nextMessage === prev.message) {
          // Same instruction — refresh the sub-line + bubble in place (the ↑/↓
          // prefix or bubble colour may have shifted) without disturbing the
          // debounce clock, and drop any stale pending candidate.
          guidanceTextRef.current = { ...prev, detail: nextDetail, arrow: guidance.arrow, bubble: guidance.bubble };
          guidancePendingRef.current = null;
        } else {
          // A different message is only a CANDIDATE. Promote it once the SAME
          // candidate has persisted for the debounce window; a candidate that
          // keeps changing resets the clock, so a flicker never wins and the copy
          // settles on whatever the scan is steadily reporting.
          const pending = guidancePendingRef.current;
          if (pending == null || pending.message !== nextMessage) {
            guidancePendingRef.current = { message: nextMessage, since: now };
          } else if (now - pending.since >= GUIDANCE_TEXT_MIN_MS) {
            commit();
          }
        }
        const held = guidanceTextRef.current!;
        heldMessage = held.message;
        heldDetail = held.detail;
        heldBubble = held.bubble;
      }

      // DETAIL MODE OWNS THE TEXT (2026-07-21). The detail probe only runs on
      // frames that earn it (`shouldProbeDetail`); every frame in between falls
      // through to HERE with the detail HUD still up, and used to overwrite the
      // lines with grid-scan copy while the variant grid stayed underneath — the
      // device screenshot of "↓ XXS · Looking for: #1 #2" stacked on Charizard's
      // variant cards.
      //
      // Re-ASSERT the WHOLE detail payload (via buildDetailReassert), not just the
      // two text lines. The native bridge is not reliably sticky for the detail
      // fields on Android (`detailSpecies` / `detailVariantSlots` / `ctaText` must
      // arrive each frame or the HUD falls back to the stale dex-number grid), so
      // an in-between frame that omitted them dropped the species page's variant
      // grid back to grid-scan numbers. Sending the full payload every detail
      // frame keeps the species + variant grid (Pro read or Basic tease) pinned
      // until a frame is genuinely not a detail page.
      // The caught-Pokémon card owns the text the same way the species card
      // does (2026-09-04): a rename / keyboard frame between two Pokémon fell
      // through to here and repainted the headline as grid copy, which the
      // iOS renderer draws at up to 36pt with no sprite.
      const detailOwnsText = detailReassertActive() || myPokemonReassertActive();
      if (detailReassertActive()) {
        void setDexPipPhase({ ...buildDetailReassert(), appliedCount: newAddCount });
      } else if (detailOwnsText) {
        void setDexPipPhase({ ...buildMyPokemonReassert(), appliedCount: newAddCount });
      } else {
        void setDexPipPhase({
          phase: pipPhase,
          // bug3 Part 4 — show the live dex type ("Scanning XXL Dex") on the
          // scanning HUD; undefined for non-scanning phases keeps their canonical
          // copy (hold steady / low confidence / saved) unchanged.
          message: useGuidance
            ? heldMessage!
            : dexScanningHudMessage(pipPhase, liveDexVariantRef.current),
          detail: useGuidance
            ? heldDetail
            : debugOverlayDetail({
                frameTs: frame.ts || Date.now(),
                ocr: `${result.region}/${result.mode} n=${totals.numbersRead}`,
                baseDetail,
              }),
          bubble: useGuidance ? heldBubble : undefined,
          hideRecent: offGrid,
          // R14 FIX 2 — the DISTINCT applied row count (same source as the
          // user-facing "N dex entries updated" line above), NOT totals.applied
          // (which sums per-frame writes incl. re-confirmations) or any cumulative
          // override/observation tally. Display-data only.
          appliedCount: newAddCount,
        });
      }

      pipActiveRef.current = pipDiag?.isActive === true;
      setState((prev) => ({
        ...prev,
        totals,
        lastDiag: diag,
        pipActive: pipDiag?.isActive === true,
      }));

    } catch (err) {
      // OCR failures on a single frame are non-fatal — we keep listening.
      // eslint-disable-next-line no-console
      console.warn("[useDexLiveScan] frame OCR failed", err);
    } finally {
      inFlightRef.current = Math.max(0, inFlightRef.current - 1);
      // scan-speed 2026-06-30 — record this frame's wall-clock cost exactly once,
      // on success AND failure (a thrown frame still consumed real time). totalMs
      // spans from after the cheap gates to here; ocrMs is 0 if the extractor was
      // never reached. Pure bookkeeping — never touches a write or gate.
      scanTimingRef.current.noteFrame({
        startMs: frameStartMs,
        totalMs:
          (typeof performance !== "undefined" ? performance.now() : Date.now()) -
          frameStartMs,
        ocrMs: frameOcrMs,
        blurDropped: frameBlurDropped,
        ocrRois: frameOcrRois,
        yieldMs: frameYieldMs,
        committedCount: frameCommittedCount,
      });
      // Mirror the inFlightRef decrement: clear the dedup in-flight marker and
      // adopt this frame's hash as the most-recent-completed fingerprint so the
      // next identical dwell frame is suppressed. Runs on success AND failure —
      // a frame whose OCR threw still had its content read, so re-running the
      // identical bytes would just throw again.
      if (dedup && dedupDecision) dedup.markDone(dedupDecision.hash, frameProductive, frameVariantHint);
    }
  }, [getActiveDexVariant, scanVariantInput]);

  /**
   * Auto-prepare + start the PiP window so it is already floating when
   * the user swaps to Pokémon GO. Runs inside the Live-Scan button tap
   * (a user gesture), which iOS requires for PiP. Never throws: PiP is a
   * convenience layer, so a failure is recorded honestly and the scan
   * continues. Returns the reason on failure so diagnostics can show it.
   */
  const autoStartPip = useCallback(async (): Promise<{ ok: boolean; reason?: string }> => {
    try {
      // eslint-disable-next-line no-console
      console.log("[live-scan] autoStartPip → preparePip()");
      const prep = await preparePip().catch((e) => ({ prepared: false, reason: String(e) }));
      // eslint-disable-next-line no-console
      console.log("[live-scan] preparePip result", prep);
      // eslint-disable-next-line no-console
      console.log("[live-scan] autoStartPip → startPip()");
      const res = await startPip();
      // eslint-disable-next-line no-console
      console.log("[live-scan] startPip result", res);
      if (res.started) return { ok: true };
      return { ok: false, reason: res.reason ?? "PiP did not start" };
    } catch (err) {
      const reason = err instanceof Error ? err.message : String(err);
      // eslint-disable-next-line no-console
      console.warn("[live-scan] autoStartPip threw", reason);
      return { ok: false, reason };
    }
  }, []);

  // ── Start / stop lifecycle ─────────────────────────────────────────
  const start = useCallback(async (reason: string = "unspecified") => {
    // Dex writes coalesce while the scan runs (lib/dexStateStore); stop flushes.
    setScanWriteCoalescing(true);
    // eslint-disable-next-line no-console
    console.log("[live-scan] start() called, phase=", phaseRef.current, "reason=", reason);
    if (phaseRef.current === "scanning" || phaseRef.current === "starting") {
      // eslint-disable-next-line no-console
      console.log("[live-scan] start() ignored, already", phaseRef.current);
      return;
    }
    handedOffToGoRef.current = false;
    frameSizeLoggedRef.current = false;
    installAppActiveTracker();
    storageBoxLatchRef.current = false;
    setPhase("starting", {
      error: undefined,
      totals: null,
      lastDiag: null,
      pipError: null,
      broadcastPhase: "starting",
      pendingFallback: null,
      reviewSummary: null,
      conflicts: [],
    });
    // b17a — honor the "Auto-update caught from scan" setting. When OFF the scan
    // must apply NOTHING additively to the store (pure preview), so we inject a
    // no-op writer into the session. Gap suggestions still work — the not-caught
    // collection lives in `ingest` and is independent of the apply path. We read
    // the setting once at session start; toggling mid-scan does not retroactively
    // change a run, which matches the device-local one-tap-scan model.
    const autoUpdateCaught = getDexScanSettings().autoUpdateCaught;
    scanIdRef.current = newScanId();
    // Open the scan-history session BEFORE the first frame can be ingested. From
    // here until stop() every collection-changing dex write — grid, detail page,
    // and the self-correction ratchet alike — is attributed to this run, because
    // the session installs a recorder on dexStateStore rather than asking each
    // apply site to remember to log.
    beginScanSession(scanIdRef.current, "live");
    sessionRef.current = createDexLiveScanSession(
      autoUpdateCaught
        ? undefined
        : { apply: () => ({ applied: 0, skippedManual: 0 }) },
    );
    // Y6 perf: clear the per-cell content cache so a prior session's pixel
    // hashes can never be replayed against this fresh scan. The cache also
    // self-clears on a variant change inside extractPokedexFrame; this is the
    // session-boundary reset.
    if (!ocrModuleRef.current) {
      ocrModuleRef.current = await import("@/lib/pokedexOcr");
    }
    ocrModuleRef.current.resetCellContentCache();
    // Advanced image pipeline session (structured JSON + registration +
    // crops). The OCR extractor is shared per-frame in processFrame, so the
    // injected `extractFrame` here is only a fallback and lazy-imports.
    pipelineRef.current = createDexPipelineRunner({
      extractFrame: async (dataUrl: string) => {
        if (!ocrModuleRef.current) {
          ocrModuleRef.current = await import("@/lib/pokedexOcr");
        }
        return ocrModuleRef.current.extractPokedexFrame(dataUrl);
      },
    });
    // b17d Fix 3 — fresh region latch per session so a prior session's stable
    // region can't leak into a new scan.
    regionStabilizerRef.current = new DexRegionStabilizer();
    // Latency hardening — fresh scan-context tracker per session so a prior
    // scan's locked region/variant can never bridge into a new scan.
    scanContextRef.current = createScanContextTracker();
    lastPillVariantRef.current = null;
    lastPageColourFamilyRef.current = null;
    // Push 11 — fresh pre-context top-row buffer per session so buffered rows
    // from a prior scan can never promote into a new one.
    preContextRef.current = createPreContextBuffer();
    // b17u — fresh scroll-stability baseline per session so the first frame of
    // a new scan is never diffed against a stale prior-session frame.
    prevStabilityProfileRef.current = null;
    scrollGateSkippedRef.current = 0;
    scrollGatePassedRef.current = 0;
    // scan-speed — fresh duplicate-frame suppressor per session so a prior
    // scan's last dwell frame can't suppress the first frame of a new scan.
    frameDedupRef.current = createFrameDedup();
    // Scanner integrity: clear the unreleased-write rejection log so its count
    // reflects THIS session (the write guard in dexStateStore records a flag for
    // every scan read of a species not released in GO — a mis-map signal).
    resetRejectedUnreleasedWrites();
    // Round-12 Fix 3 — fresh hold-steady detector + low-capture counters per
    // session so a prior scan's motion/capture history can't pre-trip the prompt.
    holdSteadyRef.current = createHoldSteadyDetector();
    holdSteadyReadWithoutApplyRef.current = 0;
    holdSteadyLowCaptureRef.current = false;
    // Round-13 Fix B — fresh present-id baseline so a prior scan's last frame
    // can't be misread as scroll motion on this scan's first frame.
    prevPresentIdsRef.current = new Set();
    // E49 — fresh on-device scan-pipeline telemetry per session.
    resetScanPipelineTelemetry();
    // Vision-only trial: no pre-OCR gate state / counters / page-identity cache
    // / colour-family latch to reset — those were all removed with the gate.
    // bug3 Part 4 — fresh live dex type so the HUD starts at "Open your Pokédex".
    liveDexVariantRef.current = null;
    // b18g — reset coverage tracking for new session.
    frameIdHistoryRef.current = [];
    allSeenIdsRef.current = new Set();
    // Fresh committed-new-add accumulator so the toast count starts at 0.
    newAddsRef.current = new Set();
    // scan-speed 2026-06-30 — fresh wall-clock timing recorder per session.
    scanTimingRef.current.reset();
    scanTimingLoggedRef.current = false;
    consecutiveGapCountRef.current = 0;
    steerTargetRowRef.current = null; // PR-E60
    if (nudgeTimerRef.current) {
      clearTimeout(nudgeTimerRef.current);
      nudgeTimerRef.current = null;
    }

    // Reset PiP HUD copy so a returning user doesn't see stale text.
    // PR AA: also clear the rolling dex grid so the previous session's
    // numbers don't carry into a fresh scan.
    resetDexRecentAdds();
    // Drop the gap-guidance caches so a fresh scan (e.g. after the user clears a
    // dex) recomputes targets rather than pointing at last session's gaps.
    scanTargetsCacheRef.current = null;
    lastLeadIdRef.current = null;
    guidanceTextRef.current = null;
    guidancePendingRef.current = null;
    scannedIdsRef.current = new Set();
    sawPokedexRef.current = false;
    selfCorrectionRef.current.reset();
    // Detail-page state is per-session: the stillness counter, the one-probe-
    // per-dwell latch and the applied set must not leak into the next scan, or a
    // species read last session would be skipped as "already applied".
    detailGateRef.current.reset();
    lastDetailSlotsRef.current = [];
    detailHudActiveRef.current = false;
    lastDetailSpeciesRef.current = null;
    lastDetailMessageRef.current = DETAIL_HUD_MESSAGE;
    detailReleaseMissesRef.current = 0;
    // My-Pokémon page state is per-session for the same reason.
    myPokemonHudActiveRef.current = false;
    lastMyPokemonHeadlineRef.current = null;
    lastMyPokemonSlotsRef.current = [];
    lastMyPokemonLeagueBadgeRef.current = null;
    lastMyPokemonPvpLineRef.current = null;
    lastMyPokemonEvoLineRef.current = null;
    lastMyPokemonTargetRef.current = null;
    lastMyPokemonHeroLinesRef.current = [];
    lastMyPokemonMovePillsRef.current = [];
    myPokemonHudLatchRef.current = { key: "?", cp: null, hpMax: null, speciesId: null, displayName: null };
    lastMyPokemonMessageRef.current = MYPOKE_HUD_MESSAGE;
    myPokemonReleaseMissesRef.current = 0;
    lastMyPokemonSightingRef.current = null;
    myPokemonActiveRecordIdRef.current = null;
    myPokemonCommittedKeysRef.current = new Set();
    myPokemonCopiedKeysRef.current = new Set();
    myPokemonReadsRef.current = 0;
    myPokemonSavesRef.current = 0;
    await clearDexPipClinical();
    // Clear any detail layout from a previous session so the HUD opens on the
    // dex-number grid, never a stale species line.
    await setDexPipPhase({
      phase: "open_pokedex",
      detail: "Live OCR ready",
      detailSpecies: undefined,
      detailVariantSlots: [],
      // A session opens on the landscape card.
      orientation: markHudOrientation("landscape"),
      heroLines: [],
      leagueBadge: "",
      pvpLine: "",
      evoLine: "",
              targetSpriteUrl: "",
              targetLeague: "",
              targetRank: 0,
              targetName: "",
              targetPercent: "",
              targetAura: "",
              targetLeagues: [],
              movePills: [],
ivPercent: 0,
              hudFocus: "" as const,
              moveRecs: [],
              ...showcaseHudFields(null),
              encounterCp: 0,
      ctaText: "",
    });

    // eslint-disable-next-line no-console
    console.log("[live-scan] checking canAutoStartBroadcast()…");
    const available = await canAutoStartBroadcast();
    // eslint-disable-next-line no-console
    console.log("[live-scan] canAutoStartBroadcast =", available);
    if (!available) {
      // No native broadcast plugin available (web preview or stale
      // binary). Surface honestly; do not silently degrade.
      setPhase("error", {
        error: "Live scan requires the Nexus Dex mobile app.",
        broadcastPhase: "inactive",
      });
      sessionRef.current = null;
      return;
    }
    // ADOPT A LIVE BROADCAST (2026-09-24 owner report: the menu offered Start
    // while a scan's broadcast was still running, and Start then failed). The
    // app's scan state can end while the system broadcast carries on: the web
    // view reloaded in the background, or a Stop & save sheet dismissed
    // without stopping. Tapping the picker on a live broadcast presents its
    // Stop sheet, not Start, so the "start" either stopped the broadcast or
    // left the trainer on a sheet that made no sense. A broadcast iOS says is
    // live is exactly what this session needs: attach to it, skip the picker.
    const adoptLiveBroadcast = getPlatform() === "ios" && (await isBroadcastLive()) === true;
    if (adoptLiveBroadcast) {
      // eslint-disable-next-line no-console
      console.log("[live-scan] broadcast already live, adopting it (no picker)");
    }

    // Bring up the PiP HUD. The live-scan HUD is on-screen for the lifetime of
    // this session (the scan owns it), so mark the overlay active from session
    // start — this makes the pre-OCR gate exclude the parked PiP slab from frame
    // one, even before the native PiP diagnostics report isActive (which only
    // happens after the first frame is OCR'd). Without this the gate stays stuck
    // in SEARCHING — see pipOverlayActiveRef. The launch itself is platform-split
    // below: iOS starts the window here; Android defers to the native capture
    // service so the HUD is not pre-flighted before screen permission exists.
    pipOverlayActiveRef.current = true;
    if (getPlatform() === "android") {
      // 2026-07-29: on Android the HUD is a system overlay that the native
      // ScreenCaptureService raises via NexPip3D.showFromBroadcast() the MOMENT
      // MediaProjection permission is granted (see ScreenCaptureService
      // .onStartCommand). Calling startPip() HERE — before the permission dialog
      // that startBroadcast() below triggers — only produces a premature "overlay
      // permission not granted" failure and the misleading yellow "HUD didn't
      // auto-start" banner, even though the HUD comes up correctly seconds later.
      // So we let the native capture lifecycle own the launch and never pre-flight
      // it from JS. We still run preparePip() to REQUEST the overlay permission up
      // front (its only job on Android), but discard its result so no pipError is
      // raised. pipActive flips true once the native diagnostics report it.
      await preparePip().catch(() => null);
      pipActiveRef.current = false;
      setState((prev) => ({ ...prev, pipActive: false, pipError: null }));
    } else {
      // iOS: start the PiP window up front (still inside the tap gesture) so it is
      // live before the user moves to Pokémon GO. A failure must NOT abort the
      // scan — record it and carry on.
      const pip = await autoStartPip();
      pipActiveRef.current = pip.ok;
      // eslint-disable-next-line no-console
      console.log("[live-scan] PiP started =", pip.ok, pip.reason ?? "");
      setState((prev) => ({
        ...prev,
        pipActive: pip.ok,
        pipError: pip.ok ? null : pip.reason ?? "PiP unavailable",
      }));
    }

    try {
      const startedAt = Date.now();
      // 1. Drain any stale frames left from a previous session.
      await broadcastBridge.clearFrames().catch(() => 0);

      // 2. JS-bridge frame listener (Android native emit / Darwin notification).
      //    Android backpressure: ack the native capture service once THIS frame
      //    is fully processed so it emits the next one. `.finally` guarantees the
      //    ack fires for every received frame — including those processFrame
      //    drops early (dedup/phase/blur) — so the demand-driven handshake never
      //    deadlocks. `requestNextFrame` is a no-op on iOS (pull-based poller).
      // Frame cadence in the exported log (2026-09-23 Discord stall report):
      // a gap longer than FRAME_GAP_LOG_MS between delivered frames is logged
      // with the page's visibility, so a stalled capture is told apart from a
      // still screen or a backgrounded WebView.
      lastFrameAtRef.current = 0;
      const FRAME_GAP_LOG_MS = 5000;
      // STALL WATCHDOG (2026-09-25 Galaxy pass: "stuck at row 133, then 200",
      // unstuck by switching apps). The capture service emits only while it
      // holds a credit, and a credit is returned when a frame finishes; a
      // native OCR call that hangs (64 s, 220 s in the log) held both credits
      // and nothing arrived until the visibility handler happened to re-ack.
      // The OCR timeout (ocrEngine) frees the frame; this re-acks whenever no
      // frame has arrived for a while, so the loop can never wait on nothing.
      if (getPlatform() === "android") {
        if (frameWatchdogRef.current) clearInterval(frameWatchdogRef.current);
        let watchdogTicks = 0;
        frameWatchdogRef.current = setInterval(() => {
          if (phaseRef.current !== "scanning") return;
          // Heartbeat while hidden: a stall with no heartbeat means the
          // WebView's timers are not running at all behind GO (throttled or
          // frozen), one with heartbeats means the capture went quiet.
          if (++watchdogTicks % 5 === 0 && typeof document !== "undefined" && document.visibilityState === "hidden") {
            // eslint-disable-next-line no-console
            console.log("[live-scan] watchdog alive", { sinceFrameMs: Date.now() - lastFrameAtRef.current, inFlight: inFlightRef.current });
          }
          const last = lastFrameAtRef.current;
          if (last > 0 && Date.now() - last > 8000) {
            // eslint-disable-next-line no-console
            console.log("[live-scan] frame watchdog re-ack", { sinceMs: Date.now() - last, inFlight: inFlightRef.current });
            void requestNextFrame();
          }
        }, 3000);
      }
      clearDexFrameDump();
      frameListenerRef.current = await onFrame((frame) => {
        const now = Date.now();
        const lastFrameAt = lastFrameAtRef.current;
        if (lastFrameAt > 0 && now - lastFrameAt > FRAME_GAP_LOG_MS) {
          // eslint-disable-next-line no-console
          console.log("[live-scan] frame gap", {
            gapMs: now - lastFrameAt,
            visibility: typeof document !== "undefined" ? document.visibilityState : null,
            inFlight: inFlightRef.current,
            phase: phaseRef.current,
          });
        }
        lastFrameAtRef.current = now;
        void processFrame(frame).finally(() => {
          void requestNextFrame();
        });
      });
      // Coming back to the foreground re-arms the capture handshake: if an ack
      // was lost while the WebView was hidden, this is the trainer's "switch
      // back to Nexus" repaired in code. Harmless when nothing was lost.
      visibilityListenerRef.current = () => {
        if (typeof document === "undefined") return;
        // eslint-disable-next-line no-console
        console.log("[live-scan] visibility", { state: document.visibilityState, phase: phaseRef.current });
        if (document.visibilityState === "visible" && phaseRef.current === "scanning") void requestNextFrame();
      };
      if (typeof document !== "undefined") {
        document.addEventListener("visibilitychange", visibilityListenerRef.current);
        // Page Lifecycle: Chromium can freeze a hidden page outright; the
        // freeze/resume pair in the export says whether that is the stall.
        for (const ev of ["freeze", "resume", "pagehide", "pageshow"]) {
          document.addEventListener(ev, lifecycleListenerRef.current);
          window.addEventListener(ev, lifecycleListenerRef.current);
        }
      }
      // eslint-disable-next-line no-console
      console.log("[live-scan] frame listener + App Group poller attached");

      // 3. Disk-backed App Group poller — the path that actually
      //    delivers reliably while iOS has backgrounded our host app.
      pollerRef.current = createAppGroupFramePoller({
        sessionStartMs: startedAt,
        // 2026-07-22 scan-speed: poll at the extension's new 2fps write cadence
        // (SampleHandler.writeInterval 1.0→0.5). Per-frame JS cost is ~482ms mean
        // after the OCR-encode fix, so 2fps is sustainable; the isBusy guard skips
        // a tick whenever a frame overruns, so this self-throttles and never piles
        // up. This is what feeds the evidence engine enough frames/cell to commit
        // ambiguous cells at a normal scroll speed.
        tickMs: 500,
        maxPerTick: 2,
        isBusy: () => inFlightRef.current >= 2,
        onFrame: (frame) => {
          void processFrame(frame);
        },
      });
      pollerRef.current.start();

      // 4. Lifecycle: if the user stops the iOS broadcast from the
      //    system UI, finalise the session ourselves.
      finishedListenerRef.current = await onFinished((data) => {
        // eslint-disable-next-line no-console
        console.log("[live-scan] broadcast finished", {
          reason: data?.reason ?? "extension",
          phase: phaseRef.current,
        });
        void finish();
      });

      // 5. Trigger the iOS broadcast picker. This is what shows the
      //    "Start Broadcast" sheet; the user has to confirm. Skipped when a
      //    broadcast is already live (adoptLiveBroadcast above).
      // eslint-disable-next-line no-console
      if (!adoptLiveBroadcast) console.log("[live-scan] startBroadcast(), presenting picker…");
      const trig = adoptLiveBroadcast ? { triggered: true } : await startBroadcast();
      // eslint-disable-next-line no-console
      console.log("[live-scan] startBroadcast triggered =", trig.triggered);
      if (!trig.triggered) {
        await stop(true);
        setPhase("error", {
          error: "Broadcast picker did not start.",
          broadcastPhase: "inactive",
        });
        return;
      }
      setPhase("scanning", { active: true, broadcastPhase: "active" });
      await setDexPipPhase({ phase: "open_pokedex" });
    } catch (err: unknown) {
      const reason = err instanceof Error ? err.message : String(err);
      await stop(true).catch(() => null);
      setPhase("error", { error: reason, broadcastPhase: "inactive" });
    }
  }, [processFrame, setPhase, autoStartPip]);

  /**
   * Stop the active scan loop and tear down listeners. `silent` skips
   * advancing the phase so finalisation can route to "saved".
   */
  const stop = useCallback(async (silent: boolean = false, opts: { keepBroadcast?: boolean } = {}) => {
    setScanWriteCoalescing(false);
    // eslint-disable-next-line no-console
    console.log("[live-scan] stop() called, silent=", silent, "phase=", phaseRef.current);
    // Capture the scan phase BEFORE this function mutates any state (the
    // `broadcastPhase:"stopping"` write below and the terminal setPhase("idle")).
    // Only a phase that was actually live/starting a broadcast should re-tap the
    // iOS picker in step 2 — see the guard there.
    const priorPhase = phaseRef.current;
    // A broadcast is live/starting for scanning|starting, and also for finishing:
    // finish() sets phase "finishing" then calls stop(true), and it only reaches
    // that from a "scanning" session (guarded at finish's entry), so a real scan
    // still stops via the picker re-tap. Every other phase (idle/saved/error) is
    // a defensive no-op teardown and must NOT present the picker.
    const phaseSuggestsBroadcasting =
      priorPhase === "scanning" || priorPhase === "starting" || priorPhase === "finishing";
    // ASK iOS, don't guess. The JS phase alone was the root of BOTH halves of the
    // 2026-07-18 broadcast regression: it drifts to idle on a component remount,
    // so a genuinely-live broadcast was never stopped (orphaned red bar), and it
    // can also sit stale at "scanning" with nothing live, so stopBroadcast ran
    // and presented the picker on ordinary navigation ("every page link opens the
    // Start Broadcast sheet"). Native state is authoritative; the phase is only a
    // fallback for when we genuinely cannot know (web / bridge unavailable).
    const liveNow = await isBroadcastLive();
    // RESTART KEEPS THE BROADCAST (2026-09-24 owner report: "an error when
    // they press start"). The menu's Start from a finished or failed session
    // tears the old one down first, and when the broadcast was still live that
    // teardown re-tapped the picker and, four seconds later, the watchdog made
    // the extension end itself: iOS reports that as a stopped-broadcast error.
    // A restart wants the broadcast, so it leaves it running for start() to
    // adopt.
    const wasBroadcasting = opts.keepBroadcast ? false : (liveNow ?? phaseSuggestsBroadcasting);
    // eslint-disable-next-line no-console
    if (DEX_SCAN_VERBOSE) console.log("[live-scan] stop(), broadcast liveness", {
      priorPhase,
      phaseSuggestsBroadcasting,
      nativeSaysLive: liveNow,
      decidedBy: liveNow == null ? "phase-fallback" : "native",
      wasBroadcasting,
    });
    // Mark the broadcast as stopping so diagnostics reflect the request
    // even if the system teardown lags (or, on older binaries, never
    // fully completes — see broadcastStopNote).
    setState((prev) => ({ ...prev, broadcastPhase: "stopping" }));

    // 1. Tear down the JS-side frame producers first so no late frame
    //    sneaks into a session we're finalising.
    if (visibilityListenerRef.current && typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", visibilityListenerRef.current);
      visibilityListenerRef.current = null;
      for (const ev of ["freeze", "resume", "pagehide", "pageshow"]) {
        document.removeEventListener(ev, lifecycleListenerRef.current);
        window.removeEventListener(ev, lifecycleListenerRef.current);
      }
    }
    if (frameListenerRef.current) {
      await frameListenerRef.current.remove().catch(() => null);
      frameListenerRef.current = null;
    }
    if (finishedListenerRef.current) {
      await finishedListenerRef.current.remove().catch(() => null);
      finishedListenerRef.current = null;
    }
    if (frameWatchdogRef.current) {
      clearInterval(frameWatchdogRef.current);
      frameWatchdogRef.current = null;
    }
    if (pollerRef.current) {
      pollerRef.current.stop();
      try { await pollerRef.current.drain(); } catch { /* swallow */ }
      pollerRef.current = null;
    }
    // b17d Fix 3 — drop the region latch so the next session starts unlatched.
    regionStabilizerRef.current = null;
    // Latency hardening — drop the scan-context lock so the next session
    // re-acquires from scratch.
    scanContextRef.current = null;
    // Push 11 — drop the pre-context buffer so a new session starts empty.
    preContextRef.current = null;
    // b17u — drop the scroll-stability baseline so the next session starts fresh.
    prevStabilityProfileRef.current = null;
    // scan-speed — drop the duplicate-frame suppressor so a late frame after
    // teardown can't be matched against this session's last dwell, and the next
    // session starts with a clean fingerprint.
    frameDedupRef.current = null;
    // Round-12 Fix 3 — drop the hold-steady detector + counters so a late frame
    // after teardown can't drive the prompt and the next session starts fresh.
    holdSteadyRef.current = null;
    holdSteadyReadWithoutApplyRef.current = 0;
    holdSteadyLowCaptureRef.current = false;
    prevPresentIdsRef.current = new Set();
    // Vision-only trial: no page-identity cache to drop on teardown (removed).

    // 2. Re-tap the iOS broadcast picker so the user can hit Stop
    //    Broadcast in the system sheet. iOS then terminates the
    //    broadcast cleanly via broadcastFinished() — no banner, no
    //    orphaned red status bar. Matches PokeGenie's flow.
    //
    //    GUARD (2026-07-08): stopBroadcast() → native tapBroadcastPicker
    //    PRESENTS the iOS system broadcast sheet by design. Many UI paths call
    //    stop() defensively at phase=idle (menu mount/unmount, navigation
    //    re-mount), and firing the picker there is the "every menu button opens
    //    the Start Broadcast screen" bug (device log: `stop() called silent=true
    //    phase=idle` → picker presented with nothing broadcasting). Only re-tap
    //    the picker when a broadcast was actually live/starting. A no-op
    //    defensive teardown at idle/inactive still does the JS-side cleanup
    //    above/below — it just never presents the picker.
    if (wasBroadcasting) {
      // eslint-disable-next-line no-console
      if (DEX_SCAN_VERBOSE) console.log("[live-scan] stop(), invoking stopBroadcast (broadcast active)", { priorPhase });
      await stopBroadcast().catch(() => null);
    } else {
      // eslint-disable-next-line no-console
      if (DEX_SCAN_VERBOSE) console.log("[live-scan] stop(), skipping stopBroadcast (no active broadcast; no picker)", { priorPhase });
    }

    // 3. Clear any stale frames so a subsequent session can't ingest a
    //    backlog written between stop request and system teardown.
    await broadcastBridge.clearFrames().catch(() => 0);

    // 4. Auto-close PiP — the scan owns the PiP window lifecycle, so a
    //    stop/save must also tear the floating window down.
    await stopPip().catch(() => false);
    await clearDexPipClinical();
    pipActiveRef.current = false;
    pipOverlayActiveRef.current = false;

    setState((prev) => ({
      ...prev,
      pipActive: false,
      // Only the iOS `finished` notification can confirm the system broadcast
      // actually ended — see the broadcast-liveness observer above. When we
      // presented the picker (wasBroadcasting), the user still has to tap "Stop
      // Broadcast" in the sheet; until then the red bar is on, so we stay
      // "stopping" and let the observer flip us to "stopped" on confirmation.
      // Claiming "stopped" here was the desync bug: a user who dismissed the
      // sheet kept broadcasting while the menu reset to idle. The defensive no-op
      // teardown (nothing was live) is genuinely stopped.
      broadcastPhase: wasBroadcasting ? "stopping" : "stopped",
    }));

    // scan-speed 2026-06-30 — emit the wall-clock timing summary HERE (not only
    // in finish()) so it lands on EVERY session end: the natural finish() path
    // (which calls stop(true)) AND a direct stop()/cancel by the tester. Earlier
    // the snapshot only fired in finish(), so a scan ended by stopping produced
    // NO timing line — exactly the 20-40s-hold case we care about. Guarded to
    // fire once per session and only when frames were actually timed, so a bare
    // stop() with nothing scanned stays silent. Pure read; never affects a gate.
    if (!scanTimingLoggedRef.current && scanTimingRef.current.snapshot().framesTimed > 0) {
      scanTimingLoggedRef.current = true;
      scanTimingRef.current.logSnapshot();
      // Native OCR split for the export (2026-09-30): queue wait vs ML Kit
      // time, native deadline hits, stale skips and JS fence timeouts.
      const nativeOcr = drainNativeOcrStats();
      if (nativeOcr) {
        // eslint-disable-next-line no-console
        console.log("[dex-scan] native ocr", nativeOcr);
      }
      // The admin "OCR performance" panel (2026-09-09): one sample per live
      // scan RUN, not per frame. `reportScanPerf` and its scan_perf table
      // existed since the B10 cycle but nothing ever called it, so the panel
      // read "No OCR runs in window" for its whole life. A run's medians are
      // the honest unit: per-frame posts at 1fps would be a load problem and
      // would drown the p95 in dwell frames. Fire-and-forget, never blocks
      // teardown.
      //
      // `success` and `source` come from describeScanRun (2026-09-15): a run
      // spent on individual Pokémon pages saves My Pokémon records and ticks
      // the dex through the cross-write in finish(), which the grid counter
      // never sees. Reporting only `snap.committed` marked every such run a
      // failure, and the admin table listed working scanners as stuck.
      //
      // WHY it did not land (2026-10-15, lib/scanOutcomes): the signals are
      // what the HUD's coaching already tracks. sessionRef is nulled only in
      // start(), so its totals are still readable here; `silent` is true for
      // finish() and the unmount teardown, false for a restart from the menu.
      const snap = scanTimingRef.current.snapshot();
      const sessionTotals = sessionRef.current?.snapshot() ?? null;
      const run = describeScanRun({
        committed: snap.committed,
        myPokemonSaves: myPokemonSavesRef.current,
        platform: getPlatform(),
        signals: {
          wallMs: snap.sessionWallMs,
          numbersRead: sessionTotals?.numbersRead ?? 0,
          cellsObserved: sessionTotals?.cellsObserved ?? 0,
          cellsResolved: scannedIdsRef.current.size,
          sawPokedex: sawPokedexRef.current,
          sawPokemonPage: myPokemonReadsRef.current > 0 || lastDetailSpeciesRef.current != null,
          ocrCalls: nativeOcr?.calls ?? 0,
          ocrTimeouts: (nativeOcr?.nativeTimeouts ?? 0) + (nativeOcr?.jsTimeouts ?? 0),
          endedBy: silent ? "finish" : "stop",
        },
      });
      void reportScanPerf({
        source: run.source,
        engine: run.engine,
        imageSize: snap.framesTimed,
        ocrMs: Math.round(snap.ocrMs.p50),
        totalMs: Math.round(snap.frameMs.p50),
        success: run.success,
        outcome: run.outcome ?? undefined,
        // Where the frames went (scan_perf.ocr_frames / blur_drops): how many
        // reached the grid OCR and how many the blur gate dropped, so the
        // admin can tell a run that never saw the grid from one the OCR lost.
        ocrFrames: snap.ocrMs.frames,
        blurDrops: snap.blurDrops,
      });
    }

    // Reader self-heal — drain the high-confidence, multi-frame-confirmed blocked
    // Shadow/Purified reads and report each to the worker (which unions it into
    // the roster + emails an admin to verify). Fire-and-forget + non-throwing so
    // a report can never block teardown. sessionRef is nulled only in start(),
    // so it is still valid here.
    {
      const reports = sessionRef.current?.drainSelfHealReports() ?? [];
      for (const r of reports) {
        // eslint-disable-next-line no-console
        console.log("[dex-scan] self-heal report", r);
        void reportSelfHeal(r, apiRequestRaw);
      }
    }

    // Close the scan-history session here rather than in finish(): stop() is the
    // one teardown every path runs through — finish, an error bail, a remount —
    // so a run that ends badly still closes its log instead of leaving a
    // recorder installed to catch the NEXT scan's writes. A run that changed
    // nothing is dropped by the store, so this costs an empty card only when
    // there is something to show.
    if (scanIdRef.current) endScanSession(scanIdRef.current);

    if (!silent) {
      setPhase("idle", { active: false });
      await setDexPipPhase({ phase: "idle" });
    }
  }, [setPhase]);

  /**
   * Save the current session's totals into the diagnostic store as a
   * final "saved" snapshot. The session has been applying observations
   * to dexStateStore all along — finish is the moment we mark the run
   * over and surface a celebratory PiP message.
   */
  const finish = useCallback(async (): Promise<{
    totals: DexLiveSessionTotals | null;
    pendingFallback: { count: number; ids: number[] } | null;
    reviewSummary: DexScanReviewSummary | null;
    conflicts: DexReconciliationConflict[];
    /** My Pokémon session outcome, so the finish UI can say "3 saved to My
     * Pokémon" instead of "Nothing read yet" on an individual-page session. */
    myPokemon: { reads: number; writes: number; pokemonSeen: number };
  }> => {
    // eslint-disable-next-line no-console
    console.log("[live-scan] finish() called, phase=", phaseRef.current);
    // b18-coverage-fix: if a finish() is already in-flight, return the same
    // promise so all concurrent callers (user tap, onFinished listener, etc.)
    // resolve to the same value and setState fires only once.
    if (finishPromiseRef.current) {
      // eslint-disable-next-line no-console
      console.log("[live-scan] finish() called while in-flight, returning existing promise");
      return finishPromiseRef.current;
    }
    const myPokemonOutcome = () => ({
      reads: myPokemonReadsRef.current,
      writes: myPokemonSavesRef.current,
      pokemonSeen: myPokemonCommittedKeysRef.current.size,
    });
    // Owning a Pokémon proves the dex tick: any records this session added
    // (shiny/lucky/size flags included) mark their dex variants now.
    // Additive + no-ops when nothing is new — see myPokemonDexCrossWrite.
    if (myPokemonSavesRef.current > 0) {
      try {
        const dexTicks = crossWriteMyPokemonToDex(listMyPokemon());
        if (dexTicks.total > 0) {
          // eslint-disable-next-line no-console
          console.log(
            `[dex-scan] my-pokemon dex cross-write: ${dexTicks.total} cells`,
            dexTicks.byVariant,
          );
        }
      } catch {
        // Never let a dex bookkeeping failure break the finish flow.
      }
    }
    if (phaseRef.current !== "scanning")
      return {
        totals: null,
        pendingFallback: null,
        reviewSummary: null,
        conflicts: [],
        myPokemon: myPokemonOutcome(),
      };
    const promise = (async () => {
    setPhase("finishing");
    await stop(true);
    // Push 10b — flush the pipeline telemetry summary to the runtime console so
    // the acquire/observe/commit latency markers and the observe-vs-commit row
    // split land in an Xcode/TestFlight trace (grep `[dex-scan] telemetry`).
    sessionRef.current?.logTelemetrySnapshot();
    const totals = sessionRef.current?.snapshot() ?? null;
    // Effectiveness item 3 — fold the three telemetry surfaces (frame intake,
    // observe-vs-commit recorder, per-gate veto tallies) into one "scan truth"
    // funnel so a device trace answers "where did this scan break?" from a
    // single grep (`[dex-scan] scan-truth`). Pure read; never relaxes a gate.
    if (sessionRef.current && totals) {
      logScanTruthSummary({
        totals,
        telemetry: sessionRef.current.telemetrySnapshot(),
        pipeline: readScanPipelineTelemetry(),
        frameStats: getLastLiveScanFrameStats(),
      });
    }
    // Scanner integrity: if the write guard rejected any unreleased-species
    // writes this session, the scanner mis-mapped cells — surface it as a
    // one-line session flag on top of the per-write warnings.
    const rejectedUnreleased = getRejectedUnreleasedWrites();
    if (rejectedUnreleased.length > 0) {
      // eslint-disable-next-line no-console
      console.warn(
        `[dex-scan] scan-integrity: ${rejectedUnreleased.length} read(s) rejected as NOT released in ` +
          `Pokémon GO. The scanner mis-mapped these cells (dropped, not written): ` +
          rejectedUnreleased.map((r) => `${r.variant}#${r.dexNumber}`).join(", "),
      );
    }
    let pendingFallback: { count: number; ids: number[] } | null = null;
    let reviewSummary: DexScanReviewSummary | null = null;
    // b18g — declared here so both the if(totals) and else branches can assign it.

    // b17a — reconcile the session's stable "not caught" readings against the
    // user's manual "caught" marks to surface gaps. Gated on the "Suggest gaps"
    // setting; the chosen confidence profile sets the HIGH bar an un-catch
    // suggestion must clear. The engine is pure and reads the live store, so a
    // misread that never cleared the HIGH bar can never surface here.
    let conflicts: DexReconciliationConflict[] = [];
    {
      const scanSettings = getDexScanSettings();
      const session = sessionRef.current;
      if (scanSettings.suggestGaps && session) {
        conflicts = reconcileNotCaughtReadings(session.notCaughtReadings(), {
          profile: scanSettings.confidenceProfile,
        });
      }
    }
    if (totals) {
      let pipelineDiag: DexImagePipelineDiag | undefined;
      if (pipelineRef.current) {
        const d = pipelineRef.current.session.diagnostics;
        pipelineDiag = {
          region: pipelineRef.current.session.region,
          entriesScanned: d.entriesScanned,
          newRegistrations: d.newRegistrations,
          dedupedSkipped: d.dedupedSkipped,
          motionSkippedFrames: d.motionSkippedFrames,
          visibilitySkippedCells: d.visibilitySkippedCells,
          cropsCaptured: d.cropsCaptured,
          cropsUnavailable: d.cropsUnavailable,
          sessionJson: JSON.stringify(pipelineRef.current.toJson()),
        };
      }
      const diag: DexScanDiagnostics = {
        ts: Date.now(),
        source: "live",
        variant: totals.lastVariant,
        variantInferred: false,
        region: totals.lastRegion as DexScanDiagnostics["region"],
        mode: totals.lastMode,
        confidence: totals.lastConfidence,
        cellsObserved: totals.cellsObserved,
        numbersRead: totals.numbersRead,
        applied: totals.applied,
        skippedManual: totals.skippedManual,
        unknownCells: totals.unknownCells,
        gated: totals.gated,
        imagePipeline: pipelineDiag,
        note: resolveDiagnosticNote({
          numbersRead: totals.numbersRead,
          applied: totals.applied,
          frameNote: newAddsRef.current.size === 0
            ? "No new entries applied"
            : `${newAddsRef.current.size} entr${newAddsRef.current.size === 1 ? "y" : "ies"} saved`,
          regionReason: undefined,
        }),
      };
      publishDexScanDiagnostics(diag);
      // G-Max QA — emit the structured per-scan QA event for the backend monitor.
      // Fire-and-forget; derived purely from the totals snapshot + applied ids,
      // so it cannot affect the scan result or the gates above.
      reportGmaxQaScanEvent({
        scanId: scanIdRef.current ?? newScanId(),
        totals,
        appliedIds: sessionRef.current?.appliedIds() ?? [],
      });
      // Build 3 / v1.4.6 b3 — propose the end-of-session fallback when the scan
      // read real grid numbers but never resolved a confident variant (so the
      // per-frame path applied nothing). The menu confirms with the user before
      // we flush these to the Nexus-side filter. We do NOT propose a fallback if
      // a confident variant WAS resolved (its numbers already applied) or if
      // nothing was buffered.
      const session = sessionRef.current;
      const fallbackCount = session?.pendingFallbackCount() ?? 0;
      const needsFallback =
        !!session && totals.applied === 0 && !totals.resolvedVariant && fallbackCount > 0;
      pendingFallback = needsFallback
        ? { count: fallbackCount, ids: session!.pendingFallbackIds() }
        : null;
      // A session spent on individual caught-Pokémon pages buffers JUNK
      // fallback numbers: the 1-2 frames before each page latches fall through
      // to the grid extractor, which reads the page's stats ("52 / 52 HP",
      // candy counts) as dex numbers with no variant. The 2026-08-20 device
      // log ended such a session with "apply 7 entries?" over numbers that
      // were never dex cells. When the whole session produced ZERO confirmed
      // grid reads AND read caught-Pokémon pages, the buffered numbers are
      // page stats, not a filter-pill problem — drop the prompt.
      if (
        pendingFallback &&
        myPokemonReadsRef.current > 0 &&
        totals.numbersRead === 0
      ) {
        // eslint-disable-next-line no-console
        console.log("[dex-scan] pending fallback suppressed (my-pokemon session)", {
          droppedCount: pendingFallback.count,
          droppedIds: pendingFallback.ids,
          myPokemonReads: myPokemonReadsRef.current,
        });
        pendingFallback = null;
      }
      // My-Pokémon session outcome, so a device log states in one line whether
      // records landed and how many the device now holds.
      // eslint-disable-next-line no-console
      console.log("[dex-scan] my-pokemon session", {
        reads: myPokemonReadsRef.current,
        writes: myPokemonSavesRef.current,
        pokemonSeen: myPokemonCommittedKeysRef.current.size,
        deviceRecordsNow: myPokemonCount(),
      });
      // Build 13a — Review & Confirm: when the scan committed entries, build the
      // detected (region, variant, count) summary from the applied dex ids
      // (region derived from the ids, never header text) and the session's
      // locked variant. Shown to the user with an edit affordance before the
      // run is treated as final. A pure-fallback run (nothing applied, prompt
      // pending) skips the review — that path has its own confirm toast.
      if (session && totals.applied > 0) {
        reviewSummary = buildDexScanReviewSummary({
          ids: session.appliedIds(),
          variant: totals.lastVariant,
        });
      }
      // b18-coverage-fix: post-scan coverage check scoped to the active region.
      // Derive expectedIds from the region the user was scanning so OCR misreads
      // outside that region (e.g. IDs 910/914/920 during a Kanto scan) do not
      // inflate the missing count. Filter allSeen to in-region IDs as well.
      // Held-cells summary (2026-09-23): which caught-verdict cells never
      // committed and why, always in the exported log (see HeldCellSummary).
      if (session) {
        const held = session.heldCellsSummary();
        // eslint-disable-next-line no-console
        console.log("[dex-scan] held cells at scan end", { count: held.length });
        for (const h of held) {
          // eslint-disable-next-line no-console
          console.log("[dex-scan] held cell", h);
        }
      }
      const activeRegion = (totals.lastRegion ?? "unknown") as PokedexRegionLite;
      const expectedIds = expectedIdsForRegion(activeRegion);
      const expectedSet = new Set(expectedIds);
      const allSeen = Array.from(allSeenIdsRef.current).filter((id) => expectedSet.has(id));
      if (allSeen.length > 0) {
        const missingIds = computeMissingIds(allSeen, expectedIds);
        const missingRows = countMissingRows(missingIds);
        // eslint-disable-next-line no-console
        console.log("[dex-scan] missingRowsAtScanEnd", {
          missingRowsAtScanEnd: missingRows,
          missingIds,
          seenCount: allSeen.length,
          region: activeRegion,
        });
        // b19 (B19-4 telemetry-first): also emit recall-gap bands so we can
        // measure in the wild how often users miss whole rows. Pure derivation
        // from missingIds, no UI surfacing yet. See b19_backlog.md B19-4.
        const recallGapBands = computeRecallGapBands(missingIds);
        // eslint-disable-next-line no-console
        console.log("[dex-scan] recallGap", {
          recallGap: {
            skippedBands: recallGapBands,
            skippedCellCount: missingIds.length,
            finalState: classifyRecallGapFinalState(recallGapBands),
          },
          region: activeRegion,
        });
      }
      setState((prev) => ({
        ...prev,
        lastDiag: diag,
        totals,
        pendingFallback,
        reviewSummary,
        conflicts,
        showScrollNudge: false,
        scrollSteer: null,
      }));
    } else {
      // No totals snapshot (defensive — finish ran with no session). Still
      // publish any conflicts so gap surfacing isn't silently dropped.
      setState((prev) => ({ ...prev, conflicts, showScrollNudge: false, scrollSteer: null }));
    }
    steerTargetRowRef.current = null; // PR-E60 — clear any active steer on finish.
    // b18-pre-ocr-gate-pip — honest completion detail. A scan that recognised
    // ZERO dex content (no cells observed AND no numbers read) must NOT report a
    // cheerful "Live session complete": that masked the PiP-overlap gate bug,
    // where every frame was dropped before OCR yet the toast still said done.
    // Distinguish three outcomes:
    //   applied > 0                          -> "N dex entries updated"
    //   recognised nothing (no cells/numbers) -> "No dex frames recognised"
    //   recognised but nothing new applied    -> "No new entries"
    //
    // PiP-trust fix: the zero-commit branch previously read "Live session
    // complete", an upbeat message that masked the case where dex content WAS
    // recognised but nothing durably persisted (a blocked iOS WKWebView write
    // now returns applied 0 / no presentIds from applyScanObservations). Use a
    // clear no-new-entries message so the PiP never implies a successful save
    // when the dex is unchanged.
    const recognisedNothing =
      !!totals && totals.applied === 0 && totals.cellsObserved === 0 && totals.numbersRead === 0;
    const finalNewAddCount = newAddsRef.current.size;
    await setDexPipPhase({
      phase: "saved",
      detail: finalNewAddCount > 0
        ? `${finalNewAddCount} dex entr${finalNewAddCount === 1 ? "y" : "ies"} updated`
        : recognisedNothing
          ? "No dex frames recognised. Keep the Pokedex centred and rescan"
          : "No new entries added",
      // R14 FIX 2 — final DISTINCT applied row count (== the number rendered in
      // the detail line), carried as a structured field so the PiP status and
      // any diagnostics agree with what the user reads, never a cumulative tally.
      appliedCount: finalNewAddCount,
    });
    setPhase("saved", { active: false });
    // eslint-disable-next-line no-console
    console.log("[live-scan] finish() done, totals", {
      cellsObserved: totals?.cellsObserved ?? 0,
      numbersRead: totals?.numbersRead ?? 0,
      // `applied` is the store's per-frame applied tally and INCLUDES
      // re-confirmations, so it overcounts and is NOT what the user sees.
      // `distinctApplied` is the genuine distinct new-to-dex count — the same
      // number the "N dex entries updated" toast shows. Surfaced explicitly so a
      // device trace never again reads `applied` as "entries the user got"
      // (the 722-observed → 36-applied red herring, 2026-06-30).
      applied: totals?.applied ?? 0,
      distinctApplied: finalNewAddCount,
      skippedManual: totals?.skippedManual ?? 0,
      gated: totals?.gated ?? {},
      // b17u — frames skipped this session as mid-scroll transitional.
      scrollSkipped: scrollGateSkippedRef.current,
    });
    // b18-recall-investigation: per-session scroll-gate distribution summary.
    // Paired with the per-frame pass/skip logs, this gives us skip rate and
    // lets us compute avg diffs for passed vs skipped on the NEXT real scan.
    // eslint-disable-next-line no-console
    console.log("[live-scan] scroll-gate session summary", {
      framesPassed: scrollGatePassedRef.current,
      framesSkipped: scrollGateSkippedRef.current,
      skipRate:
        scrollGatePassedRef.current + scrollGateSkippedRef.current > 0
          ? (
              scrollGateSkippedRef.current /
              (scrollGatePassedRef.current + scrollGateSkippedRef.current)
            ).toFixed(2)
          : "n/a",
    });
    // Vision-only trial: the pre-OCR gate session summary was removed with the
    // gate. Vision OCRs every frame that clears the blur/scroll gates; the
    // POST-OCR frame gate + write chokepoint own dex-page filtering now.
    // scan-speed — duplicate-frame suppression summary. framesSkippedDuplicate
    // counts the redundant OCR passes the dedup avoided this session (held-
    // steady dwell frames the 1 Hz extension re-emitted). A high value next to
    // a healthy `applied` proves the speed win cost no reads.
    // eslint-disable-next-line no-console
    console.log("[live-scan] finish() done, frameDedup", {
      frameDedup: {
        framesSkippedDuplicate: readScanPipelineTelemetry().framesSkippedDuplicate,
      },
    });
    // scan-speed 2026-06-30 — wall-clock timing summary. ONE grep-friendly line
    // that answers "where did the seconds go?": sessionWallMs total, busy vs idle
    // split, per-frame frameMean/P95/Max ms, OCR-lane ocrMean/P95/Max ms, and the
    // headline commitGapMaxMs — the longest real-time stall between two committed
    // entries (the "holding on the screen" wait, in ms). With this, the next scan
    // tells us whether the wait is slow OCR (high ocrP95) or too-tight gating
    // (low frame ms but high commitGapMax while frames keep reading).
    // NOTE: the snapshot itself is emitted inside stop() (called above via
    // stop(true)), so it lands on direct stops too — not only here. Do not
    // re-emit it here or the line would double-print on the finish() path.
    return { totals, pendingFallback, reviewSummary, conflicts, myPokemon: myPokemonOutcome() };
    })();
    finishPromiseRef.current = promise;
    try {
      return await promise;
    } finally {
      finishPromiseRef.current = null;
    }
  }, [setPhase, stop]);

  /**
   * Build 3 / v1.4.6 b3 — flush the buffered, variant-unresolved dex numbers to
   * `fallbackVariant` (the Nexus-side selected filter). Called by the menu ONLY
   * after the user confirms the end-of-session prompt. Re-publishes the
   * diagnostic snapshot with the now-applied count and clears the pending
   * proposal so the prompt can't fire twice.
   */
  const applyFallback = useCallback(async (fallbackVariant: PokedexVariant) => {
    const session = sessionRef.current;
    if (!session) return { applied: 0, skippedManual: 0, ids: [] as number[] };
    const res = session.finalizeFallback(fallbackVariant);
    const totals = session.snapshot();
    const diag: DexScanDiagnostics = {
      ts: Date.now(),
      source: "live",
      variant: fallbackVariant,
      variantInferred: true,
      region: totals.lastRegion as DexScanDiagnostics["region"],
      mode: totals.lastMode,
      confidence: totals.lastConfidence,
      cellsObserved: totals.cellsObserved,
      numbersRead: totals.numbersRead,
      applied: totals.applied,
      skippedManual: totals.skippedManual,
      unknownCells: totals.unknownCells,
      gated: totals.gated,
      note:
        res.applied > 0
          ? `${res.applied} entr${res.applied === 1 ? "y" : "ies"} applied to ${fallbackVariant} (fallback)`
          : "Fallback applied no new entries",
    };
    publishDexScanDiagnostics(diag);
    setState((prev) => ({ ...prev, lastDiag: diag, totals, pendingFallback: null }));
    return res;
  }, []);

  /**
   * PR-E60 — user tapped "Skip ▶" on the live steer chip. PoGo forbids
   * autoscroll, so the user has chosen to leave this row uncovered and keep
   * scanning. We clear the active steer; the skipped row stays out of
   * `allSeenIdsRef`, so the post-scan coverage check surfaces it as a gap in
   * the post-scan telemetry. Resets the gap counter so the
   * next distinct jump can raise a fresh steer.
   */
  const dismissSteer = useCallback(() => {
    if (steerTargetRowRef.current === null) return;
    // eslint-disable-next-line no-console
    if (DEX_SCAN_VERBOSE) console.log("[dex-scan] steerSkipped", { targetRow: steerTargetRowRef.current });
    steerTargetRowRef.current = null;
    consecutiveGapCountRef.current = 0;
    setState((prev) => ({ ...prev, showScrollNudge: false, scrollSteer: null }));
  }, []);

  // ── Teardown ───────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      void stop(true).catch(() => null);
    };
  }, [stop]);

  // One object per state change, not per render (2026-09-22): a fresh
  // literal with a fresh `stop` closure every render changed the context
  // value's identity on every one of the several setStates a frame makes,
  // and every consumer (the Dex page among them) re-rendered per frame.
  const stopPublic = useCallback(
    (opts?: { keepBroadcast?: boolean }) => stop(false, opts),
    [stop],
  );
  return useMemo(
    () => ({ state, start, stop: stopPublic, finish, applyFallback, dismissSteer }),
    [state, start, stopPublic, finish, applyFallback, dismissSteer],
  );
}

export type UseDexLiveScanReturn = ReturnType<typeof useDexLiveScan>;
